import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { 
  User, Phone, Mail, MapPin, Calendar, FileText, Upload, 
  Trash2, Download, GraduationCap, ClipboardCheck, Award,
  Ticket, X, Plus, CheckCircle, XCircle, Save, Pencil,
  CreditCard, FileDown, Star, MessageSquare, Send, Copy, ExternalLink,
  Shield, AlertTriangle, CheckCircle2, Loader2, BookOpen, ChevronRight, Eye,
  Building2, Search, Pen, MailPlus, PackageCheck, MessageCircle,
  Sunrise, Moon, Laptop, Clock
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { EinwilligungEUnterschrift } from "@/components/EinwilligungEUnterschrift";
import { RechnungEinwilligungBox } from "@/components/ERechnungStammdaten";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

// NEU: Kurs-Verwaltung Komponente
import TeilnehmerKurse from "./TeilnehmerKurse";
import BgAusfertigungModal from "./BgAusfertigungModal";
import { TechnikCheck } from "./TechnikCheck";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { katLabel, dokumenteNachCode, pflichtCodes } from "@/lib/dokumentKategorien";
import { DokumentKacheln } from "@/components/teilnehmer/DokumentKacheln";
import { TeilnehmerStatusLeiste } from "@/components/teilnehmer/TeilnehmerStatusLeiste";
import { TeilnehmerVerlauf } from "@/components/teilnehmer/TeilnehmerVerlauf";
import { ZertifikatFreigabe } from "@/components/teilnehmer/ZertifikatFreigabe";
import { NichtantrittKarte } from "./NichtantrittKarte";
import { DokumentSpuren } from "@/components/teilnehmer/DokumentSpuren";
import { GeburtsdatumInput, istUngueltig } from "@/components/common/GeburtsdatumInput";
import { History } from "lucide-react";
import { NotionBestaetigungButton } from "@/components/teilnehmer/NotionBestaetigungButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// E-Mail Vorlagen
const EMAIL_VORLAGEN = {
  selbstzahler: {
    betreff: "Willkommen bei Dickies-Campus - Ihre Weiterbildung",
    text: (vorname, nachname, kurs, kursStart) => `Sehr geehrte/r Herr/Frau ${nachname},

vielen Dank für Ihre Anmeldung und Ihr Interesse an unserer Weiterbildung.

Unsere Dozentin Annika wird in Kürze mit Ihnen Kontakt aufnehmen, um den Ablauf Ihrer Schulung persönlich mit Ihnen zu besprechen.


Für die Teilnahme am Online-Meeting senden wir Ihnen nachfolgend die Zugangsdaten:

Zoom-Link:
https://us06web.zoom.us/j/6019169984?pwd=5KkOVwkldEXebgogCzMoSWZTcHx9bG.1

Meeting-ID: 601 916 9984
Kenncode: 699795


Zudem richten wir für Sie ein Benutzerkonto auf unserer Lernplattform ein. Darüber erhalten Sie Zugang zu Ihren Schulungsunterlagen sowie zu ergänzendem Arbeitsmaterial. Ihre persönlichen Zugangsdaten lassen wir Ihnen in einer separaten E-Mail zukommen.

Wir wünschen Ihnen einen erfolgreichen Start und viel Freude bei Ihrer Weiterbildung.

Bei Rückfragen stehen wir gerne zur Verfügung.

Mit freundlichen Grüßen

Angelina Tran


Dickies-Campus
Neuenhausplatz 73
D-40699 Erkrath

E-Mail: angelina@dickies-helden.de
Tel: 0211 2927 3332

Web: http://www.dickies-helden.de`
  },
  bildungsgutschein: {
    betreff: (kurs) => `Willkommen bei Dickies-Campus - ${kurs || 'Ihre Weiterbildung'}`,
    text: (vorname, nachname, kurs, kursStart) => `Sehr geehrte/r Herr/Frau ${nachname},

Annika lädt Dich zu einem geplanten Zoom-Meeting ein.


Kurs: ${kurs || '[Kurs wird noch zugewiesen]'}

Dein Kurs beginnt am Montag, ${kursStart || '[Datum wird noch festgelegt]'}
Kurszeiten: Montag - Freitag 9.00 - 15.30 Uhr, bitte pünktlich sein.


Zoom-Meeting beitreten

https://us06web.zoom.us/j/6019169984?pwd=5KkOVwkldEXebgogCzMoSWZTcHx9bG.1

Meeting-ID: 601 916 9984
Kenncode: 699795


Wir wünschen dir einen erfolgreichen Start und viel Freude bei deiner Weiterbildung.

Bei Rückfragen stehen wir gerne zur Verfügung.

Mit freundlichen Grüßen

Angelina Tran


Dickies-Campus
Neuenhausplatz 73
D-40699 Erkrath

E-Mail: angelina@dickies-helden.de
Tel: 0211 2927 3332

Web: http://www.dickies-helden.de`
  }
};

// Anhang Kategorien

// Prüft ob Praktikum für diesen Kurs erforderlich ist
// Praktikum NUR bei "Pflegehilfskraft/Betreuungsassistent" - NICHT bei LG1/LG2

// =====================================================================
// AustrittsmeldungCard — UI für die "Nichtantritts-/Austrittsmeldung"
// (BG-Maßnahmen, zur Vorlage bei der Agentur für Arbeit)
// =====================================================================
const AustrittsmeldungCard = ({ teilnehmerId, teilnehmer }) => {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [grund, setGrund] = useState("nicht_angetreten");
  const [abbruchTyp, setAbbruchTyp] = useState("arbeitsaufnahme");
  const [letzterTag, setLetzterTag] = useState("");
  const [arbDatum, setArbDatum] = useState("");
  const [sonstige, setSonstige] = useState("");
  const [unterzeichner, setUnterzeichner] = useState("Linda Lüppe, Geschäftsführerin");
  const [anrede, setAnrede] = useState("");
  const [austrittsdatum, setAustrittsdatum] = useState("");
  const [tatsaechlicherEintritt, setTatsaechlicherEintritt] = useState("");
  const [geplanterEintritt, setGeplanterEintritt] = useState("");
  const [mitSignatur, setMitSignatur] = useState(true);
  const [loading, setLoading] = useState(false);

  const oeffnen = async () => {
    setOpen(true);
    try {
      const r = await axios.get(`${API}/teilnehmer/${teilnehmerId}/austrittsmeldung-prefill`);
      setPrefill(r.data);
      if (r.data?.letzter_anwesenheitstag_vorschlag) setLetzterTag(r.data.letzter_anwesenheitstag_vorschlag);
      if (r.data?.anrede) setAnrede(r.data.anrede);
      if (r.data?.tatsaechlicher_eintritt) setTatsaechlicherEintritt(r.data.tatsaechlicher_eintritt);
      if (r.data?.geplanter_eintritt) setGeplanterEintritt(r.data.geplanter_eintritt);
    } catch (_) { /* prefill optional */ }
  };

  const downloadPdf = async () => {
    if (grund === "abgebrochen_grund" && !abbruchTyp) {
      toast.error("Bitte einen Abbruchgrund auswählen");
      return;
    }
    setLoading(true);
    try {
      const body = {
        austrittsgrund: grund,
        letzter_anwesenheitstag: letzterTag || null,
        abbruch_grund_typ: grund === "abgebrochen_grund" ? abbruchTyp : null,
        arbeitsaufnahme_datum: abbruchTyp === "arbeitsaufnahme" ? (arbDatum || null) : null,
        sonstige_gruende_text: abbruchTyp === "sonstige" ? (sonstige || null) : null,
        traeger_unterzeichner: unterzeichner,
        austrittsdatum: austrittsdatum || null,
        tatsaechlicher_eintritt: tatsaechlicherEintritt || null,
        geplanter_eintritt: geplanterEintritt || null,
        anrede: anrede || null,
        mit_signatur: mitSignatur,
      };
      const r = await axios.post(
        `${API}/teilnehmer/${teilnehmerId}/austrittsmeldung-pdf`,
        body,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Austrittsmeldung_${teilnehmer.nachname || "TN"}_${teilnehmer.vorname || ""}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Austrittsmeldung erstellt");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim PDF-Download");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card data-testid="austrittsmeldung-card" className="border-amber-200 bg-amber-50/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-amber-800">
            <AlertTriangle size={18} /> Austritts-/Nichtantrittsmeldung (BG)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-stone-600 mb-3">
            Erstellt das offizielle Formular &bdquo;Nichtantritts-/Austrittsmeldung Berufliche Weiterbildung&ldquo;
            zur Vorlage bei der Agentur f&uuml;r Arbeit &mdash; vorausgef&uuml;llt mit den TN-/Ma&szlig;nahmendaten.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="austrittsmeldung-open-btn"
              onClick={oeffnen}
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-100"
            >
              <FileDown size={16} className="mr-2" /> Austrittsmeldung erstellen
            </Button>
            {(teilnehmer?.status !== "abgebrochen") && (
              <Button
                data-testid="btn-kursabbruch-markieren"
                variant="outline"
                className="border-red-500 text-red-700 hover:bg-red-100"
                onClick={async () => {
                  if (!confirm(`Kursabbruch für ${teilnehmer?.vorname || ""} ${teilnehmer?.nachname || ""} markieren?\n\nDies setzt den Status auf "abgebrochen" (kein PDF, nur DB-Status) — für nachträgliche Erfassung historischer Abbrüche.`)) return;
                  try {
                    const r = await axios.post(`${API}/teilnehmer/${teilnehmerId}/kursabbruch-markieren`, {});
                    toast.success(`${r.data.name} als Abbruch markiert (${r.data.abbruch_datum})`);
                    // Kurzer Reload, damit UI den neuen Status zeigt
                    setTimeout(() => window.location.reload(), 800);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Fehler beim Markieren");
                  }
                }}
              >
                <AlertTriangle size={16} className="mr-2" /> Nur als Abbruch markieren
              </Button>
            )}
            {(teilnehmer?.status === "abgebrochen") && (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-red-300 bg-red-50 text-red-800 text-xs font-medium" data-testid="badge-abgebrochen">
                  <AlertTriangle size={13} /> Status: Kursabbruch{teilnehmer?.abbruch_datum ? ` (${teilnehmer.abbruch_datum})` : ""}
                </div>
                <Button
                  data-testid="btn-kursabbruch-zuruecknehmen"
                  variant="ghost"
                  size="sm"
                  className="text-stone-500 hover:text-stone-800"
                  onClick={async () => {
                    if (!confirm("Kursabbruch-Markierung zurücknehmen? Der TN wird wieder als 'aktiv' geführt.")) return;
                    try {
                      await axios.post(`${API}/teilnehmer/${teilnehmerId}/kursabbruch-zuruecknehmen`);
                      toast.success("Abbruch zurückgenommen — Status: aktiv");
                      setTimeout(() => window.location.reload(), 800);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Fehler");
                    }
                  }}
                >
                  Zurücknehmen
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="austrittsmeldung-dialog">
          <DialogHeader>
            <DialogTitle>Nichtantritts-/Austrittsmeldung erstellen</DialogTitle>
            <DialogDescription>
              {prefill ? (
                <>für <b>{prefill.name}</b> · {prefill.massnahmebezeichnung || "—"}</>
              ) : "Bitte Grund auswählen"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-medium">Grund <span className="text-red-500">*</span></Label>
              <select
                data-testid="austrittsmeldung-grund-select"
                value={grund}
                onChange={(e) => setGrund(e.target.value)}
                className="mt-1 w-full border rounded px-3 py-2 text-sm"
              >
                <option value="nicht_angetreten">Die Maßnahme wurde nicht angetreten</option>
                <option value="vorzeitige_pruefung">Vorzeitige Prüfung / erfolgreiche Teilnahme</option>
                <option value="pruefung_nicht_bestanden">Prüfung wurde nicht bestanden</option>
                <option value="abgebrochen">Teilnahme abgebrochen (allgemein)</option>
                <option value="abgebrochen_grund">Abgebrochen wegen konkretem Grund …</option>
              </select>
            </div>

            {(grund === "vorzeitige_pruefung" || grund === "abgebrochen") && (
              <div>
                <Label className="text-sm font-medium">Letzter persönlicher Anwesenheitstag</Label>
                <Input
                  data-testid="austrittsmeldung-letzter-tag"
                  type="date"
                  value={letzterTag}
                  onChange={(e) => setLetzterTag(e.target.value)}
                />
              </div>
            )}

            {grund === "abgebrochen_grund" && (
              <div className="space-y-2 border-l-2 border-amber-400 pl-3">
                <Label className="text-sm font-medium">Abbruchgrund <span className="text-red-500">*</span></Label>
                <select
                  data-testid="austrittsmeldung-abbruch-typ"
                  value={abbruchTyp}
                  onChange={(e) => setAbbruchTyp(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="arbeitsaufnahme">Arbeitsaufnahme</option>
                  <option value="mangelnde_leistung">Mangelnder Leistung</option>
                  <option value="fehlzeiten">Längerer Fehlzeiten</option>
                  <option value="sonstige">Sonstige Gründe</option>
                </select>

                {abbruchTyp === "arbeitsaufnahme" && (
                  <div>
                    <Label className="text-sm">Arbeitsaufnahme zum</Label>
                    <Input
                      data-testid="austrittsmeldung-arb-datum"
                      type="date"
                      value={arbDatum}
                      onChange={(e) => setArbDatum(e.target.value)}
                    />
                  </div>
                )}
                {abbruchTyp === "sonstige" && (
                  <div>
                    <Label className="text-sm">Sonstige Gründe (Freitext)</Label>
                    <textarea
                      data-testid="austrittsmeldung-sonstige"
                      value={sonstige}
                      onChange={(e) => setSonstige(e.target.value)}
                      rows={3}
                      className="w-full border rounded px-3 py-2 text-sm"
                    />
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 border-l-2 border-stone-300 pl-3">
              <div>
                <Label className="text-sm font-medium">Anrede</Label>
                <select
                  data-testid="austrittsmeldung-anrede"
                  value={anrede}
                  onChange={(e) => setAnrede(e.target.value)}
                  className="mt-1 w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">— bitte wählen —</option>
                  <option value="Herr">Herr</option>
                  <option value="Frau">Frau</option>
                </select>
              </div>
              <div>
                <Label className="text-sm font-medium">Geplanter Eintritt</Label>
                <Input
                  data-testid="austrittsmeldung-geplanter-eintritt"
                  type="date"
                  value={geplanterEintritt}
                  onChange={(e) => setGeplanterEintritt(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Tatsächlicher Eintritt</Label>
                <Input
                  data-testid="austrittsmeldung-tatsaechlicher-eintritt"
                  type="date"
                  value={tatsaechlicherEintritt}
                  onChange={(e) => setTatsaechlicherEintritt(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Austrittsdatum</Label>
                <Input
                  data-testid="austrittsmeldung-austrittsdatum"
                  type="date"
                  value={austrittsdatum}
                  onChange={(e) => setAustrittsdatum(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Unterzeichner Träger</Label>
              <Input
                data-testid="austrittsmeldung-unterzeichner"
                value={unterzeichner}
                onChange={(e) => setUnterzeichner(e.target.value)}
                placeholder="z.B. Linda Lüppe, Geschäftsführerin"
              />
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                data-testid="austrittsmeldung-mit-signatur"
                type="checkbox"
                checked={mitSignatur}
                onChange={(e) => setMitSignatur(e.target.checked)}
                className="rounded"
              />
              <span>Unterschrift &amp; Stempel von Linda Lüppe automatisch einfügen</span>
            </label>

            {prefill && (
              <div className="bg-stone-50 border rounded p-3 text-xs space-y-1">
                <div><b>TN:</b> {prefill.name} · Geb.: {prefill.geburtsdatum || "—"}</div>
                <div><b>Kundennummer:</b> {prefill.kundennummer || "— (in TN-Stammdaten ergänzen)"}</div>
                <div><b>Maßnahme:</b> {prefill.massnahmebezeichnung} · Nr.: {prefill.massnahmenummer || "—"}</div>
                <div><b>Geplanter Eintritt:</b> {prefill.geplanter_eintritt || "—"} · <b>Tatsächlich:</b> {prefill.tatsaechlicher_eintritt || "—"}</div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>Abbrechen</Button>
            <Button
              data-testid="austrittsmeldung-download-btn"
              onClick={downloadPdf}
              disabled={loading}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {loading ? "Erstelle PDF …" : "PDF erstellen & herunterladen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// =====================================================================
// AuditAmpel — kompakter Status-Indikator (Klick öffnet Details)
// =====================================================================
const AuditAmpel = ({ teilnehmerId }) => {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/teilnehmer/${teilnehmerId}/audit-status`)
      .then(r => { if (!cancelled) setStatus(r.data); })
      .catch(() => { if (!cancelled) setStatus(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [teilnehmerId]);

  if (loading) return <div className="ml-3 w-3 h-3 rounded-full bg-stone-300 animate-pulse" data-testid="audit-ampel-loading" />;
  if (status && status.azav_relevant === false) return null;
  if (!status) return null;

  const colors = {
    gruen: { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", label: "Audit-OK" },
    gelb:  { dot: "bg-amber-500",   text: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-200",   label: "Lücken (optional)" },
    rot:   { dot: "bg-red-500",     text: "text-red-700",     bg: "bg-red-50",     border: "border-red-200",     label: "kritische Lücken" },
  }[status.ampel] || { dot: "bg-stone-400", text: "text-stone-600", bg: "bg-stone-50", border: "border-stone-200", label: "Unbekannt" };

  return (
    <>
      <button
        data-testid="audit-ampel-btn"
        onClick={() => setOpen(true)}
        className={`ml-3 flex items-center gap-2 px-3 py-1.5 rounded-full border ${colors.bg} ${colors.border} hover:opacity-80 transition-opacity`}
        title={`${status.summary.kritisch_offen} kritische / ${status.summary.optional_offen} optionale Lücken`}
      >
        <span className={`w-2.5 h-2.5 rounded-full ${colors.dot} animate-pulse`} />
        <span className={`text-xs font-medium ${colors.text}`}>{colors.label}</span>
        {(status.summary.kritisch_offen > 0 || status.summary.optional_offen > 0) && (
          <span className={`text-xs ${colors.text}`}>
            ({status.summary.kritisch_offen + status.summary.optional_offen})
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="audit-ampel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${colors.dot}`} />
              Audit-Status — {status.name}
            </DialogTitle>
            <DialogDescription>
              {status.summary.ok} von {status.summary.total} Pflicht-Checks erfüllt
              {status.is_bg && " · BG-Maßnahme"}
              {status.kurs_beendet && " · Kurs beendet"}
              {status.abgebrochen && " · TN abgebrochen"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-[60vh] overflow-y-auto">
            {status.checks.map((c, i) => (
              <div
                key={i}
                className={`flex items-start gap-3 p-3 rounded border ${
                  c.ok ? "bg-emerald-50 border-emerald-100" :
                  c.severity === "kritisch" ? "bg-red-50 border-red-200" :
                                              "bg-amber-50 border-amber-200"
                }`}
                data-testid={`audit-check-${i}`}
              >
                <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${
                  c.ok ? "bg-emerald-500" :
                  c.severity === "kritisch" ? "bg-red-500" :
                                              "bg-amber-500"
                }`}>
                  {c.ok ? "✓" : c.severity === "kritisch" ? "!" : "?"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${c.ok ? "text-emerald-900" : "text-stone-800"}`}>
                    {c.label}
                  </div>
                  {!c.ok && c.hint && (
                    <div className="text-xs text-stone-600 mt-0.5">{c.hint}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};


const TeilnehmerDetail = ({ teilnehmerId, isOpen, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teilnehmer, setTeilnehmer] = useState(null);
  const [anhaenge, setAnhaenge] = useState([]);
  const [anhaengeFehler, setAnhaengeFehler] = useState(false);
  const aktuelleTnRef = useRef(teilnehmerId);
  aktuelleTnRef.current = teilnehmerId;
  const [massnahmen, setMassnahmen] = useState([]);
  const [pflegeeinrichtungen, setPflegeeinrichtungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCode, setUploadingCode] = useState(null);
  const [regel, setRegel] = useState({ azav: false, praktikum: false });
  useEffect(() => {
    if (!teilnehmerId) return;
    axios.get(`${API}/teilnehmer/${teilnehmerId}/azav-regel`).then((r) => setRegel(r.data)).catch(() => setRegel({ azav: false, praktikum: false }));
  }, [teilnehmerId, teilnehmer?.kurs, teilnehmer?.finanzierungsart, anhaenge.length]);
  const fileInputRef = useRef(null);
  
  // Interne Notizen lokal
  const [interneNotizenLocal, setInterneNotizenLocal] = useState("");
  
  // Anwesenheits-Bearbeitung State
  const [editingAnwesenheit, setEditingAnwesenheit] = useState(null);
  const [anwesenheitForm, setAnwesenheitForm] = useState({ status: '', grund: '' });

  // Kurs-Bearbeitung State
  const [kursForm, setKursForm] = useState({
    massnahme_id: "",
    kurs: "",
    kurs_start: "",
    kurs_ende: "",
    finanzierungsart: "selbstzahler"
  });
  
  // NEU: Zusätzliche Kurse State
  const [zusatzKurse, setZusatzKurse] = useState([]);
  const [showAddKurs, setShowAddKurs] = useState(false);
  const [neuerKurs, setNeuerKurs] = useState({ kurs_id: "", kurs_name: "", start: "", ende: "" });

  // Online-Signatur Dialog
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [signDokumentDatum, setSignDokumentDatum] = useState(""); // rückwirkendes Dokument-Datum (YYYY-MM-DD)
  const [signGfDatum, setSignGfDatum] = useState(() => new Date().toISOString().slice(0, 10)); // MV: „Erkrath, …“ links
  const [signPraktikumPaket, setSignPraktikumPaket] = useState(true);
  const [signAvailableDocs, setSignAvailableDocs] = useState({});
  const [selectedSignDocs, setSelectedSignDocs] = useState([]);
  const [signCustomMessage, setSignCustomMessage] = useState("");
  const [signSending, setSignSending] = useState(false);
  const [signRequests, setSignRequests] = useState([]);

  // Nachdruck-Modal (für alte Zertifikate mit Original-Nummer + Original-Datum)
  const [showNachdruckModal, setShowNachdruckModal] = useState(false);
  const [nachdruckKurs, setNachdruckKurs] = useState(null);
  const [nachdruckNummer, setNachdruckNummer] = useState("");
  const [nachdruckDatum, setNachdruckDatum] = useState("");
  const [nachdruckLoading, setNachdruckLoading] = useState(false);
  const [nachdruckRechtsgrundlage, setNachdruckRechtsgrundlage] = useState("nrw");

  // Rechtsgrundlage pro Kurs — bestimmt welcher Fußtext auf dem Zertifikat erscheint.
  // "nrw" = Standard (kein zusätzlicher Text), "hessen" = HLfGP-Leitlinie + § 5 HGBPAV Hessen.
  // Dict indiziert per kurs_id (Fallback: "__legacy__" für Legacy-TN ohne kurse[]).
  const [zertRechtsgrundlage, setZertRechtsgrundlage] = useState({});
  const getRechtsgrundlage = (k) => {
    const key = k?.kurs_id || "__legacy__";
    return zertRechtsgrundlage[key] || "nrw";
  };
  const setKursRechtsgrundlage = (k, val) => {
    const key = k?.kurs_id || "__legacy__";
    setZertRechtsgrundlage((prev) => ({ ...prev, [key]: val }));
  };
  const downloadDigitalZertifikat = async (k, zweit = false) => {
    const neu = !k.zertifikatsnummer;
    if (zweit && !window.confirm(`Digitale ZWEITAUSFERTIGUNG für „${k.kurs_name}" erstellen?\n\n• Gleiche Nummer ${k.zertifikatsnummer}\n• Heutiges Datum + kleiner Vermerk „Zweitausfertigung"\n• PDF mit Edelpapier-Design zum Mailen`)) return;
    if (neu && !window.confirm(`Digitales Zertifikat für „${k.kurs_name}" ausstellen?\n\n• Neue fortlaufende Nummer + heutiges Ausstellungsdatum\n• PDF mit Edelpapier-Design zum Mailen\n• Das Original wird später im Büro mit GLEICHER Nummer und GLEICHEM Datum auf Edelpapier gedruckt`)) return;
    try {
      const params = new URLSearchParams({ digital: "true", rechtsgrundlage: k.zertifikat_rechtsgrundlage || getRechtsgrundlage(k) });
      if (zweit) params.set("zweitausfertigung", "true");
      if (k.kurs_id) params.set("kurs_id", k.kurs_id);
      const res = await axios.get(`${API}/teilnehmer/${teilnehmerId}/zertifikat-pdf?${params.toString()}`, { responseType: "blob" });
      const zertNr = res.headers?.["x-zertifikats-nr"] || "";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `Zertifikat_${teilnehmer.nachname || "TN"}_${zertNr.replace("/", "-")}_Digital${zweit ? "_Zweitausfertigung" : ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(zweit ? `Digitale Zweitausfertigung ${zertNr} erstellt` : `Digitales Zertifikat ${zertNr} erstellt – Original-Druck ist vorgemerkt`);
      await fetchTeilnehmer();
      await fetchAnhaenge();
      await fetchKurseZertifikatStatus();
    } catch (err) {
      let msg = "Fehler beim digitalen Zertifikat";
      try { const txt = await err.response?.data?.text?.(); if (txt) { msg = JSON.parse(txt).detail || msg; } } catch (e) {}
      toast.error(msg);
    }
  };


  // Anwesenheitsliste-Sign-Modal
  const [showAnwesenheitSignModal, setShowAnwesenheitSignModal] = useState(false);
  const [alSignForm, setAlSignForm] = useState({
    dozent_kuerzel: "AL",
    sign_date: "",
    datum_von: "",
    datum_bis: "",
    only_anwesend: true,
    custom_message: "",
  });
  const [alPreviewUrl, setAlPreviewUrl] = useState(null);
  const [alSubmitting, setAlSubmitting] = useState(false);

  // Erstgespräch Formular State
  const [erstgespraechForm, setErstgespraechForm] = useState({
    erstgespraech_datum: "",
    erstgespraech_gefuehrt_von: "",
    deutschkenntnisse_erfuellt: false,
    deutschkenntnisse_nachweis_noetig: false,
    deutschkenntnisse_nachweis_gesehen: false,
    fuehrungszeugnis_erfuellt: false,
    fuehrungszeugnis_nachweis_noetig: false,
    fuehrungszeugnis_nachweis_gesehen: false,
    technik_laptop_vorhanden: false,
    technik_internet_vorhanden: false,
    technik_arbeitsplatz_vorhanden: false,
    fuer_massnahme_geeignet: false,
    vertrag: false
  });

  // ===== Schnell-Anwesenheit (Auto-Vorschau aus Kursstart → heute) =====
  const [showQuickAnwesenheit, setShowQuickAnwesenheit] = useState(false);
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickSubmitting, setQuickSubmitting] = useState(false);
  const [quickData, setQuickData] = useState(null);
  const [quickDozent, setQuickDozent] = useState("AL");
  const [quickExtraTage, setQuickExtraTage] = useState(0);

  const openQuickAnwesenheit = async () => {
    // Vorab-Check: hat der TN einen Kursstart?
    const hasKursStart = !!(
      teilnehmer?.kurs_start
      || (teilnehmer?.kurse && teilnehmer.kurse[0]?.start)
      || (teilnehmer?.kurse && teilnehmer.kurse[0]?.kurs_start)
    );
    if (!hasKursStart) {
      toast.error("Kein Kurs-Startdatum hinterlegt", {
        description: "Bitte erst im Tab \u201EKurse\u201C ein Kurs-Startdatum setzen — sonst kann die Anwesenheitsliste nicht berechnet werden.",
        duration: 8000,
      });
      return;
    }
    setShowQuickAnwesenheit(true);
    setQuickLoading(true);
    setQuickData(null);
    setQuickExtraTage(0);
    try {
      const res = await axios.post(`${API}/teilnehmer/${teilnehmerId}/anwesenheit/auto-vorschau`, {});
      setQuickData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vorschau konnte nicht geladen werden");
      setShowQuickAnwesenheit(false);
    } finally {
      setQuickLoading(false);
    }
  };

  const addExtraTag = async () => {
    const next = quickExtraTage + 1;
    setQuickExtraTage(next);
    setQuickLoading(true);
    try {
      const res = await axios.post(`${API}/teilnehmer/${teilnehmerId}/anwesenheit/auto-vorschau`, { extra_tage: next });
      setQuickData(res.data);
      toast.success(`Tag ${res.data?.tage?.length || ""} hinzugefügt — als Prüfungstag vormarkiert`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Zusatztag konnte nicht angehängt werden");
      setQuickExtraTage(next - 1);
    } finally {
      setQuickLoading(false);
    }
  };

  const removeExtraTag = async () => {
    if (quickExtraTage <= 0) return;
    const next = quickExtraTage - 1;
    setQuickExtraTage(next);
    setQuickLoading(true);
    try {
      const res = await axios.post(`${API}/teilnehmer/${teilnehmerId}/anwesenheit/auto-vorschau`, { extra_tage: next });
      setQuickData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Zusatztag konnte nicht entfernt werden");
      setQuickExtraTage(next + 1);
    } finally {
      setQuickLoading(false);
    }
  };

  const quickToggleStatus = (rowKey) => {
    if (!quickData) return;
    const cycle = { anwesend: "entschuldigt", entschuldigt: "fehlend", fehlend: "anwesend" };
    setQuickData({
      ...quickData,
      tage: quickData.tage.map(t => {
        const k = t._orig_datum || t.datum;
        return k === rowKey ? { ...t, status: cycle[t.status] || "anwesend" } : t;
      })
    });
  };

  // Inline-Edit: Datum oder Thema pro Tag überschreiben
  const quickEditTag = (rowKey, field, value) => {
    if (!quickData) return;
    setQuickData({
      ...quickData,
      tage: quickData.tage.map(t => {
        const k = t._orig_datum || t.datum;
        if (k !== rowKey) return t;
        const next = { ...t, [field]: value, _orig_datum: k, _edited: true };
        // Wenn das Datum geändert wurde, aktualisiere auch Wochentag und Anzeige
        if (field === "datum" && value) {
          try {
            const d = new Date(value + "T00:00:00");
            const woTage = ["So","Mo","Di","Mi","Do","Fr","Sa"];
            next.wochentag = woTage[d.getDay()];
            const dd = String(d.getDate()).padStart(2,"0");
            const mm = String(d.getMonth()+1).padStart(2,"0");
            next.datum_display = `${dd}.${mm}.${d.getFullYear()}`;
          } catch (_) { /* ignore date parse */ }
        }
        return next;
      })
    });
  };

  const submitQuickAnwesenheit = async (versand_methode) => {
    if (!quickData) return;
    if (quickData.has_existing_entries) {
      if (!window.confirm(`Es existieren bereits Klassenbuch-Einträge für diesen TN.\n\nMit OK werden sie überschrieben.`)) return;
    }
    setQuickSubmitting(true);
    try {
      const res = await axios.post(`${API}/teilnehmer/${teilnehmerId}/anwesenheit/auto-prepare-and-sign`, {
        kurs_id: quickData.kurs_id,
        tage: quickData.tage.map(t => ({
          datum: t.datum,
          status: t.status,
          thema: t.thema || "",
          lg: t.lg || "",
          modul_nr: t.modul_nr != null ? String(t.modul_nr) : "",
          orig_datum: t._orig_datum || t.datum,
        })),
        dozent_kuerzel: quickDozent,
        versand_methode,
        ueberschreiben: !!quickData.has_existing_entries,
      });
      if (res.data?.sign_request?.whatsapp_url) {
        window.open(res.data.sign_request.whatsapp_url, "_blank", "noopener,noreferrer");
        toast.success(`WhatsApp geöffnet — ${res.data.tage_gespeichert} Tage gespeichert`);
      } else if (res.data?.sign_request?.status === "sent") {
        toast.success(`Sign-Link per E-Mail gesendet · ${res.data.tage_gespeichert} Tage gespeichert`);
      } else {
        toast.success(`${res.data.tage_gespeichert || 0} Tage gespeichert + Sign-Request erstellt`);
      }
      setShowQuickAnwesenheit(false);
      try { fetchTeilnehmer(); } catch (_) {}
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern + Senden");
    } finally {
      setQuickSubmitting(false);
    }
  };


  // Befragung Formular State
  const [befragungForm, setBefragungForm] = useState({
    befragung_datum: "",
    befragung_wie_erfahren: "",
    befragung_dozenten: null,
    befragung_unterrichtsmaterial: null,
    befragung_organisation: null,
    befragung_weiterempfehlung: false,
    befragung_anmerkungen: ""
  });

  // AZAV-Prüfung State
  const [azavPruefung, setAzavPruefung] = useState(null);
  const [azavLoading, setAzavLoading] = useState(false);
  const [azavDialogOpen, setAzavDialogOpen] = useState(false);
  
  // AZAV-Klassenbuch State (für BG-Teilnehmer)
  const [klassenbuchData, setKlassenbuchData] = useState(null);
  const [klassenbuchLoading, setKlassenbuchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("stammdaten");
  const [showKlassenbuchDetail, setShowKlassenbuchDetail] = useState(false);
  const [klassenbuchEintraege, setKlassenbuchEintraege] = useState([]);

  // Historie State
  const [historie, setHistorie] = useState([]);
  const [historieLoading, setHistorieLoading] = useState(false);

  // Kurs-Bearbeitung Edit Mode
  const [editKurs, setEditKurs] = useState(false);

  // Prüfungs-Bearbeitung Edit Mode
  const [editPruefung, setEditPruefung] = useState(false);
  const [pruefungForm, setPruefungForm] = useState({
    pruefung_am: "",
    pruefung_2_am: "",
    punkte_schriftlich: "",
    punkte_muendlich: "",
    punkte_facharbeit: "",
    gesamtpunkte: "",
    zertifikatsnummer: "",
    bestanden: false,
    // Zweitprüfung
    punkte_2_schriftlich: "",
    punkte_2_muendlich: "",
    punkte_2_facharbeit: "",
    gesamtpunkte_2: "",
    bestanden_2: false
  });

  // Kontaktdaten-Bearbeitung Edit Mode
  const [editKontakt, setEditKontakt] = useState(false);
  const [kontaktForm, setKontaktForm] = useState({
    vorname: "",
    nachname: "",
    finanzierungsart: "selbstzahler",
    geburtsdatum: "",
    telefon: "",
    whatsapp_nummer: "",
    email: "",
    strasse: "",
    plz: "",
    ort: ""
  });

  // Pflegeeinrichtung-Bearbeitung
  const [editPflegeeinrichtung, setEditPflegeeinrichtung] = useState(false);
  const [pflegeeinrichtungSearch, setPflegeeinrichtungSearch] = useState("");

  // Zertifikat-Status pro Kurs (für Multi-Kurs-Teilnehmer)
  const [kurseZertifikatStatus, setKurseZertifikatStatus] = useState(null);

  // BG-Ausfertigung Modal
  const [bgModalOpen, setBgModalOpen] = useState(false);

  const fetchKurseZertifikatStatus = async () => {
    try {
      const id = teilnehmerId;
      const r = await axios.get(`${API}/teilnehmer/${id}/kurse-zertifikat-status`);
      if (aktuelleTnRef.current === id) setKurseZertifikatStatus(r.data);
    } catch (e) {
      setKurseZertifikatStatus({ kurse: [], main_kurs_name: "" });
    }
  };

  useEffect(() => {
    if (teilnehmerId && isOpen) {
      setEditKurs(false); // Reset edit mode when dialog opens
      setEditPflegeeinrichtung(false);
      setEditPruefung(false);
      setAnhaenge([]);
      setHistorie([]);
      setKurseZertifikatStatus(null);
      fetchTeilnehmer();
      fetchAnhaenge();
      fetchMassnahmen();
      fetchPflegeeinrichtungen();
      fetchHistorie();
      fetchKurseZertifikatStatus();
    }
  }, [teilnehmerId, isOpen]);

  // Initialize pruefungForm when teilnehmer loads
  useEffect(() => {
    if (teilnehmer) {
      setPruefungForm({
        pruefung_am: teilnehmer.pruefung_am || "",
        pruefung_2_am: teilnehmer.pruefung_2_am || "",
        punkte_schriftlich: teilnehmer.punkte_schriftlich || "",
        punkte_muendlich: teilnehmer.punkte_muendlich || "",
        punkte_facharbeit: teilnehmer.punkte_facharbeit || "",
        gesamtpunkte: teilnehmer.gesamtpunkte || "",
        zertifikatsnummer: teilnehmer.zertifikatsnummer || "",
        bestanden: teilnehmer.bestanden || false,
        punkte_2_schriftlich: teilnehmer.punkte_2_schriftlich || "",
        punkte_2_muendlich: teilnehmer.punkte_2_muendlich || "",
        punkte_2_facharbeit: teilnehmer.punkte_2_facharbeit || "",
        gesamtpunkte_2: teilnehmer.gesamtpunkte_2 || "",
        bestanden_2: teilnehmer.bestanden_2 || false
      });
      setKontaktForm({
        vorname: teilnehmer.vorname || "",
        nachname: teilnehmer.nachname || "",
        finanzierungsart: teilnehmer.finanzierungsart || "selbstzahler",
        geburtsdatum: teilnehmer.geburtsdatum || "",
        telefon: teilnehmer.telefon || "",
        whatsapp_nummer: teilnehmer.whatsapp_nummer || "",
        email: teilnehmer.email || "",
        strasse: teilnehmer.strasse || "",
        plz: teilnehmer.plz || "",
        ort: teilnehmer.ort || ""
      });
    }
  }, [teilnehmer]);

  const fetchMassnahmen = async () => {
    try {
      const response = await axios.get(`${API}/massnahmen`);
      setMassnahmen(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Maßnahmen");
    }
  };

  const fetchPflegeeinrichtungen = async () => {
    try {
      const response = await axios.get(`${API}/pflegeeinrichtungen`);
      setPflegeeinrichtungen(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Pflegeeinrichtungen");
    }
  };

  const fetchHistorie = async () => {
    try {
      setHistorieLoading(true);
      const id = teilnehmerId;
      const response = await axios.get(`${API}/aktivitaeten/teilnehmer/${id}?limit=10`);
      if (aktuelleTnRef.current === id) setHistorie(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Historie");
    } finally {
      setHistorieLoading(false);
    }
  };

  const getPflegeeinrichtungName = (id) => {
    if (!id) return null;
    const pe = pflegeeinrichtungen.find(p => p.id === id);
    return pe ? pe.einrichtungsname : null;
  };

  const fetchTeilnehmer = async (still = false) => {
    try {
      if (!still) setLoading(true);
      const id = teilnehmerId;
      const response = await axios.get(`${API}/teilnehmer/${id}`);
      if (aktuelleTnRef.current !== id) return;
      const data = response.data;
      setTeilnehmer(data);
      
      // Interne Notizen lokal setzen (leer für neue Eingabe)
      setInterneNotizenLocal("");
      
      // Kurs Form füllen
      setKursForm({
        massnahme_id: data.massnahme_id || "",
        kurs: data.kurs || "",
        kurs_start: data.kurs_start || "",
        kurs_ende: data.kurs_ende || "",
        finanzierungsart: data.finanzierungsart || "selbstzahler"
      });
      
      // Zusätzliche Kurse laden
      setZusatzKurse(data.kurse || []);
      
      // Erstgespräch Form füllen
      setErstgespraechForm({
        erstgespraech_datum: data.erstgespraech_datum || "",
        erstgespraech_gefuehrt_von: data.erstgespraech_gefuehrt_von || "",
        deutschkenntnisse_erfuellt: data.deutschkenntnisse_erfuellt || false,
        deutschkenntnisse_nachweis_noetig: data.deutschkenntnisse_nachweis_noetig || false,
        deutschkenntnisse_nachweis_gesehen: data.deutschkenntnisse_nachweis_gesehen || false,
        fuehrungszeugnis_erfuellt: data.fuehrungszeugnis_erfuellt || false,
        fuehrungszeugnis_nachweis_noetig: data.fuehrungszeugnis_nachweis_noetig || false,
        fuehrungszeugnis_nachweis_gesehen: data.fuehrungszeugnis_nachweis_gesehen || false,
        technik_laptop_vorhanden: data.technik_laptop_vorhanden || false,
        technik_internet_vorhanden: data.technik_internet_vorhanden || false,
        technik_arbeitsplatz_vorhanden: data.technik_arbeitsplatz_vorhanden || false,
        fuer_massnahme_geeignet: data.fuer_massnahme_geeignet || false,
        vertrag: data.vertrag || false
      });

      // Befragung Form füllen
      setBefragungForm({
        befragung_datum: data.befragung_datum || "",
        befragung_wie_erfahren: data.befragung_wie_erfahren || "",
        befragung_dozenten: data.befragung_dozenten || null,
        befragung_unterrichtsmaterial: data.befragung_unterrichtsmaterial || null,
        befragung_organisation: data.befragung_organisation || null,
        befragung_weiterempfehlung: data.befragung_weiterempfehlung || false,
        befragung_anmerkungen: data.befragung_anmerkungen || ""
      });
    } catch (error) {
      toast.error("Fehler beim Laden des Teilnehmers");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnhaenge = async () => {
    const id = teilnehmerId;
    try {
      const response = await axios.get(`${API}/teilnehmer/${id}/anhaenge`);
      if (aktuelleTnRef.current !== id) return;
      setAnhaenge((response.data || []).filter((a) => !a.teilnehmer_id || a.teilnehmer_id === id));
      setAnhaengeFehler(false);
    } catch (error) {
      if (aktuelleTnRef.current !== id) return;
      setAnhaenge([]);
      setAnhaengeFehler(true);
      toast.error("Dokumente konnten nicht geladen werden – bitte erneut öffnen");
    }
  };

  // Einzelnes Feld direkt speichern (für Praktikums-Dokumentation)
  const updateTeilnehmerField = async (field, value) => {
    try {
      // Lokales State sofort aktualisieren für schnelle UI-Reaktion
      setTeilnehmer(prev => ({ ...prev, [field]: value }));
      
      // An Backend senden - NUR das geänderte Feld senden!
      // Das verhindert versehentliches Überschreiben anderer Felder
      const updateData = { [field]: value };
      
      // Wichtig: Auch vorname/nachname mitsenden wenn Name-bezogen
      if (field === 'vorname' || field === 'nachname') {
        updateData.vorname = field === 'vorname' ? value : teilnehmer.vorname;
        updateData.nachname = field === 'nachname' ? value : teilnehmer.nachname;
      }
      
      await axios.put(`${API}/teilnehmer/${teilnehmerId}`, updateData);
      
      // Kein Toast bei jedem Feld-Update, nur bei Fehlern
    } catch (error) {
      toast.error("Fehler beim Speichern");
      // Bei Fehler: Daten neu laden
      fetchTeilnehmer();
    }
  };

  // Anwesenheit bearbeiten (für AU-Tage etc.)
  const saveAnwesenheit = async () => {
    if (!editingAnwesenheit) return;
    try {
      await axios.put(`${API}/bg-anwesenheit/${editingAnwesenheit.id}`, {
        status: anwesenheitForm.status,
        grund: anwesenheitForm.grund
      });
      toast.success("Anwesenheit aktualisiert");
      setEditingAnwesenheit(null);
      // Klassenbuch-Daten neu laden
      if (teilnehmer?.finanzierungsart === 'bildungsgutschein') {
        try {
          const res = await axios.get(`${API}/bg-klassenbuch/statistik/${teilnehmerId}`);
          setKlassenbuchData(res.data);
        } catch (e) {
          console.error("Fehler beim Laden der Klassenbuch-Daten:", e);
        }
      }
    } catch (error) {
      toast.error("Fehler beim Speichern der Anwesenheit");
    }
  };

  const openAnwesenheitEdit = (anwesenheit) => {
    setEditingAnwesenheit(anwesenheit);
    setAnwesenheitForm({
      status: anwesenheit.status,
      grund: anwesenheit.grund || ''
    });
  };

  const saveErstgespraech = async () => {
    setSaving(true);
    try {
      // 1. Erstgespräch-Daten speichern
      await axios.put(`${API}/teilnehmer/${teilnehmerId}/erstgespraech`, erstgespraechForm);
      
      // 2. PDF automatisch generieren und als Anhang speichern (AZAV-konforme Checkliste mit 6 Sektionen)
      try {
        await axios.post(`${API}/teilnehmer/${teilnehmerId}/erstgespraech-checkliste`, {});
        toast.success("Erstgespräch gespeichert und PDF erstellt");
      } catch (pdfError) {
        console.error("PDF-Generierung fehlgeschlagen:", pdfError);
        toast.success("Erstgespräch-Daten gespeichert (PDF-Generierung fehlgeschlagen)");
      }
      
      fetchTeilnehmer();
      fetchAnhaenge(); // Anhänge neu laden um das neue PDF zu sehen
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const saveBefragung = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/teilnehmer/${teilnehmerId}/befragung`, {
        ...befragungForm,
        befragung_ausgefuellt: true
      });
      toast.success("Befragung gespeichert");
      fetchTeilnehmer();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  // Kurs-Zuweisung speichern
  const saveKursZuweisung = async () => {
    setSaving(true);
    try {
      // Kursname aus der ausgewählten Maßnahme ermitteln
      let kursName = kursForm.kurs;
      if (kursForm.massnahme_id) {
        const selectedMassnahme = massnahmen.find(m => m.id === kursForm.massnahme_id);
        if (selectedMassnahme) {
          kursName = selectedMassnahme.name;
        }
      }

      // Nur relevante Felder senden - nicht alle Teilnehmerdaten
      await axios.put(`${API}/teilnehmer/${teilnehmerId}`, {
        finanzierungsart: kursForm.finanzierungsart || teilnehmer.finanzierungsart || "selbstzahler",
        massnahme_id: kursForm.massnahme_id || null,
        kurs: kursName,
        kurs_start: kursForm.kurs_start || null,
        kurs_ende: kursForm.kurs_ende || null
      });
      toast.success("Kurs & Finanzierung gespeichert");
      fetchTeilnehmer();
      if (onUpdate) onUpdate();
      setEditKurs(false);
    } catch (error) {
      console.error("Fehler:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  // Prüfungsdaten speichern
  const savePruefung = async () => {
    setSaving(true);
    try {
      // Nur Prüfungsfelder senden
      await axios.put(`${API}/teilnehmer/${teilnehmerId}`, {
        pruefung_am: pruefungForm.pruefung_am || null,
        pruefung_2_am: pruefungForm.pruefung_2_am || null,
        punkte_schriftlich: pruefungForm.punkte_schriftlich || null,
        punkte_muendlich: pruefungForm.punkte_muendlich || null,
        punkte_facharbeit: pruefungForm.punkte_facharbeit || null,
        gesamtpunkte: pruefungForm.gesamtpunkte || null,
        zertifikatsnummer: pruefungForm.zertifikatsnummer || null,
        bestanden: pruefungForm.bestanden,
        punkte_2_schriftlich: pruefungForm.punkte_2_schriftlich || null,
        punkte_2_muendlich: pruefungForm.punkte_2_muendlich || null,
        punkte_2_facharbeit: pruefungForm.punkte_2_facharbeit || null,
        gesamtpunkte_2: pruefungForm.gesamtpunkte_2 || null,
        bestanden_2: pruefungForm.bestanden_2 || false
      });
      toast.success("Prüfungsdaten gespeichert");
      fetchTeilnehmer();
      if (onUpdate) onUpdate();
      setEditPruefung(false);
    } catch (error) {
      console.error("Fehler:", error);
      toast.error("Fehler beim Speichern der Prüfungsdaten");
    } finally {
      setSaving(false);
    }
  };

  // Kontaktdaten speichern
  const saveKontakt = async () => {
    if (istUngueltig(kontaktForm.geburtsdatum)) {
      toast.error("Geburtsdatum ist unvollständig – bitte als TT.MM.JJJJ eingeben. Nichts gespeichert.");
      return;
    }
    setSaving(true);
    try {
      // Nur Kontaktfelder senden
      await axios.put(`${API}/teilnehmer/${teilnehmerId}`, {
        vorname: kontaktForm.vorname || null,
        nachname: kontaktForm.nachname || null,
        finanzierungsart: kontaktForm.finanzierungsart || teilnehmer.finanzierungsart || "selbstzahler",
        geburtsdatum: kontaktForm.geburtsdatum || null,
        telefon: kontaktForm.telefon || null,
        whatsapp_nummer: kontaktForm.whatsapp_nummer || null,
        email: kontaktForm.email || null,
        strasse: kontaktForm.strasse || null,
        plz: kontaktForm.plz || null,
        ort: kontaktForm.ort || null
      });
      toast.success("Stammdaten gespeichert");
      fetchTeilnehmer();
      if (onUpdate) onUpdate();
      setEditKontakt(false);
    } catch (error) {
      console.error("Fehler:", error);
      toast.error("Fehler beim Speichern der Stammdaten");
    } finally {
      setSaving(false);
    }
  };

  const savePflegeeinrichtung = async (peId) => {
    setSaving(true);
    try {
      // Nur Pflegeeinrichtung-ID senden
      await axios.put(`${API}/teilnehmer/${teilnehmerId}`, {
        pflegeeinrichtung_id: peId || null
      });
      toast.success("Pflegeeinrichtung zugeordnet");
      fetchTeilnehmer();
      if (onUpdate) onUpdate();
      setEditPflegeeinrichtung(false);
      setPflegeeinrichtungSearch("");
    } catch (error) {
      console.error("Fehler:", error);
      toast.error("Fehler beim Zuordnen der Pflegeeinrichtung");
    } finally {
      setSaving(false);
    }
  };

  const getFilteredPflegeeinrichtungen = () => {
    if (!pflegeeinrichtungSearch) return pflegeeinrichtungen.slice(0, 10);
    const search = pflegeeinrichtungSearch.toLowerCase();
    return pflegeeinrichtungen
      .filter(pe => pe.einrichtungsname?.toLowerCase().includes(search) || pe.ort?.toLowerCase().includes(search))
      .slice(0, 15);
  };

  // AZAV-Prüfung durchführen
  const runAzavPruefung = async () => {
    setAzavLoading(true);
    try {
      const response = await axios.get(`${API}/azav/pruefung/${teilnehmerId}`);
      setAzavPruefung(response.data);
      setAzavDialogOpen(true);
    } catch (error) {
      const detail = error.response?.data?.detail || "Fehler bei der AZAV-Prüfung";
      toast.error(detail);
    } finally {
      setAzavLoading(false);
    }
  };

  // AZAV-Klassenbuch Daten laden (für BG-Teilnehmer)
  const loadKlassenbuchData = async () => {
    if (!teilnehmerId || teilnehmer?.finanzierungsart !== 'bildungsgutschein') return;
    
    setKlassenbuchLoading(true);
    try {
      const response = await axios.get(`${API}/bg-klassenbuch/teilnehmer/${teilnehmerId}`);
      setKlassenbuchData(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Klassenbuch-Daten:", error);
      setKlassenbuchData(null);
    } finally {
      setKlassenbuchLoading(false);
    }
  };
  
  // Klassenbuch-Einträge für diesen Teilnehmer laden
  const loadKlassenbuchEintraege = async () => {
    if (!klassenbuchData?.anwesenheit?.length) return;
    
    // kurs_id aus den Anwesenheits-Daten extrahieren
    const kursId = klassenbuchData.anwesenheit[0]?.kurs_id;
    if (!kursId) return;
    
    try {
      const response = await axios.get(`${API}/bg-klassenbuch/kurs/${kursId}`);
      setKlassenbuchEintraege(response.data);
      setShowKlassenbuchDetail(true);
    } catch (error) {
      console.error("Fehler beim Laden der Klassenbuch-Einträge:", error);
    }
  };
  
  // Lade Klassenbuch-Daten wenn AZAV-Tab aktiviert wird
  useEffect(() => {
    if (activeTab === 'azav' && regel.azav && !klassenbuchData) {
      loadKlassenbuchData();
    }
  }, [activeTab, teilnehmer?.finanzierungsart, teilnehmerId]);

  // AZAV-Prüfmappe herunterladen
  const downloadAzavPruefmappe = async () => {
    try {
      const response = await axios.get(`${API}/azav/pruefmappe/${teilnehmerId}`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `AZAV_Pruefmappe_${teilnehmer?.nachname}_${teilnehmer?.vorname}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("AZAV-Prüfmappe heruntergeladen");
    } catch (error) {
      const detail = error.response?.data?.detail || "Fehler beim Erstellen der Prüfmappe";
      toast.error(detail);
    }
  };

  const downloadErstgespraechPDF = async () => {
    try {
      const response = await axios.post(`${API}/teilnehmer/${teilnehmerId}/erstgespraech-checkliste`, {}, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Checkliste_Erstgespraech_${teilnehmer?.nachname}_${teilnehmer?.vorname}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Erstgespräch-Checkliste heruntergeladen");
      // Anhänge-Liste aktualisieren
      fetchAnhaenge();
    } catch (error) {
      toast.error("Fehler beim PDF-Download");
    }
  };

  const downloadBefragungPDF = async () => {
    try {
      const response = await axios.get(`${API}/teilnehmer/${teilnehmerId}/teilnehmerbefragung-pdf`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Teilnehmerbefragung_${teilnehmer?.nachname}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF generiert und als Anhang gespeichert");
      // Anhänge-Liste aktualisieren
      fetchAnhaenge();
    } catch (error) {
      toast.error("Fehler beim PDF-Download");
    }
  };

  const uploadDatei = async (file, code) => {
    setUploadingCode(code);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("kategorie", code);
    try {
      await axios.post(`${API}/teilnehmer/${teilnehmerId}/anhaenge`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`„${file.name}“ als ${katLabel(code)} abgelegt`);
      fetchAnhaenge();
    } catch (error) {
      toast.error("Fehler beim Hochladen");
    } finally {
      setUploadingCode(null);
    }
  };

  const openSignDialog = async () => {
    try {
      const [docsRes, reqsRes] = await Promise.all([
        axios.get(`${API}/sign-dokumente/available`),
        axios.get(`${API}/sign-requests/teilnehmer/${teilnehmerId}`),
      ]);
      setSignAvailableDocs(docsRes.data);
      setSignRequests(reqsRes.data || []);
      setSelectedSignDocs([]);
      setSignCustomMessage("");
      // Beginn schon erreicht → rückwirkend auf den Tag vor Maßnahmebeginn datieren (wie bisher)
      const start = String(teilnehmer?.kurs_start || (teilnehmer?.kurse || []).find((k) => k?.start)?.start || "").slice(0, 10);
      const vorStart = start && new Date(`${start}T00:00:00`) <= new Date()
        ? new Date(new Date(`${start}T12:00:00`).getTime() - 86400000).toISOString().slice(0, 10) : "";
      setSignDokumentDatum(vorStart);
      setSignGfDatum(new Date().toISOString().slice(0, 10));
      setShowSignDialog(true);
    } catch (e) {
      toast.error("Fehler beim Laden der Dokumente");
    }
  };

  const klassenbuchPdfSpeichern = async () => {
    try {
      const kursStart = teilnehmer.kurs_start?.replace(/-/g, '');
      const isLG = teilnehmer.kurs?.toLowerCase().includes('lg');
      const kursId = isLG ? `lg1lg2_${kursStart}` : `pfh_${kursStart}`;
      const res = await axios.post(`${API}/klassenbuch-neu/kurs/${kursId}/pdf-save/${teilnehmerId}`);
      toast.success(`Klassenbuch abgelegt: ${res.data.filename}`);
      fetchAnhaenge();
    } catch (err) {
      toast.error('Fehler beim Speichern des Klassenbuch-PDFs');
    }
  };

  const anwesenheitPdfSpeichern = async () => {
    try {
      const res = await axios.post(`${API}/anwesenheit-pdf/${teilnehmerId}`);
      toast.success(`Anwesenheitsliste abgelegt: ${res.data.filename}`);
      fetchAnhaenge();
    } catch (err) {
      toast.error('Fehler beim Speichern des Anwesenheits-PDFs');
    }
  };

  const handleDownload = async (anhang) => {
    try {
      const response = await axios.get(
        `${API}/teilnehmer/${teilnehmerId}/anhaenge/${anhang.id}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", anhang.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Fehler beim Download");
    }
  };

  const handleOpenDocument = async (anhang) => {
    try {
      const response = await axios.get(
        `${API}/teilnehmer/${teilnehmerId}/anhaenge/${anhang.id}`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error("Fehler beim Öffnen");
    }
  };

  const handleDeleteAnhang = async (anhangId) => {
    if (!window.confirm("Anhang wirklich löschen?\n\nDu kannst die Löschung 10 Sekunden lang rückgängig machen.")) return;
    try {
      await axios.delete(`${API}/teilnehmer/${teilnehmerId}/anhaenge/${anhangId}`);
      fetchAnhaenge();
      toast.success("Anhang gelöscht", {
        duration: 10000,
        action: {
          label: "Rückgängig",
          onClick: async () => {
            try {
              await axios.post(`${API}/teilnehmer/${teilnehmerId}/anhaenge/${anhangId}/restore`);
              toast.success("Wiederhergestellt");
              fetchAnhaenge();
            } catch (e) {
              toast.error("Wiederherstellung fehlgeschlagen: " + (e.response?.data?.detail || e.message));
            }
          },
        },
      });
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("de-DE");
    } catch {
      return dateStr;
    }
  };

  // Checkbox Komponente
  const CheckboxField = ({ label, checked, onChange }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <span className="text-sm">{label}</span>
      <Checkbox checked={checked} onCheckedChange={onChange} />
    </div>
  );

  // Bewertungs-Sterne Komponente
  const RatingSelect = ({ value, onChange, label }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((num) => (
          <button
            key={num}
            type="button"
            onClick={() => onChange(num)}
            className={`w-10 h-10 rounded-lg border-2 transition-all ${
              value === num 
                ? "bg-amber-100 border-amber-400 text-amber-700" 
                : "bg-white border-gray-200 hover:border-amber-300"
            }`}
          >
            {num}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-400">1 = sehr gut, 5 = mangelhaft</p>
    </div>
  );

  if (!isOpen) return null;

  const fehlendePflicht = teilnehmer
    ? pflichtCodes(regel).filter((c) => !dokumenteNachCode(anhaenge)[c]?.length).length
    : 0;

  return (
    <Dialog open={isOpen} onOpenChange={(v) => { if (!bgModalOpen) onClose(v); }}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto p-0"
        onInteractOutside={(e) => { if (bgModalOpen) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (bgModalOpen) e.preventDefault(); }}
        onFocusOutside={(e) => { if (bgModalOpen) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (bgModalOpen) e.preventDefault(); }}
      >
        <DialogTitle className="sr-only">Teilnehmerkarte</DialogTitle>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : teilnehmer ? (
          <>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#d2c9c1] to-[#d1b198] flex items-center justify-center text-white font-semibold text-lg">
                    {teilnehmer.vorname?.[0]}{teilnehmer.nachname?.[0]}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>
                      {teilnehmer.vorname} {teilnehmer.nachname}
                      {(teilnehmer.status === "abgebrochen" || teilnehmer.kurs_abbruch) && (
                        <Badge data-testid="tn-abbruch-badge" className="ml-2 align-middle bg-red-600 text-white hover:bg-red-600">
                          ABBRUCH{teilnehmer.abbruch_datum ? ` · ${new Date(teilnehmer.abbruch_datum).toLocaleDateString("de-DE")}` : ""}
                        </Badge>
                      )}
                      {teilnehmer.status === "nicht_angetreten" && (
                        <Badge data-testid="tn-nicht-angetreten-badge" className="ml-2 align-middle bg-red-600 text-white hover:bg-red-600">NICHT ANGETRETEN</Badge>
                      )}
                    </h2>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      {teilnehmer.kurs && <span>{teilnehmer.kurs}</span>}
                      {teilnehmer.ninox_id && (
                        <Badge variant="outline" className="text-xs">Ninox: {teilnehmer.ninox_id}</Badge>
                      )}
                    </div>
                  </div>
                  <AuditAmpel teilnehmerId={teilnehmerId} />
                </div>
                <div className="flex items-center gap-2">
                  {/* BG-Ausfertigung Button - nur bei Bildungsgutschein */}
                  {teilnehmer.finanzierungsart === "bildungsgutschein" && (
                    <Button
                      onClick={() => setBgModalOpen(true)}
                      className="text-white hover:opacity-90 transition"
                      style={{ background: '#D9775A' }}
                      data-testid="bg-ausfertigung-open-btn"
                    >
                      <FileText size={16} className="mr-2" />
                      BG-Ausfertigung ausfüllen
                    </Button>
                  )}
                  {/* AZAV-Prüfung Button - nur bei Bildungsgutschein */}
                  {teilnehmer.finanzierungsart === "bildungsgutschein" && (
                    <Button 
                      onClick={runAzavPruefung}
                      disabled={azavLoading}
                      className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                    >
                      {azavLoading ? (
                        <Loader2 size={16} className="mr-2 animate-spin" />
                      ) : (
                        <Shield size={16} className="mr-2" />
                      )}
                      AZAV-Prüfung vorbereiten
                    </Button>
                  )}
                  <Badge className={
                    teilnehmer.finanzierungsart === "bildungsgutschein" 
                      ? "bg-amber-100 text-amber-700"
                      : teilnehmer.finanzierungsart === "einrichtung"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-700"
                  }>
                    {teilnehmer.bg_sz || teilnehmer.finanzierungsart?.toUpperCase() || "SZ"}
                  </Badge>
                  {teilnehmer.einwilligung_whatsapp_gruppe && (
                    <Badge
                      className="bg-green-100 text-green-700 border border-green-200 flex items-center gap-1"
                      title="Teilnehmer/in hat der WhatsApp-Kursgruppe zugestimmt — für Dozenten: darf eingeladen werden"
                      data-testid="badge-wa-gruppe-header"
                    >
                      <MessageCircle size={12} strokeWidth={2.5} /> WA-Gruppe OK
                    </Badge>
                  )}
                </div>
              </div>
              <TeilnehmerStatusLeiste
                teilnehmer={teilnehmer}
                anhaenge={anhaenge}
                regel={regel}
                onDokumente={() => setActiveTab("anhaenge")}
              />
            </div>

            {/* AZAV-Prüfung Dialog */}
            {azavDialogOpen && azavPruefung && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-hidden">
                  <div className={`px-6 py-4 ${
                    azavPruefung.ampel === 'gruen' ? 'bg-emerald-500' :
                    azavPruefung.ampel === 'gelb' ? 'bg-amber-500' : 'bg-red-500'
                  } text-white`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Shield size={28} />
                        <div>
                          <h3 className="text-lg font-semibold">AZAV-Prüfung</h3>
                          <p className="text-sm opacity-90">{azavPruefung.teilnehmer_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">
                          {azavPruefung.ampel === 'gruen' ? '✓ BEREIT' :
                           azavPruefung.ampel === 'gelb' ? 'HINWEISE' : '✗ NICHT BEREIT'}
                        </div>
                        <p className="text-xs opacity-75">
                          {azavPruefung.zusammenfassung?.gruen} ✓ | {azavPruefung.zusammenfassung?.gelb} | {azavPruefung.zusammenfassung?.rot} ✗
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6 overflow-y-auto max-h-[50vh]">
                    <h4 className="font-semibold mb-3">Dokumenten-Checkliste</h4>
                    <div className="space-y-2">
                      {azavPruefung.checks?.map((check, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            check.status === 'gruen' ? 'bg-emerald-50' :
                            check.status === 'gelb' ? 'bg-amber-50' : 'bg-red-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {check.status === 'gruen' ? (
                              <CheckCircle2 size={20} className="text-emerald-600" />
                            ) : check.status === 'gelb' ? (
                              <AlertTriangle size={20} className="text-amber-600" />
                            ) : (
                              <XCircle size={20} className="text-red-600" />
                            )}
                            <span className="font-medium">{check.name}</span>
                          </div>
                          <span className={`text-sm ${
                            check.status === 'gruen' ? 'text-emerald-700' :
                            check.status === 'gelb' ? 'text-amber-700' : 'text-red-700'
                          }`}>
                            {check.details?.substring(0, 50)}
                          </span>
                        </div>
                      ))}
                    </div>

                    {azavPruefung.fehlende_elemente?.length > 0 && (
                      <div className="mt-4 p-4 bg-red-50 rounded-lg">
                        <h4 className="font-semibold text-red-800 mb-2">Fehlende Elemente</h4>
                        <ul className="space-y-1 text-sm text-red-700">
                          {azavPruefung.fehlende_elemente.map((el, idx) => (
                            <li key={idx}>{el}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      Geprüft am: {new Date(azavPruefung.timestamp).toLocaleString('de-DE')}
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setAzavDialogOpen(false)}>
                        Schließen
                      </Button>
                      {(azavPruefung.ampel === 'gruen' || azavPruefung.ampel === 'gelb') && (
                        <Button 
                          onClick={downloadAzavPruefmappe}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <FileDown size={16} className="mr-2" />
                          Prüfmappe erstellen
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="px-6 pt-4 border-b">
                <TabsList className="flex flex-wrap h-auto w-full justify-start gap-1 bg-stone-100 p-1 rounded-lg" data-testid="tn-tabs">
                  <TabsTrigger value="stammdaten" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-stammdaten">
                    <User size={14} /> Stammdaten
                  </TabsTrigger>
                  <TabsTrigger value="anhaenge" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-dokumente">
                    <FileText size={14} /> Dokumente
                    {anhaengeFehler ? <Badge className="ml-1 text-[10px] px-1.5 py-0 bg-stone-400 text-white">?</Badge> : fehlendePflicht > 0
                      ? <Badge className="ml-1 text-[10px] px-1.5 py-0 bg-red-600 text-white hover:bg-red-600">{fehlendePflicht} fehlt</Badge>
                      : anhaenge.length > 0 && <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{anhaenge.length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="erstgespraech" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-vertraege">
                    <ClipboardCheck size={14} /> Verträge
                  </TabsTrigger>
                  <TabsTrigger value="pruefung" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-pruefung">
                    <Award size={14} /> Prüfung
                  </TabsTrigger>
                  {regel.azav && (
                    <TabsTrigger value="azav" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-azav">
                      <Shield size={14} /> AZAV
                    </TabsTrigger>
                  )}
                  {regel.praktikum && (
                    <TabsTrigger value="praktikum" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-praktikum">
                      <ClipboardCheck size={14} /> Praktikum
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="email" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-email">
                    <Send size={14} /> Start-E-Mail
                    {teilnehmer.start_email_gesendet && <CheckCircle size={12} className="text-emerald-500 ml-1" />}
                  </TabsTrigger>
                  <TabsTrigger value="befragung" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-befragung">
                    <MessageSquare size={14} /> Befragung
                    {teilnehmer.befragung_ausgefuellt && <CheckCircle size={12} className="text-emerald-500 ml-1" />}
                  </TabsTrigger>
                  <TabsTrigger value="verlauf" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-verlauf">
                    <History size={14} /> Verlauf
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Stammdaten Tab */}
              <TabsContent value="stammdaten" className="px-6 py-4 space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <User size={18} className="text-gray-500" /> Kontaktdaten
                      </CardTitle>
                      {!editKontakt ? (
                        <Button variant="outline" size="sm" onClick={() => setEditKontakt(true)}>
                          Bearbeiten
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => {
                            setEditKontakt(false);
                            setKontaktForm({
                              geburtsdatum: teilnehmer.geburtsdatum || "",
                              telefon: teilnehmer.telefon || "",
                              email: teilnehmer.email || "",
                              strasse: teilnehmer.strasse || "",
                              plz: teilnehmer.plz || "",
                              ort: teilnehmer.ort || ""
                            });
                          }}>
                            Abbrechen
                          </Button>
                          <Button size="sm" onClick={saveKontakt} disabled={saving}>
                            <Save size={14} className="mr-1" /> {saving ? "..." : "Speichern"}
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {editKontakt ? (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Vorname *</Label>
                          <Input 
                            value={kontaktForm.vorname || ""}
                            onChange={(e) => setKontaktForm({...kontaktForm, vorname: e.target.value})}
                            placeholder="Vorname"
                          />
                        </div>
                        <div>
                          <Label>Nachname *</Label>
                          <Input 
                            value={kontaktForm.nachname || ""}
                            onChange={(e) => setKontaktForm({...kontaktForm, nachname: e.target.value})}
                            placeholder="Nachname"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>Finanzierungsart *</Label>
                          <Select 
                            value={kontaktForm.finanzierungsart} 
                            onValueChange={(v) => setKontaktForm({...kontaktForm, finanzierungsart: v})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="selbstzahler">Selbstzahler (SZ)</SelectItem>
                              <SelectItem value="einrichtung">Pflegeeinrichtung (PE)</SelectItem>
                              <SelectItem value="bildungsgutschein">Bildungsgutschein (BG)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Geburtsdatum</Label>
                          <GeburtsdatumInput
                            value={kontaktForm.geburtsdatum}
                            onChange={(v) => setKontaktForm({...kontaktForm, geburtsdatum: v})}
                            testid="kontakt-geburtsdatum"
                          />
                        </div>
                        <div>
                          <Label>Telefon</Label>
                          <Input 
                            value={kontaktForm.telefon || ""}
                            onChange={(e) => setKontaktForm({...kontaktForm, telefon: e.target.value})}
                            placeholder="z.B. 0151 12345678"
                          />
                        </div>
                        <div>
                          <Label>WhatsApp-Nummer (optional)</Label>
                          <Input 
                            value={kontaktForm.whatsapp_nummer || ""}
                            onChange={(e) => setKontaktForm({...kontaktForm, whatsapp_nummer: e.target.value})}
                            placeholder="Falls abweichend von Telefon"
                            data-testid="whatsapp-nummer-input"
                          />
                        </div>
                        <div>
                          <Label>E-Mail</Label>
                          <Input 
                            type="email"
                            value={kontaktForm.email || ""}
                            onChange={(e) => setKontaktForm({...kontaktForm, email: e.target.value})}
                            placeholder="z.B. max@beispiel.de"
                          />
                        </div>
                        <div>
                          <Label>Straße</Label>
                          <Input 
                            value={kontaktForm.strasse || ""}
                            onChange={(e) => setKontaktForm({...kontaktForm, strasse: e.target.value})}
                            placeholder="z.B. Musterstraße 123"
                          />
                        </div>
                        <div>
                          <Label>PLZ</Label>
                          <Input 
                            value={kontaktForm.plz || ""}
                            onChange={(e) => setKontaktForm({...kontaktForm, plz: e.target.value})}
                            placeholder="z.B. 40210"
                          />
                        </div>
                        <div>
                          <Label>Ort</Label>
                          <Input 
                            value={kontaktForm.ort || ""}
                            onChange={(e) => setKontaktForm({...kontaktForm, ort: e.target.value})}
                            placeholder="z.B. Düsseldorf"
                          />
                        </div>
                      </div>
                    ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <Calendar size={16} className="text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500">Geburtsdatum</p>
                            <p className="font-medium">{formatDate(teilnehmer.geburtsdatum)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Phone size={16} className="text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500">Telefon</p>
                            <p className="font-medium">{teilnehmer.telefon || "-"}</p>
                          </div>
                        </div>
                        {teilnehmer.whatsapp_nummer && (
                          <div className="flex items-center gap-3">
                            <MessageCircle size={16} className="text-emerald-500" />
                            <div>
                              <p className="text-xs text-gray-500">WhatsApp</p>
                              <p className="font-medium">{teilnehmer.whatsapp_nummer}</p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-3">
                          <Mail size={16} className="text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500">E-Mail</p>
                            <p className="font-medium">{teilnehmer.email || "-"}</p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <MapPin size={16} className="text-gray-400 mt-1" />
                          <div>
                            <p className="text-xs text-gray-500">Adresse</p>
                            <p className="font-medium">{teilnehmer.strasse || "-"}</p>
                            <p className="text-sm text-gray-600">{teilnehmer.plz} {teilnehmer.ort}</p>
                          </div>
                        </div>
                        {teilnehmer.kundennummer && (
                          <div className="flex items-center gap-3">
                            <CreditCard size={16} className="text-gray-400" />
                            <div>
                              <p className="text-xs text-gray-500">Kundennummer</p>
                              <p className="font-medium font-mono">{teilnehmer.kundennummer}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    )}
                  </CardContent>
                </Card>

                {/* Kursart Card (Morgenkurs / Abendkurs / E-Learning) */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock size={18} className="text-gray-500" /> Kursart
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-gray-500 mb-3">
                      Mehrfachauswahl möglich – z.B. Morgenkurs plus E-Learning.
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { key: "morgenkurs", label: "Morgenkurs", icon: Sunrise, classes: "bg-[#FBEDE7] text-[#9A3412] border-[#EBC9B9]" },
                        { key: "abendkurs", label: "Abendkurs", icon: Moon, classes: "bg-[#2a2118] text-[#FBEDE7] border-[#3a2f22]" },
                        { key: "elearning", label: "E-Learning", icon: Laptop, classes: "bg-[#f8f4ec] text-[#5a4a35] border-[#e6d9c1]" },
                      ].map(o => {
                        const active = Array.isArray(teilnehmer.kurszeiten) && teilnehmer.kurszeiten.includes(o.key);
                        const Icon = o.icon;
                        return (
                          <button
                            key={o.key}
                            type="button"
                            data-testid={`detail-kurszeit-${o.key}`}
                            onClick={async () => {
                              const current = Array.isArray(teilnehmer.kurszeiten) ? [...teilnehmer.kurszeiten] : [];
                              const next = active ? current.filter(k => k !== o.key) : [...current, o.key];
                              try {
                                await axios.patch(`${API}/teilnehmer/${teilnehmerId}/kurszeiten`, { kurszeiten: next });
                                fetchTeilnehmer();
                                if (onUpdate) onUpdate();
                                toast.success("Kursart aktualisiert");
                              } catch (e) {
                                toast.error("Fehler beim Speichern");
                              }
                            }}
                            className={`flex flex-col items-center justify-center gap-1 py-3 rounded-lg border transition ${active ? o.classes + " shadow-sm" : "bg-white border-gray-200 hover:border-[#EBC9B9] hover:bg-[#faf6ee] text-gray-600"}`}
                          >
                            <Icon size={18} />
                            <span className="text-xs font-medium">{o.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Pflegeeinrichtung Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building2 size={18} className="text-gray-500" /> Pflegeeinrichtung
                      </CardTitle>
                      {!editPflegeeinrichtung ? (
                        <Button variant="outline" size="sm" onClick={() => setEditPflegeeinrichtung(true)}>
                          {teilnehmer.pflegeeinrichtung_id ? "Ändern" : "Zuordnen"}
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => {
                          setEditPflegeeinrichtung(false);
                          setPflegeeinrichtungSearch("");
                        }}>
                          Abbrechen
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {editPflegeeinrichtung ? (
                      <div className="space-y-3">
                        <div className="relative">
                          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <Input
                            placeholder="Pflegeeinrichtung suchen..."
                            value={pflegeeinrichtungSearch}
                            onChange={(e) => setPflegeeinrichtungSearch(e.target.value)}
                            className="pl-9"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto border rounded-md">
                          {/* Option zum Entfernen */}
                          {teilnehmer.pflegeeinrichtung_id && (
                            <div
                              className="p-2 hover:bg-red-50 cursor-pointer border-b text-red-600 flex items-center gap-2"
                              onClick={() => savePflegeeinrichtung(null)}
                            >
                              <XCircle size={14} /> Zuordnung entfernen
                            </div>
                          )}
                          {getFilteredPflegeeinrichtungen().map(pe => (
                            <div
                              key={pe.id}
                              className={`p-2 hover:bg-[#faf5f0] cursor-pointer border-b last:border-0 ${teilnehmer.pflegeeinrichtung_id === pe.id ? 'bg-[#faf5f0]' : ''}`}
                              onClick={() => savePflegeeinrichtung(pe.id)}
                            >
                              <p className="font-medium text-sm">{pe.einrichtungsname}</p>
                              <p className="text-xs text-gray-500">{pe.strasse}, {pe.plz} {pe.ort}</p>
                            </div>
                          ))}
                          {getFilteredPflegeeinrichtungen().length === 0 && (
                            <p className="p-3 text-sm text-gray-500 text-center">Keine Einrichtung gefunden</p>
                          )}
                        </div>
                        {pflegeeinrichtungen.length > 10 && !pflegeeinrichtungSearch && (
                          <p className="text-xs text-gray-400 text-center">Tippen Sie um zu suchen ({pflegeeinrichtungen.length} Einrichtungen)</p>
                        )}
                      </div>
                    ) : (
                      <div>
                        {teilnehmer.pflegeeinrichtung_id ? (
                          <div className="flex items-start gap-3">
                            <Building2 size={16} className="text-[#D9775A] mt-1" />
                            <div className="flex-1">
                              <button
                                className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                                onClick={() => navigate(`/pflegeeinrichtungen?id=${teilnehmer.pflegeeinrichtung_id}`)}
                              >
                                {getPflegeeinrichtungName(teilnehmer.pflegeeinrichtung_id) || "Lädt..."}
                              </button>
                              {teilnehmer.notion_pflegedienst_name && teilnehmer.notion_pflegedienst_name !== getPflegeeinrichtungName(teilnehmer.pflegeeinrichtung_id) && (
                                <p className="text-xs text-gray-400 mt-1">
                                  Notion: {teilnehmer.notion_pflegedienst_name}
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-3">
                            <Building2 size={24} className="mx-auto text-gray-300 mb-2" />
                            <p className="text-sm text-gray-500">Keine Pflegeeinrichtung zugeordnet</p>
                            {teilnehmer.notion_pflegedienst_name && (
                              <p className="text-xs text-amber-600 mt-1">
                                Notion: {teilnehmer.notion_pflegedienst_name}
                              </p>
                            )}
                            {teilnehmer.finanzierungsart === "einrichtung" && (
                              <p className="text-xs text-red-500 mt-1">Finanzierung über Einrichtung - bitte zuordnen!</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Interne Notizen Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <MessageSquare size={18} className="text-gray-500" /> Interne Notizen
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* Bestehende Notizen anzeigen */}
                      {teilnehmer.interne_notizen && (
                        <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                          <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                            {teilnehmer.interne_notizen}
                          </pre>
                        </div>
                      )}
                      
                      {/* Neue Notiz hinzufügen */}
                      <div className="border-t pt-3">
                        <Label className="text-xs text-gray-500 mb-1 block">Neue Notiz hinzufügen:</Label>
                        <Textarea
                          placeholder="Neue Notiz eingeben..."
                          value={interneNotizenLocal}
                          onChange={(e) => setInterneNotizenLocal(e.target.value)}
                          className="min-h-[60px]"
                        />
                        {interneNotizenLocal.trim() && (
                          <div className="flex justify-end gap-2 mt-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setInterneNotizenLocal("")}
                            >
                              Abbrechen
                            </Button>
                            <Button 
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700"
                              onClick={async () => {
                                try {
                                  const heute = new Date().toLocaleDateString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit', 
                                    year: 'numeric'
                                  });
                                  const benutzer = user?.name || 'Unbekannt';
                                  const neueNotiz = `[${heute} - ${benutzer}] ${interneNotizenLocal.trim()}`;
                                  const alleNotizen = teilnehmer.interne_notizen 
                                    ? `${neueNotiz}\n\n${teilnehmer.interne_notizen}`
                                    : neueNotiz;
                                  
                                  await axios.put(`${API}/teilnehmer/${teilnehmerId}`, {
                                    interne_notizen: alleNotizen
                                  });
                                  setInterneNotizenLocal("");
                                  fetchTeilnehmer();
                                  fetchHistorie();
                                  toast.success("Notiz hinzugefügt");
                                } catch (error) {
                                  toast.error("Fehler beim Speichern");
                                }
                              }}
                            >
                              + Notiz hinzufügen
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* NEUE Kurs-Verwaltung mit vollständigen Kurs-Daten */}
                <Card>
                  <CardContent className="pt-4">
                    <TeilnehmerKurse 
                      teilnehmerId={teilnehmerId}
                      teilnehmer={teilnehmer}
                      massnahmen={massnahmen}
                      onKurseChanged={() => fetchTeilnehmer(true)}
                    />
                    
                    {/* Finanzierung - bleibt global */}
                    <div className="border-t mt-4 pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-sm flex items-center gap-2">
                          <CreditCard size={16} className="text-gray-500" /> Finanzierung
                        </h4>
                        {!editKurs ? (
                          <Button variant="outline" size="sm" onClick={() => setEditKurs(true)}>
                            Bearbeiten
                          </Button>
                        ) : (
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => {
                              setEditKurs(false);
                              setKursForm({
                                massnahme_id: teilnehmer.massnahme_id || "",
                                kurs: teilnehmer.kurs || "",
                                kurs_start: teilnehmer.kurs_start || "",
                                kurs_ende: teilnehmer.kurs_ende || "",
                                finanzierungsart: teilnehmer.finanzierungsart || "selbstzahler"
                              });
                            }}>
                              Abbrechen
                            </Button>
                            <Button size="sm" onClick={saveKursZuweisung} disabled={saving}>
                              <Save size={14} className="mr-1" /> {saving ? "..." : "Speichern"}
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {editKurs ? (
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs">Finanzierungsart</Label>
                            <Select 
                              value={kursForm.finanzierungsart || "selbstzahler"} 
                              onValueChange={(v) => setKursForm({...kursForm, finanzierungsart: v})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="selbstzahler">Selbstzahler (SZ)</SelectItem>
                                <SelectItem value="einrichtung">Pflegeeinrichtung (PE)</SelectItem>
                                <SelectItem value="bildungsgutschein">Bildungsgutschein (BG)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Finanzierungsart</p>
                            <p className="font-medium capitalize">{teilnehmer.finanzierungsart || "Selbstzahler"}</p>
                          </div>
                          {teilnehmer.institution && (
                            <div>
                              <p className="text-xs text-gray-500">Institution/Kostenträger</p>
                              <p className="font-medium">{teilnehmer.institution}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Klassenbuch-Aktionen — immer sichtbar; Buttons prüfen Kursdaten intern und zeigen ggf. Toast */}
                    {true && (
                      <div className="border-t mt-4 pt-4 space-y-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                          data-testid="btn-anwesenheit-pdf"
                          onClick={async () => {
                            try {
                              toast.info("Anwesenheits-PDF wird generiert...");
                              const res = await axios.get(`${API}/klassenbuch-neu/teilnehmer/${teilnehmerId}/stundenplan-pdf`, { responseType: "blob" });
                              const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                              const a = document.createElement("a"); a.href = url;
                              a.download = `Stundenplan_Anwesenheit_${teilnehmer.nachname || 'TN'}.pdf`;
                              document.body.appendChild(a); a.click(); a.remove();
                              toast.success("Stundenplan & Anwesenheit heruntergeladen");
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Noch kein Klassenbuch für diesen Kurs vorhanden. Bitte zuerst über die Klassenbuch-Verwaltung anlegen.");
                            }
                          }}
                        >
                          <FileDown size={16} className="mr-2" />
                          Stundenplan & Anwesenheit (PDF)
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200"
                          data-testid="btn-azav-klassenbuch-pdf"
                          onClick={async () => {
                            try {
                              toast.info("AZAV-Klassenbuch wird generiert...");
                              const res = await axios.get(`${API}/klassenbuch-neu/teilnehmer/${teilnehmerId}/azav-klassenbuch-pdf`, { responseType: "blob" });
                              const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                              const a = document.createElement("a"); a.href = url;
                              a.download = `AZAV_Klassenbuch_${teilnehmer.nachname || 'TN'}.pdf`;
                              document.body.appendChild(a); a.click(); a.remove();
                              toast.success("AZAV-Klassenbuch heruntergeladen");
                            } catch (err) {
                              let msg = "Noch kein Klassenbuch vorhanden";
                              try {
                                if (err.response?.data instanceof Blob) {
                                  const txt = await err.response.data.text();
                                  try { msg = JSON.parse(txt).detail || txt; } catch { msg = txt; }
                                } else if (err.response?.data?.detail) {
                                  msg = err.response.data.detail;
                                }
                              } catch (_) {}
                              toast.error(msg);
                            }
                          }}
                        >
                          <FileDown size={16} className="mr-2" />
                          AZAV-Klassenbuch (Themen + Lernziele + Anwesenheit)
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-purple-700 hover:bg-purple-50"
                          data-testid="btn-klassenbuch-verwaltung"
                          onClick={() => {
                            onClose();
                            navigate(`/klassenbuch-neu?teilnehmer=${teilnehmerId}`);
                          }}
                        >
                          <BookOpen size={16} className="mr-2" />
                          Klassenbuch-Verwaltung öffnen
                        </Button>

                        {/* === NEU: Anwesenheitsliste online signieren lassen === */}
                        <div className="border-t mt-3 pt-3 space-y-2">
                          <div className="flex items-start gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
                            <Pen size={14} className="mt-0.5 flex-shrink-0" />
                            <span>
                              <b>Online unterschreiben lassen:</b> Eine Signatur des Teilnehmers wird elektronisch erfasst und automatisch in jede Tageszeile der Anwesenheitsliste gestempelt — zusammen mit der hinterlegten Dozent-Unterschrift.
                            </span>
                          </div>
                          {teilnehmer.referenz_unterschrift_b64 && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full border-emerald-300 text-emerald-700 bg-emerald-50/40"
                              data-testid="btn-anwesenheit-with-stored-sig"
                              onClick={() => {
                                setAlSignForm({
                                  dozent_kuerzel: "AL",
                                  sign_date: "",
                                  datum_von: "",
                                  datum_bis: "",
                                  only_anwesend: true,
                                  custom_message: "",
                                  use_stored_signature: true,
                                });
                                setAlPreviewUrl(null);
                                setShowAnwesenheitSignModal(true);
                              }}
                            >
                              <Pen size={14} className="mr-2" /> Mit gespeicherter Unterschrift füllen
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="btn-anwesenheit-quick"
                            onClick={openQuickAnwesenheit}
                          >
                            <MailPlus size={14} className="mr-2" />
                            Schnell senden (Auto: Kursstart → heute)
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full text-xs"
                            data-testid="btn-anwesenheit-sign-online"
                            disabled={!teilnehmer.email}
                            onClick={() => {
                              setAlSignForm({
                                dozent_kuerzel: "AL",
                                sign_date: "",
                                datum_von: "",
                                datum_bis: "",
                                only_anwesend: true,
                                custom_message: "",
                                use_stored_signature: false,
                              });
                              setAlPreviewUrl(null);
                              setShowAnwesenheitSignModal(true);
                            }}
                          >
                            <MailPlus size={14} className="mr-2" />
                            Anwesenheitsliste online unterschreiben lassen
                          </Button>
                          {/* Notfall-Upload: TN hat per Hand/E-Mail signiert → manuell hochladen */}
                          <div className="border-t border-stone-200 pt-2 mt-2">
                            <input
                              type="file"
                              id="upload-anwesenheit-signed"
                              accept="application/pdf,image/*"
                              className="hidden"
                              data-testid="input-upload-al-signed"
                              onChange={async (e) => {
                                const f = e.target.files?.[0];
                                if (!f) return;
                                try {
                                  const fd = new FormData();
                                  fd.append("file", f);
                                  fd.append("kategorie", "AL");
                                  fd.append("signed", "true");
                                  toast.info(`„${f.name}" wird hochgeladen...`);
                                  await axios.post(`${API}/teilnehmer/${teilnehmerId}/anhaenge`, fd, {
                                    headers: { "Content-Type": "multipart/form-data" }
                                  });
                                  toast.success("Signierte Anwesenheitsliste hochgeladen und im CRM gespeichert");
                                  try { fetchAnhaenge(); } catch (_) {}
                                  try { await fetchTeilnehmer(); } catch (_) {}
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
                                } finally {
                                  e.target.value = "";
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full text-xs"
                              data-testid="btn-al-manual-upload"
                              onClick={() => document.getElementById('upload-anwesenheit-signed')?.click()}
                            >
                              <Upload size={12} className="mr-2" />
                              Signierte Version manuell hochladen (Notfall)
                            </Button>
                            <p className="text-[10px] text-stone-500 mt-1 px-1">
                              Falls der Sign-Link fehlschlug oder der TN per E-Mail/Post signiert: PDF hier ablegen — wird als <code>AL signed</code> in den Anhängen gespeichert.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* AZAV Tab (nur für Bildungsgutschein) */}
              {teilnehmer.finanzierungsart === 'bildungsgutschein' && (
                <TabsContent value="azav" className="px-6 py-4 space-y-4">
                  {klassenbuchLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <Loader2 className="animate-spin" size={24} />
                    </div>
                  ) : klassenbuchData ? (
                    <>
                      {/* Statistik-Karten */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Card>
                          <CardContent className="p-3 text-center">
                            <p className="text-xl font-bold">{klassenbuchData.statistik?.theorie?.ue_erreicht || 0}</p>
                            <p className="text-xs text-gray-500">UE erreicht</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-3 text-center">
                            <p className="text-xl font-bold text-emerald-600">{klassenbuchData.statistik?.theorie?.anwesend || 0}</p>
                            <p className="text-xs text-gray-500">Tage anwesend</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-3 text-center">
                            <p className="text-xl font-bold text-amber-600">{klassenbuchData.statistik?.theorie?.entschuldigt || 0}</p>
                            <p className="text-xs text-gray-500">Entschuldigt</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-3 text-center">
                            <p className="text-xl font-bold text-red-600">{klassenbuchData.statistik?.theorie?.unentschuldigt || 0}</p>
                            <p className="text-xs text-gray-500">Unentschuldigt</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Praktikum */}
                      {klassenbuchData.statistik?.praktikum?.total > 0 && (
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <GraduationCap size={16} /> Praktikum
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="flex gap-6">
                              <div>
                                <span className="text-lg font-bold text-[#D9775A]">
                                  {klassenbuchData.statistik?.praktikum?.anwesend}/{klassenbuchData.statistik?.praktikum?.total}
                                </span>
                                <p className="text-xs text-gray-500">Praktikumstage</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Praktikums-Dokumentation - NUR wenn Praktikum erforderlich (Pflegehilfskraft/Betreuung) */}
                      {regel.praktikum ? (
                        <Card>
                          <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-sm flex items-center gap-2">
                                <FileText size={16} /> Praktikums-Dokumentation
                              </CardTitle>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-xs text-gray-500 hover:text-red-500"
                                onClick={() => updateTeilnehmerField('praktikum_erforderlich', false)}
                              >
                                <X size={14} className="mr-1" /> Nicht erforderlich
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs text-gray-500">Praktikumseinrichtung</Label>
                                <Input 
                                  value={teilnehmer.praktikumseinrichtung || ''}
                                  onChange={(e) => updateTeilnehmerField('praktikumseinrichtung', e.target.value)}
                                  placeholder="Name der Einrichtung"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">Praktikumsvertrag</Label>
                                <div className="flex items-center gap-2 mt-1">
                                  <input 
                                    type="checkbox"
                                    checked={teilnehmer.praktikumsvertrag_vorhanden || false}
                                    onChange={(e) => updateTeilnehmerField('praktikumsvertrag_vorhanden', e.target.checked)}
                                    className="w-4 h-4"
                                  />
                                  <span className="text-sm">Vorhanden</span>
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">Vertrag angefordert am</Label>
                                <Input 
                                  type="date"
                                  value={teilnehmer.praktikumsvertrag_angefordert_am || ''}
                                  onChange={(e) => updateTeilnehmerField('praktikumsvertrag_angefordert_am', e.target.value)}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">Vertrag erhalten am</Label>
                                <Input 
                                  type="date"
                                  value={teilnehmer.praktikumsvertrag_erhalten_am || ''}
                                  onChange={(e) => updateTeilnehmerField('praktikumsvertrag_erhalten_am', e.target.value)}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">Praktikum Beginn</Label>
                                <Input 
                                  type="date"
                                  value={teilnehmer.praktikum_beginn || ''}
                                  onChange={(e) => updateTeilnehmerField('praktikum_beginn', e.target.value)}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">Praktikum Ende</Label>
                                <Input 
                                  type="date"
                                  value={teilnehmer.praktikum_ende || ''}
                                  onChange={(e) => updateTeilnehmerField('praktikum_ende', e.target.value)}
                                  className="mt-1"
                                />
                              </div>
                              <div className="col-span-2">
                                <Label className="text-xs text-gray-500">Notiz zum Praktikum</Label>
                                <Input 
                                  value={teilnehmer.praktikum_notiz || ''}
                                  onChange={(e) => updateTeilnehmerField('praktikum_notiz', e.target.value)}
                                  placeholder="Interne Notiz..."
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card className="border-dashed border-gray-300 bg-gray-50/50">
                          <CardContent className="py-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-gray-500">
                                <CheckCircle size={16} className="text-emerald-500" />
                                <span className="text-sm">Kein Praktikum erforderlich (LG1/LG2)</span>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-xs text-gray-400 hover:text-gray-600"
                                onClick={() => updateTeilnehmerField('praktikum_erforderlich', true)}
                              >
                                <Plus size={14} className="mr-1" /> Praktikum hinzufügen
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* AZAV-Dokumente Checkliste */}
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Shield size={16} /> AZAV-Dokumente
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between py-2 border-b">
                              <span className="text-sm">Bildungsgutschein-Scan</span>
                              {(teilnehmer.bildungsgutschein_scan
                                || teilnehmer.bildungsgutschein_vorhanden
                                || teilnehmer.bildungsgutschein_scan_data
                                || (teilnehmer.anhaenge || []).some(a => a.kategorie === "BG")
                                || anhaenge.some(a => a.kategorie === "BG")) ? (
                                <Badge className="bg-emerald-100 text-emerald-700">Vorhanden</Badge>
                              ) : (
                                <Badge variant="destructive">Fehlt</Badge>
                              )}
                            </div>
                            <div className="flex items-center justify-between py-2 border-b">
                              <span className="text-sm">Bildungsgutschein-Nr.</span>
                              {teilnehmer.bildungsgutschein_nummer ? (
                                <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">{teilnehmer.bildungsgutschein_nummer}</span>
                              ) : (
                                <Badge variant="destructive">Fehlt</Badge>
                              )}
                            </div>
                            <div 
                              className="flex items-center justify-between py-2 border-b cursor-pointer hover:bg-gray-50 -mx-4 px-4 transition-colors"
                              onClick={loadKlassenbuchEintraege}
                            >
                              <span className="text-sm flex items-center gap-2">
                                <BookOpen size={14} className="text-[#D9775A]" />
                                Klassenbuch-Einträge
                              </span>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-emerald-100 text-emerald-700">
                                  {klassenbuchData.statistik?.theorie?.total || 0} Tage
                                </Badge>
                                <ChevronRight size={16} className="text-gray-400" />
                              </div>
                            </div>
                            <div className="flex items-center justify-between py-2 border-b">
                              <span className="text-sm">Erstgespräch-Checkliste</span>
                              {teilnehmer.erstgespraech_checkliste ? (
                                <Badge className="bg-emerald-100 text-emerald-700">Vorhanden</Badge>
                              ) : (
                                <Badge variant="outline">Ausstehend</Badge>
                              )}
                            </div>
                            {/* Praktikumsvertrag nur anzeigen wenn erforderlich */}
                            {regel.praktikum && (
                              <div className="flex items-center justify-between py-2 border-b">
                                <span className="text-sm">Praktikumsvertrag</span>
                                {(teilnehmer.praktikumsvertrag_vorhanden
                                  || teilnehmer.praktikumsvertrag_scan_data
                                  || (teilnehmer.anhaenge || []).some(a => a.kategorie === "PV")
                                  || anhaenge.some(a => a.kategorie === "PV")) ? (
                                  <Badge className="bg-emerald-100 text-emerald-700">Vorhanden</Badge>
                                ) : teilnehmer.praktikumsvertrag_angefordert_am ? (
                                  <Badge className="bg-amber-100 text-amber-700">
                                    Angefordert {new Date(teilnehmer.praktikumsvertrag_angefordert_am).toLocaleDateString('de-DE')}
                                  </Badge>
                                ) : (
                                  <Badge variant="destructive">Fehlt</Badge>
                                )}
                              </div>
                            )}
                            <div className="flex items-center justify-between py-2 border-b">
                              <span className="text-sm">Prüfungsdokumente</span>
                              <div className="flex items-center gap-2">
                                <Badge className={anhaenge.filter(a => a.kategorie === 'PR').length > 0 ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}>
                                  {anhaenge.filter(a => a.kategorie === 'PR').length} Dokument(e)
                                </Badge>
                              </div>
                            </div>
                            <div className="flex items-center justify-between py-2 border-b">
                              <span className="text-sm">AU-Bescheinigungen</span>
                              <div className="flex items-center gap-2">
                                <Badge className={anhaenge.filter(a => a.kategorie === 'AU').length > 0 ? "bg-rose-100 text-rose-700" : "bg-gray-100 text-gray-500"}>
                                  {anhaenge.filter(a => a.kategorie === 'AU').length} Dokument(e)
                                </Badge>
                              </div>
                            </div>
                            <div className="flex items-center justify-between py-2">
                              <span className="text-sm">Teilnehmerbefragung</span>
                              {teilnehmer.befragung_verschickt ? (
                                <Badge className="bg-emerald-100 text-emerald-700">Verschickt</Badge>
                              ) : (
                                <Badge variant="outline">Ausstehend</Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Uploads laufen einheitlich über die Dokument-Kacheln */}
                      <Button variant="outline" className="w-full" onClick={() => setActiveTab("anhaenge")} data-testid="azav-zu-dokumenten-btn">
                        <Upload size={14} className="mr-2" /> Dokumente hochladen (Prüfung, AU-Bescheinigung …)
                      </Button>

                      {/* Klassenbuch Detail Ansicht */}
                      {showKlassenbuchDetail && klassenbuchEintraege.length > 0 && (
                        <Card>
                          <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-sm flex items-center gap-2">
                                <BookOpen size={16} /> Klassenbuch - {teilnehmer.kurs}
                              </CardTitle>
                              <Button variant="ghost" size="sm" onClick={() => setShowKlassenbuchDetail(false)}>
                                <X size={16} />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="max-h-64 overflow-y-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="text-left py-2 px-2 font-medium">Tag</th>
                                    <th className="text-left py-2 px-2 font-medium">Datum</th>
                                    <th className="text-left py-2 px-2 font-medium">Thema</th>
                                    <th className="text-center py-2 px-2 font-medium">UE</th>
                                    <th className="text-center py-2 px-2 font-medium">Status</th>
                                    <th className="text-center py-2 px-1 font-medium w-8"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {klassenbuchEintraege.map((kb, idx) => {
                                    // Anwesenheit für diesen Tag finden
                                    const anw = klassenbuchData.anwesenheit?.find(a => a.datum === kb.datum);
                                    return (
                                      <tr key={kb.id || idx} className="border-b last:border-0 hover:bg-gray-50">
                                        <td className="py-1.5 px-2 text-gray-500">{kb.tag_im_kurs || idx + 1}</td>
                                        <td className="py-1.5 px-2">
                                          {new Date(kb.datum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                        </td>
                                        <td className="py-1.5 px-2 max-w-[200px] truncate" title={kb.thema}>
                                          {kb.thema}
                                        </td>
                                        <td className="py-1.5 px-2 text-center">{kb.ue}</td>
                                        <td className="py-1.5 px-2 text-center">
                                          {anw ? (
                                            <button 
                                              onClick={() => openAnwesenheitEdit(anw)}
                                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs cursor-pointer hover:opacity-80 ${
                                                anw.status === 'anwesend' ? 'bg-emerald-50 text-emerald-700' :
                                                anw.status === 'entschuldigt' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                                              }`}
                                              title="Klicken zum Bearbeiten"
                                            >
                                              {anw.status === 'anwesend' ? <CheckCircle2 size={10} /> : 
                                               anw.status === 'entschuldigt' ? <AlertTriangle size={10} /> : <XCircle size={10} />}
                                              {anw.status === 'anwesend' ? 'A' : anw.status === 'entschuldigt' ? 'E' : 'U'}
                                            </button>
                                          ) : '-'}
                                        </td>
                                        <td className="py-1.5 px-1 text-center">
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-6 w-6 p-0"
                                            onClick={() => anw && openAnwesenheitEdit(anw)}
                                            title="Anwesenheit bearbeiten"
                                          >
                                            <Pencil size={12} />
                                          </Button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Anwesenheit bearbeiten Dialog */}
                      {editingAnwesenheit && (
                        <Card className="border-2 border-[#D9775A]">
                          <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-sm flex items-center gap-2">
                                <Calendar size={16} /> Anwesenheit bearbeiten - {new Date(editingAnwesenheit.datum).toLocaleDateString('de-DE')}
                              </CardTitle>
                              <Button variant="ghost" size="sm" onClick={() => setEditingAnwesenheit(null)}>
                                <X size={16} />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0 space-y-3">
                            <div>
                              <Label className="text-xs text-gray-500">Status</Label>
                              <div className="flex gap-2 mt-1">
                                <Button 
                                  size="sm"
                                  variant={anwesenheitForm.status === 'anwesend' ? 'default' : 'outline'}
                                  className={anwesenheitForm.status === 'anwesend' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
                                  onClick={() => setAnwesenheitForm(prev => ({...prev, status: 'anwesend'}))}
                                >
                                  <CheckCircle2 size={14} className="mr-1" /> Anwesend
                                </Button>
                                <Button 
                                  size="sm"
                                  variant={anwesenheitForm.status === 'entschuldigt' ? 'default' : 'outline'}
                                  className={anwesenheitForm.status === 'entschuldigt' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                                  onClick={() => setAnwesenheitForm(prev => ({...prev, status: 'entschuldigt'}))}
                                >
                                  <AlertTriangle size={14} className="mr-1" /> Entschuldigt (AU)
                                </Button>
                                <Button 
                                  size="sm"
                                  variant={anwesenheitForm.status === 'unentschuldigt' ? 'default' : 'outline'}
                                  className={anwesenheitForm.status === 'unentschuldigt' ? 'bg-red-500 hover:bg-red-600' : ''}
                                  onClick={() => setAnwesenheitForm(prev => ({...prev, status: 'unentschuldigt'}))}
                                >
                                  <XCircle size={14} className="mr-1" /> Unentschuldigt
                                </Button>
                              </div>
                            </div>
                            {anwesenheitForm.status === 'entschuldigt' && (
                              <div>
                                <Label className="text-xs text-gray-500">Grund (z.B. AU)</Label>
                                <Input 
                                  value={anwesenheitForm.grund}
                                  onChange={(e) => setAnwesenheitForm(prev => ({...prev, grund: e.target.value}))}
                                  placeholder="z.B. AU-Bescheinigung vorhanden"
                                  className="mt-1"
                                />
                              </div>
                            )}
                            <div className="flex gap-2 pt-2">
                              <Button onClick={saveAnwesenheit} className="bg-[#D9775A] hover:bg-[#C2603F]">
                                <Save size={14} className="mr-1" /> Speichern
                              </Button>
                              <Button variant="outline" onClick={() => setEditingAnwesenheit(null)}>
                                Abbrechen
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Anwesenheitsverlauf (kompakt) */}
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Calendar size={16} /> Anwesenheitsverlauf (letzte 10 Tage)
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {klassenbuchData.anwesenheit?.slice(-10).reverse().map((a, idx) => (
                              <div key={idx} className="flex items-center justify-between py-1 text-sm border-b last:border-0">
                                <span className="text-gray-600">{new Date(a.datum).toLocaleDateString('de-DE')}</span>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                  a.status === 'anwesend' ? 'bg-emerald-50 text-emerald-700' :
                                  a.status === 'entschuldigt' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                                }`}>
                                  {a.status === 'anwesend' && <CheckCircle2 size={12} />}
                                  {a.status === 'entschuldigt' && <AlertTriangle size={12} />}
                                  {a.status === 'unentschuldigt' && <XCircle size={12} />}
                                  {a.status === 'anwesend' ? 'Anwesend' : a.status === 'entschuldigt' ? 'Entschuldigt' : 'Unentschuldigt'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Aktionen */}
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={runAzavPruefung}>
                          <Shield size={14} className="mr-1" /> AZAV-Prüfung
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Card>
                      <CardContent className="p-6 text-center text-gray-500">
                        <Shield size={32} className="mx-auto mb-2 text-gray-300" />
                        <p>Keine Klassenbuch-Daten vorhanden</p>
                        <p className="text-sm mt-1">Klicken Sie auf den Tab um die Daten zu laden</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              )}

              {/* Verträge & Erstgespräch Tab */}
              <TabsContent value="erstgespraech" className="px-6 py-4 space-y-4">
                {/* Datenschutz-Einwilligungen (Toggles → PDF-Kreuze) */}
                <Card className="border-l-4 border-l-blue-500">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield size={18} className="text-blue-500" /> Datenschutz-Einwilligungen
                    </CardTitle>
                    <p className="text-xs text-stone-500 mt-1">
                      Freiwillig, jederzeit widerrufbar. Die Auswahl wird als Häkchen in das FB_TN_03-PDF übernommen.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { key: "einwilligung_fotos", label: "Foto- / Bildaufnahmen für Marketing & QM", hint: "Webseite, Social Media, interne Qualitätssicherung" },
                      { key: "einwilligung_whatsapp", label: "Kommunikation per WhatsApp", hint: "Termine, Materialien, Rückfragen zur Kursorganisation" },
                      { key: "einwilligung_whatsapp_gruppe", label: "Teilnahme an WhatsApp-Kursgruppe", hint: "Austausch mit Mitteilnehmenden und Dozent:innen" },
                    ].map(({ key, label, hint }) => (
                      <div key={key} className="flex items-start justify-between gap-4 p-3 bg-blue-50/40 border border-blue-100 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-slate-900">{label}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
                        </div>
                        <Switch
                          data-testid={`toggle-${key}`}
                          checked={!!teilnehmer?.[key]}
                          onCheckedChange={async (checked) => {
                            try {
                              const r = await axios.patch(
                                `${API}/teilnehmer/${teilnehmerId}/einwilligungen`,
                                { [key]: checked }
                              );
                              setTeilnehmer((t) => ({ ...t, ...(r.data?.einwilligungen || {}) }));
                              toast.success(`${label}: ${checked ? "erteilt" : "widerrufen"}`);
                            } catch (e) {
                              toast.error("Speichern fehlgeschlagen",
                                { description: e?.response?.data?.detail || e.message });
                            }
                          }}
                        />
                      </div>
                    ))}
                    {teilnehmer?.einwilligung_updated_at && (
                      <p className="text-xs text-slate-400">
                        Zuletzt aktualisiert: {new Date(teilnehmer.einwilligung_updated_at).toLocaleString("de-DE")}
                      </p>
                    )}
                  </CardContent>
                </Card>

                <EinwilligungEUnterschrift teilnehmerId={teilnehmerId} />

                <RechnungEinwilligungBox tnId={teilnehmerId} />

                {/* AZAV-Druckvorlagen pro Teilnehmer */}
                <Card className="border-l-4 border-l-emerald-500">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText size={18} className="text-emerald-500" /> AZAV-Verträge & Pflichtdokumente
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      <Button
                        variant="outline"
                        className="h-auto py-3 flex flex-col items-start gap-1 hover:bg-emerald-50 hover:border-emerald-300"
                        data-testid="btn-massnahmevertrag"
                        onClick={async () => {
                          try {
                            toast.info("Maßnahmevertrag wird generiert...");
                            const res = await axios.get(`${API}/teilnehmer/${teilnehmerId}/massnahmevertrag-pdf`, { responseType: "blob" });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            const a = document.createElement("a"); a.href = url; a.download = `Massnahmevertrag_${teilnehmer.nachname || 'TN'}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            toast.success("Maßnahmevertrag heruntergeladen");
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Fehler bei Maßnahmevertrag");
                          }
                        }}
                      >
                        <span className="text-xs text-emerald-700 font-mono">FB_TN_02</span>
                        <span className="font-medium text-sm">Maßnahmevertrag</span>
                        <span className="text-xs text-stone-500">Mit Kursdaten + Preis</span>
                      </Button>
                      <Button
                        variant="outline"
                        className="h-auto py-3 flex flex-col items-start gap-1 hover:bg-emerald-50 hover:border-emerald-300"
                        data-testid="btn-schulungsplan"
                        onClick={async () => {
                          try {
                            const kursId = teilnehmer?.kurs_id || teilnehmer?.kurse?.[0]?.kurs_id || "lg";
                            // Maßnahmenstart als Plan-Startdatum nutzen (falls vorhanden)
                            const startDatum = teilnehmer?.kurse?.[0]?.start || teilnehmer?.kurse?.[0]?.kurs_start || teilnehmer?.start || teilnehmer?.kurs_start || "";
                            const q = startDatum ? `?start_datum=${encodeURIComponent(startDatum)}` : "";
                            toast.info("Schulungsplan (AfA) wird generiert...");
                            const res = await axios.get(`${API}/kurse/${encodeURIComponent(kursId)}/schulungsplan-pdf${q}`, { responseType: "blob" });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            const a = document.createElement("a"); a.href = url; a.download = `Schulungsplan_${kursId}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            toast.success("Schulungsplan heruntergeladen");
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Fehler beim Schulungsplan");
                          }
                        }}
                      >
                        <span className="text-xs text-emerald-700 font-mono">FB_TN_12</span>
                        <span className="font-medium text-sm">Schulungsplan (AfA)</span>
                        <span className="text-xs text-stone-500">Curriculum + Tagesplan</span>
                      </Button>
                      <Button
                        variant="outline"
                        className="h-auto py-3 flex flex-col items-start gap-1 hover:bg-emerald-50 hover:border-emerald-300"
                        data-testid="btn-agb"
                        onClick={async () => {
                          try {
                            toast.info("AGB-PDF wird generiert...");
                            const res = await axios.get(`${API}/azav/agb-pdf`, { responseType: "blob" });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            const a = document.createElement("a"); a.href = url; a.download = `AGB_Dickies_Campus.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            toast.success("AGB heruntergeladen");
                          } catch (err) {
                            toast.error("Fehler bei AGB-PDF");
                          }
                        }}
                      >
                        <span className="text-xs text-emerald-700 font-mono">FB_TN_02a</span>
                        <span className="font-medium text-sm">AGB Dickies Campus</span>
                        <span className="text-xs text-stone-500">Anlage zum Vertrag</span>
                      </Button>
                      <Button
                        variant="outline"
                        className="h-auto py-3 flex flex-col items-start gap-1 hover:bg-emerald-50 hover:border-emerald-300"
                        data-testid="btn-datenschutz"
                        onClick={async () => {
                          try {
                            toast.info("Datenschutz-PDF wird generiert...");
                            const res = await axios.get(`${API}/teilnehmer/${teilnehmerId}/datenschutz-pdf`, { responseType: "blob" });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            const a = document.createElement("a"); a.href = url; a.download = `Datenschutz_${teilnehmer.nachname || 'TN'}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            toast.success("Datenschutz-PDF heruntergeladen");
                          } catch (err) {
                            toast.error("Fehler bei Datenschutz-PDF");
                          }
                        }}
                      >
                        <span className="text-xs text-emerald-700 font-mono">FB_TN_03</span>
                        <span className="font-medium text-sm">Datenschutzinformationen</span>
                        <span className="text-xs text-stone-500">Mit Empfangsbestätigung</span>
                      </Button>
                      <Button
                        variant="outline"
                        className="h-auto py-3 flex flex-col items-start gap-1 hover:bg-emerald-50 hover:border-emerald-300"
                        data-testid="btn-erstgespraech-checkliste"
                        onClick={downloadErstgespraechPDF}
                      >
                        <span className="text-xs text-emerald-700 font-mono">FB_TN_01</span>
                        <span className="font-medium text-sm">Erstgespräch-Checkliste</span>
                        <span className="text-xs text-stone-500">Mit Daten aus diesem Tab</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <TechnikCheck teilnehmerId={teilnehmerId} initial={teilnehmer.technik_check} onSaved={fetchTeilnehmer} />

                {/* Zertifikate pro Kurs (Multi-Kurs-fähig) */}
                <Card className="border-l-4 border-l-yellow-500">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Award size={18} className="text-yellow-600" /> Zertifikate (pro Kurs)
                    </CardTitle>
                    <p className="text-xs text-stone-500 mt-1">
                      Druck nur möglich, wenn Kurs als bestanden markiert ist. Fortlaufende Nummer wird pro Druck vergeben und am jeweiligen Kurs gespeichert.
                    </p>
                  </CardHeader>
                  <CardContent>
                    {!kurseZertifikatStatus ? (
                      <p className="text-sm text-stone-500">Lade Kursdaten...</p>
                    ) : (kurseZertifikatStatus.kurse || []).length === 0 ? (
                      <p className="text-sm text-stone-500">Keine Kurse hinterlegt. Bitte zuerst einen Kurs im Stammdaten-Tab anlegen.</p>
                    ) : (
                      <div className="space-y-3">
                        {kurseZertifikatStatus.kurse.map((k, idx) => {
                          const istAusgestellt = !!k.zertifikatsnummer;
                          const zweitCount = k.zertifikat_zweitausfertigung_count || 0;
                          return (
                          <div
                            key={k.kurs_id || idx}
                            data-testid={`zertifikat-kurs-row-${idx}`}
                            className={`flex flex-col gap-3 p-4 rounded-lg border ${
                              istAusgestellt ? "border-emerald-300 bg-emerald-50/40" : k.druckbar ? "border-yellow-400 bg-yellow-50/40" : "border-stone-200 bg-stone-50/40"
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{k.kurs_name || "(Unbenannter Kurs)"}</div>
                              <div className="text-xs text-stone-500 mt-0.5">
                                {k.start || "—"} bis {k.ende || "—"}
                                {k.vorlage_name ? <> · Vorlage: <span className="font-mono">{k.vorlage_name.split('(')[0].trim()}</span></> : <> · <span className="text-red-600">Keine Vorlage</span></>}
                              </div>
                              <div className="text-xs mt-1">
                                {istAusgestellt ? (
                                  <div className="space-y-0.5">
                                    {k.zertifikat_widerrufen && (
                                      <div className="text-red-700 font-bold bg-red-50 border border-red-300 rounded px-2 py-1 my-1 text-sm">
                                        WIDERRUFEN am {k.zertifikat_widerrufen_am || "—"}
                                        {k.zertifikat_widerrufen_grund && <> · Grund: <em>{k.zertifikat_widerrufen_grund}</em></>}
                                      </div>
                                    )}
                                    <div className="text-emerald-700">Original: <strong>{k.zertifikatsnummer}</strong>{k.zertifikat_original_gedruckt_am ? ` · gedruckt am ${k.zertifikat_original_gedruckt_am}` : k.zertifikat_ausgestellt_am ? ` · ${k.zertifikat_ausgestellt_am}` : ""}{k.zertifikat_original_gedruckt_von ? ` · von ${k.zertifikat_original_gedruckt_von}` : ""}</div>
                                    {k.zertifikat_original_druck_offen && (
                                      <div className="text-sky-700 font-medium" data-testid={`zertifikat-original-offen-${idx}`}>
                                        Digital verschickt{k.zertifikat_digital_am ? ` am ${k.zertifikat_digital_am}` : ""} · Original auf Edelpapier noch drucken
                                      </div>
                                    )}
                                    {zweitCount > 0 && (
                                      <div className="text-amber-700">
                                        {zweitCount} Zweitausfertigung{zweitCount > 1 ? "en" : ""} ausgestellt
                                        {k.zertifikat_letzte_ausstellung_am ? ` · zuletzt am ${k.zertifikat_letzte_ausstellung_am}` : ""}
                                        {k.zertifikat_letzte_ausstellung_von && k.zertifikat_letzte_ausstellung_von !== k.zertifikat_original_gedruckt_von ? ` · von ${k.zertifikat_letzte_ausstellung_von}` : ""}
                                      </div>
                                    )}
                                  </div>
                                ) : k.rezert && k.druckbar ? (
                                  <span className="text-yellow-700" data-testid={`zertifikat-rezi-hinweis-${idx}`}>Rezertifizierung – ohne Prüfung direkt druckbar</span>
                                ) : k.bestanden ? (
                                  <span className="text-yellow-700">Bestanden – bereit zum Original-Druck</span>
                                ) : (
                                  <span className="text-stone-500">Noch nicht bestanden{k.grund ? ` (${k.grund})` : ""}</span>
                                )}
                              </div>
                              {/* Druck-Historie (max. 5 letzte Einträge) */}
                              {Array.isArray(k.zertifikat_druck_history) && k.zertifikat_druck_history.length > 0 && (
                                <details className="mt-2">
                                  <summary className="text-xs text-stone-600 cursor-pointer hover:text-stone-800">Druck-Historie ({k.zertifikat_druck_history.length})</summary>
                                  <ul className="mt-1 ml-3 text-xs text-stone-600 space-y-0.5">
                                    {k.zertifikat_druck_history.slice(-5).reverse().map((h, hi) => (
                                      <li key={hi}>
                                        <span className={h.type === "original" ? "text-emerald-700 font-medium" : h.type === "digital" ? "text-sky-700" : "text-amber-700"}>
                                          {h.type === "original" ? "Original" : h.type === "digital" ? "Digital-PDF" : "Zweitausfertigung"}
                                        </span>
                                        {" · "}{h.gedruckt_am_date || (h.gedruckt_am || "").slice(0,10)}
                                        {" · "}{h.gedruckt_von_name || "?"}
                                      </li>
                                    ))}
                                  </ul>
                                </details>
                              )}
                            </div>
                            <div className="flex gap-2 flex-wrap items-center pt-3 border-t border-stone-200">
                              <div className="flex items-center gap-1.5" data-testid={`zertifikat-rechtsgrundlage-wrap-${idx}`}>
                                <label className="text-xs text-stone-600 whitespace-nowrap">Rechtsgrundlage:</label>
                                <select
                                  data-testid={`select-rechtsgrundlage-${idx}`}
                                  className={`h-8 rounded border px-2 py-1 text-xs bg-white ${getRechtsgrundlage(k) === "hessen" ? "border-amber-500 text-amber-800 font-medium" : "border-stone-300 text-stone-700"}`}
                                  value={getRechtsgrundlage(k)}
                                  onChange={(e) => setKursRechtsgrundlage(k, e.target.value)}
                                  title="Bestimmt den rechtlichen Fußtext auf dem Zertifikat. NRW = Standard (kein Zusatztext). Hessen = HLfGP-Leitlinie + § 5 HGBPAV Hessen."
                                >
                                  <option value="nrw">NRW (Standard)</option>
                                  <option value="hessen">Hessen (HLfGP § 5 HGBPAV)</option>
                                </select>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={!k.zertifikatsnummer}
                                data-testid={`btn-zertifikat-reset-${idx}`}
                                className="border-red-300 text-red-700 hover:bg-red-50"
                                onClick={async () => {
                                  const nr = k.zertifikatsnummer || "—";
                                  const msg = `Zertifikats-Ausstellung ZURÜCKSETZEN?\n\nKurs: ${k.kurs_name}\nNummer: ${nr}\nOriginal-Datum: ${k.zertifikat_original_gedruckt_am || k.zertifikat_ausgestellt_am || "—"}\n\nNach dem Reset:\n• Nummer & Original-Datum werden ENTFERNT\n• Nummer gilt als STORNIERT (protokolliert)\n• Nur das vom System erzeugte PDF dieser Nummer wird archiviert – hochgeladene Scans bleiben unberührt\n• Der Counter wird nur zurückgesetzt, wenn es die zuletzt vergebene Nummer war\n• Beim nächsten Druck wird wieder „Original" (statt „Zweitausfertigung") erstellt\n\nNur verwenden, wenn dieses Zertifikat fälschlich ausgestellt wurde!\n\nFortfahren?`;
                                  if (!window.confirm(msg)) return;
                                  const reason = window.prompt("Begründung (Audit-Trail):", "Fälschlich ausgestellt") || "Fälschlich ausgestellt";
                                  try {
                                    const res = await axios.post(
                                      `${API}/teilnehmer/${teilnehmerId}/zertifikat-reset`,
                                      { kurs_id: k.kurs_id, rollback_counter: true, reason }
                                    );
                                    toast.success(`✓ Reset OK — Nummer ${res.data.removed_zertifikatsnummer || nr} entfernt, ${res.data.removed_anhaenge_count} PDF(s) archiviert${res.data.counter_rolled_back ? ", Counter zurückgesetzt" : ""}`);
                                    await fetchTeilnehmer();
                                    await fetchAnhaenge();
                                    await fetchKurseZertifikatStatus();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || "Reset fehlgeschlagen");
                                  }
                                }}
                              >
                                ↺ Reset
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={!k.vorlage_id || (!istAusgestellt && !k.druckbar)}
                                data-testid={`btn-zertifikat-digital-${idx}`}
                                className="border-sky-400 text-sky-700 hover:bg-sky-50"
                                onClick={() => downloadDigitalZertifikat(k)}
                              >
                                Digital-PDF
                              </Button>
                              {istAusgestellt && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={!k.vorlage_id}
                                  data-testid={`btn-zertifikat-digital-zweit-${idx}`}
                                  className="border-sky-400 text-sky-700 hover:bg-sky-50"
                                  onClick={() => downloadDigitalZertifikat(k, true)}
                                >
                                  Digital-Zweitausfertigung
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={!k.vorlage_id}
                                data-testid={`btn-zertifikat-testdruck-${idx}`}
                                onClick={async () => {
                                  try {
                                    toast.info("Test-Druck wird erstellt...");
                                    // Sauberer Testdruck: nur preview (kein Counter), KEIN debug (keine Hilfslinien/mm-Skala)
                                    const rg = getRechtsgrundlage(k);
                                    const q = `preview=true&rechtsgrundlage=${encodeURIComponent(rg)}${k.kurs_id ? `&kurs_id=${encodeURIComponent(k.kurs_id)}` : ""}`;
                                    const res = await axios.get(`${API}/teilnehmer/${teilnehmerId}/zertifikat-pdf?${q}`, { responseType: "blob" });
                                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                    const a = document.createElement("a"); a.href = url;
                                    a.download = `Zertifikat_TESTDRUCK_${teilnehmer.nachname || 'TN'}_${(k.kurs_name||'').replace(/[^a-zA-Z0-9]/g,'_').slice(0,30)}.pdf`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    toast.success("Test-Druck heruntergeladen (Counter NICHT hochgezählt)");
                                  } catch (err) {
                                    let msg = "Fehler beim Test-Druck";
                                    try { const txt = await err.response?.data?.text?.(); if (txt) { msg = JSON.parse(txt).detail || msg; } } catch (e) {}
                                    toast.error(msg);
                                  }
                                }}
                              >
                                Test-Druck
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`btn-teilnahmebescheinigung-${idx}`}
                                onClick={async () => {
                                  try {
                                    toast.info("Teilnahmebescheinigung wird erstellt...");
                                    const params = new URLSearchParams();
                                    if (k.kurs_id) params.set("kurs_id", k.kurs_id);
                                    if (k.kurs_name) params.set("kurs_name", k.kurs_name);
                                    const q = params.toString() ? `?${params.toString()}` : "";
                                    const res = await axios.get(`${API}/teilnehmer/${teilnehmerId}/teilnahmebescheinigung-pdf${q}`, { responseType: "blob" });
                                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                    const a = document.createElement("a"); a.href = url;
                                    a.download = `Teilnahmebescheinigung_${teilnehmer.nachname || 'TN'}_${(k.kurs_name||'').replace(/[^a-zA-Z0-9]/g,'_').slice(0,30)}.pdf`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    toast.success("Teilnahmebescheinigung heruntergeladen");
                                  } catch (err) {
                                    let msg = "Fehler beim Erstellen der Teilnahmebescheinigung";
                                    try { const txt = await err.response?.data?.text?.(); if (txt) { msg = JSON.parse(txt).detail || msg; } } catch (e) {}
                                    toast.error(msg);
                                  }
                                }}
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                title="Detaillierte AZAV-Teilnahmebescheinigung (FB_TN_06) mit Modul-Inhalten und Anwesenheitsquote — auch ohne Prüfungsbestehen ausstellbar"
                              >
                                Teilnahme
                              </Button>
                              <NotionBestaetigungButton teilnehmerId={teilnehmerId} nachname={teilnehmer.nachname} kurs={k} idx={idx} />
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`btn-modulubersicht-${idx}`}
                                onClick={async () => {
                                  try {
                                    toast.info("Modulübersicht wird erstellt...");
                                    const params = new URLSearchParams();
                                    if (k.kurs_id) params.set("kurs_id", k.kurs_id);
                                    if (k.kurs_name) params.set("kurs_name", k.kurs_name);
                                    const q = params.toString() ? `?${params.toString()}` : "";
                                    const res = await axios.get(`${API}/teilnehmer/${teilnehmerId}/modulubersicht-pdf${q}`, { responseType: "blob" });
                                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                    const a = document.createElement("a"); a.href = url;
                                    a.download = `Modulübersicht_${teilnehmer.nachname || 'TN'}_${(k.kurs_name||'').replace(/[^a-zA-Z0-9]/g,'_').slice(0,30)}.pdf`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    toast.success("Modulübersicht heruntergeladen");
                                  } catch (err) {
                                    let msg = "Fehler beim Erstellen der Modulübersicht";
                                    try { const txt = await err.response?.data?.text?.(); if (txt) { msg = JSON.parse(txt).detail || msg; } } catch (e) {}
                                    toast.error(msg);
                                  }
                                }}
                                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                                title="Kompakte Modulübersicht / Transcript — Beiblatt zum Zertifikat, nur Modul-Inhalte mit Stundenumfang, ohne rechtlichen Rahmentext"
                              >
                                Modulübersicht
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={!k.vorlage_id}
                                data-testid={`btn-zertifikat-nachdruck-${idx}`}
                                onClick={() => {
                                  // Modal mit Original-Daten vorbefüllen (falls vorhanden)
                                  setNachdruckKurs(k);
                                  setNachdruckNummer(k.zertifikatsnummer || "");
                                  // Rechtsgrundlage: falls beim Original gespeichert → vorbefüllen, sonst pro-Kurs-Auswahl übernehmen
                                  setNachdruckRechtsgrundlage(k.zertifikat_rechtsgrundlage || getRechtsgrundlage(k) || "nrw");
                                  // Original-Ausstellungsdatum als YYYY-MM-DD vorbefüllen
                                  let orig = k.zertifikat_original_gedruckt_am || k.zertifikat_ausgestellt_am || "";
                                  if (orig && orig.includes(".")) {
                                    // DD.MM.YYYY → YYYY-MM-DD
                                    const parts = orig.split(".");
                                    if (parts.length === 3) orig = `${parts[2]}-${parts[1].padStart(2,"0")}-${parts[0].padStart(2,"0")}`;
                                  } else if (orig && orig.length >= 10) {
                                    orig = orig.slice(0, 10);
                                  }
                                  setNachdruckDatum(orig);
                                  setShowNachdruckModal(true);
                                }}
                                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                              >
                                Nachdruck
                              </Button>
                              <Button
                                size="sm"
                                disabled={!k.druckbar}
                                className={k.druckbar ? (istAusgestellt ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-yellow-500 hover:bg-yellow-600 text-white") : ""}
                                data-testid={`btn-zertifikat-drucken-${idx}`}
                                onClick={async () => {
                                  const vorlageName = k.vorlage_name || "Zertifikat";
                                  const confirmMsg = k.zertifikat_original_druck_offen
                                    ? `ORIGINAL auf Edelpapier drucken?\n\nDas Zertifikat wurde bereits digital verschickt.\n• Gleiche Nummer ${k.zertifikatsnummer}\n• Gleiches Ausstellungsdatum ${k.zertifikat_ausgestellt_am || ""}\n• Kein Design-Hintergrund – nur Text für das Edelpapier\n\nBitte Edelpapier einlegen.`
                                    : istAusgestellt
                                    ? `ACHTUNG: Original wurde bereits ausgestellt!\n\nNummer: ${k.zertifikatsnummer}\nOriginal-Datum: ${k.zertifikat_original_gedruckt_am || k.zertifikat_ausgestellt_am || "—"}\nGedruckt von: ${k.zertifikat_original_gedruckt_von || "—"}\nBisherige Zweitausfertigungen: ${zweitCount}\n\nEine ZWEITAUSFERTIGUNG wird gedruckt:\n• Gleiche Nummer ${k.zertifikatsnummer}\n• Heutiges Datum als Ausstellungstag der Kopie\n• PDF wird zusätzlich mit „ZWEITAUSFERTIGUNG" markiert\n• Audit: Ihr Name wird hinterlegt\n\nFortfahren?`
                                    : `ORIGINAL-Zertifikat „${vorlageName}" für Kurs „${k.kurs_name}" jetzt ausstellen?\n\n• Neue fortlaufende Nummer wird vergeben\n• Original-Datum wird festgeschrieben\n• PDF wird als Anhang abgelegt\n• Audit: Ihr Name wird hinterlegt\n\nEin Original kann nur EINMAL gedruckt werden. Spätere Drucke werden automatisch zur „Zweitausfertigung" mit Heutigem Datum.\n\nBitte passendes Goldpapier einlegen.`;
                                  if (!window.confirm(confirmMsg)) return;
                                  try {
                                    toast.info(istAusgestellt ? "Zweitausfertigung wird erstellt..." : "Original-Zertifikat wird generiert...");
                                    const rg = getRechtsgrundlage(k);
                                    const params = new URLSearchParams();
                                    params.set("rechtsgrundlage", rg);
                                    params.set("original", "true");
                                    if (k.kurs_id) params.set("kurs_id", k.kurs_id);
                                    const q = `?${params.toString()}`;
                                    const res = await axios.get(`${API}/teilnehmer/${teilnehmerId}/zertifikat-pdf${q}`, { responseType: "blob" });
                                    const zertNr = res.headers?.['x-zertifikats-nr'] || '';
                                    const istZweit = res.headers?.['x-zweitausfertigung'] === "1";
                                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                    const a = document.createElement("a"); a.href = url;
                                    a.download = `Zertifikat_${teilnehmer.nachname || 'TN'}_${zertNr.replace('/', '-')}${istZweit ? '_Zweitausfertigung' : ''}.pdf`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    toast.success(`${istZweit ? "Zweitausfertigung" : "Original"} ${zertNr} ausgestellt & gespeichert`);
                                    await fetchTeilnehmer();
                                    await fetchAnhaenge();
                                    await fetchKurseZertifikatStatus();
                                  } catch (err) {
                                    let msg = "Fehler bei Zertifikat-Generierung";
                                    try { const txt = await err.response?.data?.text?.(); if (txt) { msg = JSON.parse(txt).detail || msg; } } catch (e) {}
                                    toast.error(msg);
                                  }
                                }}
                              >
                                {istAusgestellt ? "Zweitausfertigung" : "Original drucken"}
                              </Button>
                              {istAusgestellt && (
                                k.zertifikat_widerrufen ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    data-testid={`btn-zertifikat-widerruf-aufheben-${idx}`}
                                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                    title="Widerruf aufheben (Zertifikat wird wieder als gültig markiert)"
                                    onClick={async () => {
                                      if (!window.confirm(`Widerruf von Zertifikat ${k.zertifikatsnummer} aufheben? Die Verifikations-Seite zeigt das Zertifikat danach wieder als GÜLTIG an.`)) return;
                                      try {
                                        await axios.post(`${API}/teilnehmer/${teilnehmerId}/zertifikat/widerruf-aufheben?kurs_id=${encodeURIComponent(k.kurs_id)}`);
                                        toast.success("Widerruf aufgehoben — Zertifikat wieder gültig");
                                        await fetchKurseZertifikatStatus();
                                      } catch (err) {
                                        toast.error("Fehler beim Aufheben");
                                      }
                                    }}
                                  >
                                    ↻ Widerruf aufheben
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    data-testid={`btn-zertifikat-widerruf-${idx}`}
                                    className="border-red-300 text-red-700 hover:bg-red-50"
                                    title="Zertifikat als WIDERRUFEN markieren (z.B. bei nachträglich bekanntem Prüfungsbetrug). QR-Code-Verify zeigt dann ROT 'WIDERRUFEN'."
                                    onClick={async () => {
                                      const grund = window.prompt(
                                        `ZERTIFIKAT WIDERRUFEN\n\nNummer: ${k.zertifikatsnummer}\nKurs: ${k.kurs_name}\n\nBitte geben Sie den Widerrufsgrund an (wird auf Verify-Seite öffentlich angezeigt — DSGVO beachten!):`,
                                        ""
                                      );
                                      if (grund === null) return; // Abgebrochen
                                      try {
                                        await axios.post(`${API}/teilnehmer/${teilnehmerId}/zertifikat/widerruf?kurs_id=${encodeURIComponent(k.kurs_id)}&grund=${encodeURIComponent(grund || "Kein Grund angegeben")}`);
                                        toast.success(`Zertifikat ${k.zertifikatsnummer} wurde widerrufen — QR-Code-Verify zeigt jetzt ROT.`);
                                        await fetchKurseZertifikatStatus();
                                      } catch (err) {
                                        toast.error("Fehler beim Widerruf");
                                      }
                                    }}
                                  >
                                    Widerrufen
                                  </Button>
                                )
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Erstgespräch-Daten (für Checkliste & Vertrag)</CardTitle>
                      <Button size="sm" onClick={saveErstgespraech} disabled={saving} data-testid="btn-erstgespraech-save">
                        <Save size={14} className="mr-1" /> {saving ? "..." : "Speichern"}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Datum Erstgespräch</Label>
                        <Input 
                          type="date" 
                          value={erstgespraechForm.erstgespraech_datum}
                          onChange={(e) => setErstgespraechForm({...erstgespraechForm, erstgespraech_datum: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Gesprächsführer</Label>
                        <Input 
                          value={erstgespraechForm.erstgespraech_gefuehrt_von}
                          onChange={(e) => setErstgespraechForm({...erstgespraechForm, erstgespraech_gefuehrt_von: e.target.value})}
                          placeholder="z.B. Jean Dickie"
                        />
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="font-medium text-sm mb-3">Deutschkenntnisse (bei Nicht-Muttersprachlern)</h4>
                      <div className="grid grid-cols-3 gap-2">
                        <CheckboxField label="Erfüllt" checked={erstgespraechForm.deutschkenntnisse_erfuellt} 
                          onChange={(v) => setErstgespraechForm({...erstgespraechForm, deutschkenntnisse_erfuellt: v})} />
                        <CheckboxField label="Nachweis nötig" checked={erstgespraechForm.deutschkenntnisse_nachweis_noetig}
                          onChange={(v) => setErstgespraechForm({...erstgespraechForm, deutschkenntnisse_nachweis_noetig: v})} />
                        <CheckboxField label="Nachweis gesehen" checked={erstgespraechForm.deutschkenntnisse_nachweis_gesehen}
                          onChange={(v) => setErstgespraechForm({...erstgespraechForm, deutschkenntnisse_nachweis_gesehen: v})} />
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="font-medium text-sm mb-3">Polizeiliches Führungszeugnis</h4>
                      <div className="grid grid-cols-3 gap-2">
                        <CheckboxField label="Erfüllt" checked={erstgespraechForm.fuehrungszeugnis_erfuellt}
                          onChange={(v) => setErstgespraechForm({...erstgespraechForm, fuehrungszeugnis_erfuellt: v})} />
                        <CheckboxField label="Nachweis nötig" checked={erstgespraechForm.fuehrungszeugnis_nachweis_noetig}
                          onChange={(v) => setErstgespraechForm({...erstgespraechForm, fuehrungszeugnis_nachweis_noetig: v})} />
                        <CheckboxField label="Nachweis gesehen" checked={erstgespraechForm.fuehrungszeugnis_nachweis_gesehen}
                          onChange={(v) => setErstgespraechForm({...erstgespraechForm, fuehrungszeugnis_nachweis_gesehen: v})} />
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="font-medium text-sm mb-3">Technische Ausstattung</h4>
                      <div className="grid grid-cols-3 gap-2">
                        <CheckboxField label="Laptop/PC vorhanden" checked={erstgespraechForm.technik_laptop_vorhanden}
                          onChange={(v) => setErstgespraechForm({...erstgespraechForm, technik_laptop_vorhanden: v})} />
                        <CheckboxField label="Internet vorhanden" checked={erstgespraechForm.technik_internet_vorhanden}
                          onChange={(v) => setErstgespraechForm({...erstgespraechForm, technik_internet_vorhanden: v})} />
                        <CheckboxField label="Arbeitsplatz vorhanden" checked={erstgespraechForm.technik_arbeitsplatz_vorhanden}
                          onChange={(v) => setErstgespraechForm({...erstgespraechForm, technik_arbeitsplatz_vorhanden: v})} />
                      </div>
                    </div>

                    <div className="border-t pt-4 grid grid-cols-2 gap-4">
                      <CheckboxField label="Für Maßnahme geeignet" checked={erstgespraechForm.fuer_massnahme_geeignet}
                        onChange={(v) => setErstgespraechForm({...erstgespraechForm, fuer_massnahme_geeignet: v})} />
                      <CheckboxField label="Vertrag unterschrieben" checked={erstgespraechForm.vertrag}
                        onChange={(v) => setErstgespraechForm({...erstgespraechForm, vertrag: v})} />
                    </div>
                  </CardContent>
                </Card>

                <NichtantrittKarte teilnehmer={teilnehmer} onChanged={() => { fetchTeilnehmer(true); if (onUpdate) onUpdate(); }} />

                {/* BG-Austrittsmeldung (nur bei Bildungsgutschein) */}
                {teilnehmer.finanzierungsart === 'bildungsgutschein' && (
                  <AustrittsmeldungCard
                    teilnehmerId={teilnehmerId}
                    teilnehmer={teilnehmer}
                  />
                )}

                {/* Kursabbruch für SZ / Selbstzahler (kein Austrittsformular an BA nötig) */}
                {teilnehmer.finanzierungsart !== 'bildungsgutschein' && teilnehmer.status !== 'abgebrochen' && (
                  <Card data-testid="sz-abbruch-card" className="border-red-200 bg-red-50/40">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2 text-red-800">
                        <AlertTriangle size={18} /> Kursabbruch markieren
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-stone-600 mb-3">
                        Setzt den Status auf &bdquo;abgebrochen&ldquo; f&uuml;r auditsichere Dokumentation. Kein PDF, kein Formular an die Agentur f&uuml;r Arbeit n&ouml;tig (kein Bildungsgutschein).
                      </p>
                      <Button
                        data-testid="btn-sz-kursabbruch-markieren"
                        variant="outline"
                        className="border-red-500 text-red-700 hover:bg-red-100"
                        onClick={async () => {
                          if (!confirm(`Kursabbruch für ${teilnehmer?.vorname || ""} ${teilnehmer?.nachname || ""} markieren?\n\nDies setzt den Status auf "abgebrochen".`)) return;
                          try {
                            const r = await axios.post(`${API}/teilnehmer/${teilnehmerId}/kursabbruch-markieren`, {});
                            toast.success(`${r.data.name} als Abbruch markiert (${r.data.abbruch_datum})`);
                            setTimeout(() => window.location.reload(), 800);
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Fehler beim Markieren");
                          }
                        }}
                      >
                        <AlertTriangle size={16} className="mr-2" /> Als Abbruch markieren
                      </Button>
                    </CardContent>
                  </Card>
                )}
                {teilnehmer.finanzierungsart !== 'bildungsgutschein' && teilnehmer.status === 'abgebrochen' && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-red-300 bg-red-50 text-red-800 text-xs font-medium" data-testid="sz-badge-abgebrochen">
                    <AlertTriangle size={13} /> Status: Kursabbruch{teilnehmer?.abbruch_datum ? ` (${teilnehmer.abbruch_datum})` : ""}
                  </div>
                )}
              </TabsContent>

              {/* Start-E-Mail Tab */}
              <TabsContent value="email" className="px-6 py-4 space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Send size={18} className="text-gray-500" /> Start-E-Mail versenden
                      </CardTitle>
                      {teilnehmer.start_email_gesendet && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle size={12} className="mr-1" /> Gesendet am {formatDate(teilnehmer.start_email_datum)}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!teilnehmer.email ? (
                      <div className="p-4 bg-amber-50 rounded-lg text-amber-700 flex items-center gap-2">
                        <Mail size={18} />
                        <span>Keine E-Mail-Adresse hinterlegt. Bitte zuerst E-Mail in Stammdaten ergänzen.</span>
                      </div>
                    ) : (
                      <>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Mail size={16} className="text-gray-400" />
                            <span className="font-medium">{teilnehmer.email}</span>
                          </div>
                          <div className="text-sm text-gray-500">
                            Vorlage: {teilnehmer.finanzierungsart === "bildungsgutschein" ? "Bildungsgutschein" : "Selbstzahler"}
                          </div>
                          {teilnehmer.kurs && (
                            <div className="text-sm text-gray-500">Kurs: {teilnehmer.kurs}</div>
                          )}
                          {teilnehmer.kurs_start && (
                            <div className="text-sm text-gray-500">Start: {formatDate(teilnehmer.kurs_start)}</div>
                          )}
                        </div>

                        <div>
                          <Label>Betreff</Label>
                          <Input 
                            value={teilnehmer.finanzierungsart === "bildungsgutschein" 
                              ? EMAIL_VORLAGEN.bildungsgutschein.betreff(teilnehmer.kurs) 
                              : EMAIL_VORLAGEN.selbstzahler.betreff}
                            readOnly
                            className="bg-gray-50"
                          />
                        </div>

                        <div>
                          <Label>E-Mail Text</Label>
                          <Textarea 
                            value={teilnehmer.finanzierungsart === "bildungsgutschein"
                              ? EMAIL_VORLAGEN.bildungsgutschein.text(teilnehmer.vorname, teilnehmer.nachname, teilnehmer.kurs, formatDate(teilnehmer.kurs_start))
                              : EMAIL_VORLAGEN.selbstzahler.text(teilnehmer.vorname, teilnehmer.nachname, teilnehmer.kurs, formatDate(teilnehmer.kurs_start))}
                            readOnly
                            rows={15}
                            className="bg-gray-50 font-mono text-sm"
                          />
                        </div>

                        <div className="flex gap-2 pt-2">
                          <Button 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => {
                              const text = teilnehmer.finanzierungsart === "bildungsgutschein"
                                ? EMAIL_VORLAGEN.bildungsgutschein.text(teilnehmer.vorname, teilnehmer.nachname, teilnehmer.kurs, formatDate(teilnehmer.kurs_start))
                                : EMAIL_VORLAGEN.selbstzahler.text(teilnehmer.vorname, teilnehmer.nachname, teilnehmer.kurs, formatDate(teilnehmer.kurs_start));
                              navigator.clipboard.writeText(text);
                              toast.success("E-Mail-Text in Zwischenablage kopiert");
                            }}
                          >
                            <Copy size={16} className="mr-2" /> Text kopieren
                          </Button>
                          <Button 
                            className="flex-1"
                            onClick={() => {
                              const betreff = teilnehmer.finanzierungsart === "bildungsgutschein"
                                ? EMAIL_VORLAGEN.bildungsgutschein.betreff(teilnehmer.kurs)
                                : EMAIL_VORLAGEN.selbstzahler.betreff;
                              const text = teilnehmer.finanzierungsart === "bildungsgutschein"
                                ? EMAIL_VORLAGEN.bildungsgutschein.text(teilnehmer.vorname, teilnehmer.nachname, teilnehmer.kurs, formatDate(teilnehmer.kurs_start))
                                : EMAIL_VORLAGEN.selbstzahler.text(teilnehmer.vorname, teilnehmer.nachname, teilnehmer.kurs, formatDate(teilnehmer.kurs_start));
                              
                              const mailtoLink = `mailto:${teilnehmer.email}?subject=${encodeURIComponent(betreff)}&body=${encodeURIComponent(text)}`;
                              window.open(mailtoLink, '_blank');
                              
                              // Markiere als gesendet
                              axios.put(`${API}/teilnehmer/${teilnehmerId}`, {
                                ...teilnehmer,
                                start_email_gesendet: true,
                                start_email_datum: new Date().toISOString().split('T')[0]
                              }).then(() => {
                                fetchTeilnehmer();
                                toast.success("E-Mail-Programm geöffnet");
                              });
                            }}
                          >
                            <ExternalLink size={16} className="mr-2" /> In E-Mail-Programm öffnen
                          </Button>
                        </div>

                        {teilnehmer.finanzierungsart === "bildungsgutschein" && !teilnehmer.kurs && (
                          <div className="p-3 bg-amber-50 rounded-lg text-amber-700 text-sm">
                            <strong>Hinweis:</strong> Bitte weisen Sie dem Teilnehmer einen Kurs zu, damit der Kursname und das Startdatum automatisch in der E-Mail erscheinen.
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Prüfung Tab */}
              <TabsContent value="pruefung" className="px-6 py-4 space-y-4">
                <ZertifikatFreigabe teilnehmerId={teilnehmerId} anhaengeAnzahl={anhaenge.length} onDokumente={() => setActiveTab("anhaenge")} />
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <ClipboardCheck size={18} className="text-gray-500" /> Prüfungsergebnisse
                      </CardTitle>
                      {!editPruefung ? (
                        <Button variant="outline" size="sm" data-testid="btn-pruefung-auf-pruefungsseite"
                          onClick={() => { window.location.href = `/pruefungen?tn=${teilnehmerId}`; }}>
                          Auf der Prüfungen-Seite bearbeiten
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => {
                            setEditPruefung(false);
                            setPruefungForm({
                              pruefung_am: teilnehmer.pruefung_am || "",
                              pruefung_2_am: teilnehmer.pruefung_2_am || "",
                              punkte_schriftlich: teilnehmer.punkte_schriftlich || "",
                              punkte_muendlich: teilnehmer.punkte_muendlich || "",
                              bestanden: teilnehmer.bestanden || false
                            });
                          }}>
                            Abbrechen
                          </Button>
                          <Button size="sm" onClick={savePruefung} disabled={saving}>
                            <Save size={14} className="mr-1" /> {saving ? "..." : "Speichern"}
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {editPruefung ? (
                      <div className="space-y-6">
                        {/* ============== ERSTPRÜFUNG ============== */}
                        <div className="border border-stone-200 rounded-lg p-4 bg-stone-50/50">
                          <h4 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">1</span>
                            Erstprüfung
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Prüfungsdatum</Label>
                              <Input 
                                type="date" 
                                value={pruefungForm.pruefung_am || ""}
                                onChange={(e) => setPruefungForm({...pruefungForm, pruefung_am: e.target.value})}
                                data-testid="input-pruefung-am"
                              />
                            </div>
                            <div>
                              <Label>Zertifikatsnummer</Label>
                              <Input 
                                value={pruefungForm.zertifikatsnummer || ""}
                                onChange={(e) => setPruefungForm({...pruefungForm, zertifikatsnummer: e.target.value})}
                                placeholder="Z-2026-001"
                                data-testid="input-zertifikatsnummer"
                              />
                            </div>
                            <div>
                              <Label>Punkte schriftlich</Label>
                              <Input 
                                type="number" 
                                value={pruefungForm.punkte_schriftlich || ""}
                                onChange={(e) => setPruefungForm({...pruefungForm, punkte_schriftlich: e.target.value})}
                                placeholder="0-100"
                                data-testid="input-punkte-schriftlich"
                              />
                            </div>
                            <div>
                              <Label>Punkte mündlich</Label>
                              <Input 
                                type="number" 
                                value={pruefungForm.punkte_muendlich || ""}
                                onChange={(e) => setPruefungForm({...pruefungForm, punkte_muendlich: e.target.value})}
                                placeholder="0-100"
                                data-testid="input-punkte-muendlich"
                              />
                            </div>
                            <div>
                              <Label>Punkte Facharbeit</Label>
                              <Input 
                                type="number" 
                                value={pruefungForm.punkte_facharbeit || ""}
                                onChange={(e) => setPruefungForm({...pruefungForm, punkte_facharbeit: e.target.value})}
                                placeholder="0-100"
                                data-testid="input-punkte-facharbeit"
                              />
                            </div>
                            <div>
                              <Label>Gesamtpunkte</Label>
                              <Input 
                                type="number" 
                                value={pruefungForm.gesamtpunkte || ""}
                                onChange={(e) => setPruefungForm({...pruefungForm, gesamtpunkte: e.target.value})}
                                placeholder="Gesamtpunktzahl"
                                data-testid="input-gesamtpunkte"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-3">
                            <input 
                              type="checkbox" 
                              id="bestanden" 
                              checked={pruefungForm.bestanden}
                              onChange={(e) => setPruefungForm({...pruefungForm, bestanden: e.target.checked})}
                              className="w-4 h-4"
                              data-testid="checkbox-bestanden"
                            />
                            <Label htmlFor="bestanden">Erstprüfung bestanden</Label>
                          </div>
                        </div>

                        {/* ============== ZWEITPRÜFUNG (Wiederholungsprüfung) ============== */}
                        <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/40">
                          <h4 className="font-semibold text-stone-800 mb-1 flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs font-bold">2</span>
                            Zweitprüfung (Wiederholungsprüfung)
                          </h4>
                          <p className="text-xs text-stone-500 mb-3">Nur ausfüllen, wenn Erstprüfung nicht bestanden — gem. VA_03 Phase 4</p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>2. Prüfungstermin</Label>
                              <Input 
                                type="date" 
                                value={pruefungForm.pruefung_2_am || ""}
                                onChange={(e) => setPruefungForm({...pruefungForm, pruefung_2_am: e.target.value})}
                                data-testid="input-pruefung-2-am"
                              />
                            </div>
                            <div>
                              <Label>Gesamtpunkte (Zweit)</Label>
                              <Input 
                                type="number" 
                                value={pruefungForm.gesamtpunkte_2 || ""}
                                onChange={(e) => setPruefungForm({...pruefungForm, gesamtpunkte_2: e.target.value})}
                                placeholder="Gesamtpunktzahl 2. Versuch"
                                data-testid="input-gesamtpunkte-2"
                              />
                            </div>
                            <div>
                              <Label>Punkte schriftlich (Zweit)</Label>
                              <Input 
                                type="number" 
                                value={pruefungForm.punkte_2_schriftlich || ""}
                                onChange={(e) => setPruefungForm({...pruefungForm, punkte_2_schriftlich: e.target.value})}
                                placeholder="0-100"
                                data-testid="input-punkte-2-schriftlich"
                              />
                            </div>
                            <div>
                              <Label>Punkte mündlich (Zweit)</Label>
                              <Input 
                                type="number" 
                                value={pruefungForm.punkte_2_muendlich || ""}
                                onChange={(e) => setPruefungForm({...pruefungForm, punkte_2_muendlich: e.target.value})}
                                placeholder="0-100"
                                data-testid="input-punkte-2-muendlich"
                              />
                            </div>
                            <div>
                              <Label>Punkte Facharbeit (Zweit)</Label>
                              <Input 
                                type="number" 
                                value={pruefungForm.punkte_2_facharbeit || ""}
                                onChange={(e) => setPruefungForm({...pruefungForm, punkte_2_facharbeit: e.target.value})}
                                placeholder="0-100"
                                data-testid="input-punkte-2-facharbeit"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-3">
                            <input 
                              type="checkbox" 
                              id="bestanden_2" 
                              checked={pruefungForm.bestanden_2}
                              onChange={(e) => setPruefungForm({...pruefungForm, bestanden_2: e.target.checked})}
                              className="w-4 h-4"
                              data-testid="checkbox-bestanden-2"
                            />
                            <Label htmlFor="bestanden_2">Zweitprüfung bestanden</Label>
                          </div>
                        </div>
                      </div>
                    ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Prüfungsdatum</p>
                        <p className="font-medium">{formatDate(teilnehmer.pruefung_am)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">2. Prüfungstermin</p>
                        <p className="font-medium">{formatDate(teilnehmer.pruefung_2_am)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Punkte schriftlich</p>
                        <p className="font-medium text-lg">{teilnehmer.punkte_schriftlich ?? "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Punkte mündlich</p>
                        <p className="font-medium text-lg">{teilnehmer.punkte_muendlich ?? "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Punkte Facharbeit</p>
                        <p className="font-medium text-lg">{teilnehmer.punkte_facharbeit ?? "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Gesamtpunkte</p>
                        <p className="font-medium text-lg">{teilnehmer.gesamtpunkte ?? "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Zertifikatsnummer</p>
                        <p className="font-medium">{teilnehmer.zertifikatsnummer || "-"}</p>
                      </div>
                    </div>
                    )}
                    <div className="mt-4 p-4 rounded-lg flex items-center justify-center gap-3"
                      style={{ backgroundColor: teilnehmer.bestanden ? '#d1fae5' : '#fef3c7' }}>
                      {teilnehmer.bestanden ? (
                        <>
                          <CheckCircle size={24} className="text-emerald-600" />
                          <span className="font-semibold text-emerald-700">Bestanden</span>
                        </>
                      ) : (
                        <>
                          <XCircle size={24} className="text-amber-600" />
                          <span className="font-semibold text-amber-700">
                            {teilnehmer.pruefung_am ? "Nicht bestanden" : "Prüfung ausstehend"}
                          </span>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Award size={18} className="text-gray-500" /> Zertifikat
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Zertifikat</p>
                        <p className="font-medium">{teilnehmer.zertifikat || "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Teilnahmebescheinigung</p>
                        <p className="font-medium">{teilnehmer.teilnahmebescheinigung || "-"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Befragung Tab (nur für BG) - Vereinfacht wegen Anonymität */}
              <TabsContent value="befragung" className="px-6 py-4 space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">
                      Teilnehmerbefragung (AZAV)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800">
                        <strong>Hinweis:</strong> Die Teilnehmerbefragung ist anonym und wird separat verschickt. 
                        Hier können Sie nur dokumentieren, dass die Befragung versendet wurde.
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-4 border rounded-lg">
                      <input
                        type="checkbox"
                        id="befragung_verschickt"
                        checked={teilnehmer?.befragung_verschickt || false}
                        onChange={async (e) => {
                          const newValue = e.target.checked;
                          try {
                            await axios.put(`${API}/teilnehmer/${teilnehmerId}`, {
                              befragung_verschickt: newValue,
                              befragung_verschickt_am: newValue ? new Date().toISOString().split('T')[0] : null
                            });
                            setTeilnehmer(prev => ({
                              ...prev,
                              befragung_verschickt: newValue,
                              befragung_verschickt_am: newValue ? new Date().toISOString().split('T')[0] : null
                            }));
                            toast.success(newValue ? "Befragung als verschickt markiert" : "Markierung entfernt");
                          } catch (err) {
                            console.error("Fehler:", err);
                            toast.error("Fehler beim Speichern");
                          }
                        }}
                        className="w-5 h-5 rounded border-gray-300 cursor-pointer"
                      />
                      <div>
                        <Label htmlFor="befragung_verschickt" className="text-base font-medium cursor-pointer">
                          Teilnehmerbefragung verschickt
                        </Label>
                        {teilnehmer.befragung_verschickt_am && (
                          <p className="text-sm text-gray-500">
                            Verschickt am: {teilnehmer.befragung_verschickt_am}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Anhänge Tab */}
              <TabsContent value="anhaenge" className="px-6 py-4 space-y-5">
                <div className="flex flex-wrap items-center gap-2" data-testid="dok-aktionen">
                  <Button onClick={openSignDialog} disabled={!teilnehmer?.email} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-open-sign-dialog">
                    <Pen size={15} className="mr-2" /> Zur Unterschrift senden
                  </Button>
                  <Button variant="outline" onClick={klassenbuchPdfSpeichern} disabled={!teilnehmer.kurs_start} data-testid="btn-klassenbuch-pdf">
                    <BookOpen size={15} className="mr-2" /> Klassenbuch als PDF ablegen
                  </Button>
                  <Button variant="outline" onClick={anwesenheitPdfSpeichern} data-testid="btn-anwesenheit-pdf">
                    <Calendar size={15} className="mr-2" /> Anwesenheitsliste als PDF ablegen
                  </Button>
                  {!teilnehmer?.email && (
                    <span className="text-xs text-red-600">Für Online-Unterschrift zuerst eine E-Mail-Adresse hinterlegen.</span>
                  )}
                </div>
                {anhaengeFehler && (
                  <div className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-center justify-between" data-testid="anhaenge-ladefehler">
                    Dokumente konnten nicht geladen werden.
                    <button onClick={fetchAnhaenge} className="underline" data-testid="anhaenge-neu-laden">Erneut laden</button>
                  </div>
                )}
                <DokumentKacheln
                  ladefehler={anhaengeFehler}
                  anhaenge={anhaenge}
                  teilnehmer={teilnehmer}
                  regel={regel}
                  uploadingCode={uploadingCode}
                  onUpload={uploadDatei}
                  onOpen={handleOpenDocument}
                  onDownload={handleDownload}
                  onDelete={handleDeleteAnhang}
                />
                <DokumentSpuren key={`spuren-${anhaenge.length}`} teilnehmerId={teilnehmerId} onRestored={fetchAnhaenge} />
              </TabsContent>

              <TabsContent value="verlauf" className="px-6 py-4">
                {activeTab === "verlauf" && <TeilnehmerVerlauf teilnehmerId={teilnehmerId} />}
              </TabsContent>

              {/* Praktikum Tab */}
              {regel.praktikum && (
                <TabsContent value="praktikum" className="px-6 py-4 space-y-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <ClipboardCheck size={18} className="text-purple-500" /> Praktikums-Dokumente
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-gray-600">
                        AZAV-konforme Praktikumsdokumente — bestehen aus 3 Formblättern,
                        die der <b>Praktikumsbetrieb</b> ausfüllt/unterschreibt:
                        FB_TN_07 (Vertrag), FB_TN_08 (Laufzettel), FB_TN_09 (Bewertung).
                      </p>

                      {/* === Gemeinsame Praktikums-Daten === */}
                      <div className="border rounded-lg p-4 space-y-3 bg-stone-50 border-stone-200">
                        <h4 className="font-medium text-sm text-stone-900">Praktikums-Daten</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-500">Praktikumsbetrieb / Station</label>
                            <input 
                              type="text" 
                              id="laufzettel-betrieb"
                              defaultValue={teilnehmer?.praktikumsbetrieb || ""}
                              className="w-full border rounded px-2 py-1 text-sm mt-1"
                              placeholder="z.B. Seniorenheim Sonnenschein"
                              data-testid="laufzettel-betrieb"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Praktikumszeit</label>
                            <input 
                              type="text" 
                              id="laufzettel-zeit"
                              defaultValue={teilnehmer?.praktikumszeit || ""}
                              className="w-full border rounded px-2 py-1 text-sm mt-1"
                              placeholder="z.B. 01.06.2026 - 31.08.2026"
                              data-testid="laufzettel-zeit"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Name der Praxisanleitung</label>
                            <input 
                              type="text" 
                              id="laufzettel-anleitung"
                              defaultValue={teilnehmer?.praxisanleitung || ""}
                              className="w-full border rounded px-2 py-1 text-sm mt-1"
                              placeholder="z.B. Petra Schmidt"
                              data-testid="laufzettel-anleitung"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Dozent/GF Kürzel</label>
                            <select id="laufzettel-kuerzel" className="w-full border rounded px-2 py-1 text-sm mt-1" data-testid="laufzettel-kuerzel">
                              <option value="MD">MD - Markus Dickie (bis 11.10.2026)</option>
                              <option value="JM">JM - Julia Müller</option>
                              <option value="AL">AL - Annika Lorke</option>
                            </select>
                            <label className="flex items-center gap-2 text-xs text-gray-600 mt-2">
                              <input type="checkbox" id="laufzettel-ohne-freigabe" data-testid="laufzettel-ohne-freigabe" />
                              Laufzettel ohne GF-Freigabe-Zeile drucken
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* === NEU: Komplettes Paket an TN senden === */}
                      <div className="border-2 rounded-lg p-4 space-y-3 bg-emerald-50/60 border-emerald-300">
                        <div className="flex items-start gap-3">
                          <PackageCheck size={22} className="text-emerald-600 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm text-emerald-900">Komplettes Praktikums-Paket per E-Mail senden</h4>
                            <p className="text-xs text-stone-600 mt-1">
                              Sendet alle 3 PDFs (Vertrag + Laufzettel + Bewertung) gebündelt an den Teilnehmer (<b>{teilnehmer?.email || "—"}</b>).
                              Der TN leitet diese an den Praktikumsbetrieb weiter — Betrieb füllt aus, unterschreibt,
                              schickt die Originale an uns zurück.
                            </p>
                          </div>
                        </div>

                        {teilnehmer?.praktikum_paket_sent_at && (
                          <div className="text-xs bg-emerald-100 border border-emerald-200 rounded px-3 py-2 text-emerald-800">
                            <b>✓ Zuletzt gesendet:</b> {new Date(teilnehmer.praktikum_paket_sent_at).toLocaleString("de-DE")}
                            {teilnehmer?.praktikum_paket_sent_to && <> an <span className="font-mono">{teilnehmer.praktikum_paket_sent_to}</span></>}
                          </div>
                        )}

                        <div>
                          <label className="text-xs text-gray-600">Optionale Nachricht im E-Mail-Text</label>
                          <textarea
                            id="praktikum-paket-msg"
                            rows={2}
                            className="w-full border rounded px-2 py-1 text-sm mt-1"
                            placeholder="z.B. „Bitte besprechen Sie das Paket mit Ihrem Praktikumsbetrieb."
                            data-testid="praktikum-paket-msg"
                          />
                        </div>

                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="send-praktikum-paket-btn"
                          disabled={!teilnehmer?.email}
                          onClick={async () => {
                            const betrieb = document.getElementById('laufzettel-betrieb').value;
                            const zeit = document.getElementById('laufzettel-zeit').value;
                            const anleitung = document.getElementById('laufzettel-anleitung').value;
                            const kuerzel = document.getElementById('laufzettel-kuerzel').value;
                            const msg = document.getElementById('praktikum-paket-msg').value;
                            try {
                              toast.info("Erstelle PDFs und sende E-Mail...");
                              const res = await axios.post(`${API}/teilnehmer/${teilnehmerId}/praktikum-paket/send`, {
                                praktikumsbetrieb: betrieb,
                                praktikumszeit: zeit,
                                praxisanleitung: anleitung,
                                dozent_kuerzel: kuerzel,
                                custom_message: msg,
                              });
                              toast.success(`Paket (3 PDFs) an ${res.data.to} gesendet`);
                              // Teilnehmer neu laden (Status anzeigen)
                              try { await fetchTeilnehmer(); } catch (_) { /* noop */ }
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Fehler beim Senden des Pakets");
                            }
                          }}
                        >
                          <MailPlus size={14} className="mr-2" /> Paket per E-Mail senden
                        </Button>
                      </div>

                      <div className="text-xs text-stone-400 text-center">— oder einzeln herunterladen —</div>

                      {/* Praktikumsvertrag (FB_TN_07) */}
                      <div className="border rounded-lg p-4 space-y-3 bg-emerald-50/40 border-emerald-200">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <h4 className="font-medium text-sm flex items-center gap-2"><span className="font-mono text-xs text-emerald-700">FB_TN_07</span> Praktikumsvertrag</h4>
                            <p className="text-xs text-stone-500 mt-1">3-Parteien-Vertrag (Praktikant / Praktikumsbetrieb / Bildungsträger). Name & Telefon vorausgefüllt — Betrieb-Felder bleiben für handschriftliche Ergänzung leer.</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-emerald-300 text-emerald-700"
                            data-testid="download-praktikumsvertrag-btn"
                            onClick={async () => {
                              try {
                                toast.info("Praktikumsvertrag wird generiert...");
                                const res = await axios.get(`${API}/teilnehmer/${teilnehmerId}/praktikumsvertrag-pdf`, { responseType: "blob" });
                                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                const a = document.createElement("a"); a.href = url; a.download = `Praktikumsvertrag_${teilnehmer.nachname || 'TN'}.pdf`;
                                document.body.appendChild(a); a.click(); a.remove();
                                toast.success("Praktikumsvertrag heruntergeladen");
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Fehler beim Praktikumsvertrag");
                              }
                            }}
                          >
                            <FileDown size={14} className="mr-1" /> Nur PDF
                          </Button>
                        </div>
                      </div>
                      
                      {/* Laufzettel */}
                      <div className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <h4 className="font-medium text-sm"><span className="font-mono text-xs text-purple-700">FB_TN_08</span> Praktikums-Laufzettel</h4>
                            <p className="text-xs text-stone-500 mt-1">Wird vom Praktikumsbetrieb / Praxisanleitung während des Praktikums geführt.</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-purple-300 text-purple-700"
                            data-testid="download-laufzettel-btn"
                            onClick={async () => {
                              const betrieb = document.getElementById('laufzettel-betrieb').value;
                              const zeit = document.getElementById('laufzettel-zeit').value;
                              const anleitung = document.getElementById('laufzettel-anleitung').value;
                              const kuerzel = document.getElementById('laufzettel-kuerzel').value;
                              if (!betrieb) { toast.error('Bitte Praktikumsbetrieb eingeben'); return; }
                              try {
                                const res = await axios.post(`${API}/teilnehmer/${teilnehmerId}/laufzettel`, {
                                  praktikumsbetrieb: betrieb,
                                  praktikumszeit: zeit,
                                  praxisanleitung: anleitung,
                                  dozent_kuerzel: kuerzel,
                                  ohne_freigabe: document.getElementById('laufzettel-ohne-freigabe')?.checked || false
                                }, { responseType: 'blob' });
                                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `Laufzettel_${teilnehmer?.nachname || 'TN'}.pdf`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                toast.success('Laufzettel heruntergeladen');
                              } catch (err) {
                                toast.error('Fehler beim Erstellen des Laufzettels');
                              }
                            }}
                          >
                            <Download size={14} className="mr-1" /> Nur PDF
                          </Button>
                        </div>
                      </div>

                      {/* Bewertungsbogen */}
                      <div className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <h4 className="font-medium text-sm"><span className="font-mono text-xs text-purple-700">FB_TN_09</span> Praktikums-Bewertungsbogen</h4>
                            <p className="text-xs text-stone-500 mt-1">Bewertung der Praktikumsleistung durch die Praxisanleitung am Ende des Praktikums.</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-purple-300 text-purple-600"
                            data-testid="download-bewertung-btn"
                            onClick={async () => {
                              const betrieb = document.getElementById('laufzettel-betrieb')?.value || '';
                              const kuerzel = document.getElementById('laufzettel-kuerzel')?.value || 'MD';
                              try {
                                const res = await axios.post(`${API}/teilnehmer/${teilnehmerId}/praktikum-bewertung`, {
                                  praktikumsbetrieb: betrieb,
                                  dozent_kuerzel: kuerzel
                                }, { responseType: 'blob' });
                                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `Bewertung_${teilnehmer?.nachname || 'TN'}.pdf`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                toast.success('Bewertungsbogen heruntergeladen');
                              } catch (err) {
                                toast.error('Fehler beim Erstellen des Bewertungsbogens');
                              }
                            }}
                          >
                            <Download size={14} className="mr-1" /> Nur PDF
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              )}
            </Tabs>

            {/* Historie Box */}
            <Card className="mt-4 border-dashed">
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <FileText size={16} className="text-gray-400" />
                  Änderungshistorie
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                {historieLoading ? (
                  <div className="text-center text-gray-400 text-sm py-2">Lade...</div>
                ) : historie.length === 0 ? (
                  <div className="text-center text-gray-400 text-sm py-2">Noch keine Änderungen</div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {historie.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs border-b pb-2 last:border-0">
                        <span className="text-gray-400 whitespace-nowrap">
                          {item.datum_formatiert || new Date(item.datum).toLocaleDateString("de-DE")}
                        </span>
                        <span className="text-gray-600 flex-1">
                          {item.benutzer && <span className="font-medium">{item.benutzer}: </span>}
                          {item.details || `${item.aktion}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="p-8 text-center text-gray-500">Teilnehmer nicht gefunden</div>
        )}
      </DialogContent>

      {/* Online-Unterschrift Dialog */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pen size={18} /> Unterlagen zur Online-Unterschrift senden
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
              <p className="text-blue-900">
                E-Mail-Empfänger: <b>{teilnehmer?.email}</b>
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Wählen Sie die Dokumente aus. Der TN erhält einen Link, kann auf dem Smartphone unterschreiben → PDFs landen automatisch hier.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-sm mb-2 text-stone-900">Dokumente auswählen</h4>
              <div className="space-y-2">
                {Object.entries(signAvailableDocs).map(([key, info]) => (
                  <label
                    key={key}
                    className="flex items-start gap-3 p-3 border border-stone-200 rounded hover:bg-stone-50 cursor-pointer"
                    data-testid={`sign-doc-checkbox-${key}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSignDocs.includes(key)}
                      onChange={e => {
                        if (e.target.checked) setSelectedSignDocs([...selectedSignDocs, key]);
                        else setSelectedSignDocs(selectedSignDocs.filter(k => k !== key));
                      }}
                      className="mt-1 w-4 h-4 accent-emerald-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-stone-900">{info.label}</div>
                      <div className="text-xs text-stone-500">
                        {info.code}
                        {!info.requires_tn_signature && " · TN-Unterschrift optional"}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {selectedSignDocs.includes("massnahmevertrag") && (
              <div data-testid="sign-gf-datum-block">
                <label className="text-sm font-medium text-stone-900 block mb-1">Maßnahmevertrag – Datum Geschäftsführung („Erkrath, …“)</label>
                <input
                  type="date"
                  value={signGfDatum}
                  onChange={e => setSignGfDatum(e.target.value)}
                  className="w-full border border-stone-300 rounded p-2 text-sm"
                  data-testid="sign-gf-datum"
                />
                <p className="text-xs text-stone-500 mt-1">Vorausgefüllt mit heute (Versandtag). Das eingetragene Datum wird mit Eintragungszeitpunkt protokolliert.</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-stone-900 block mb-1">Dokument-Datum (optional)</label>
              <input
                type="date"
                value={signDokumentDatum}
                onChange={e => setSignDokumentDatum(e.target.value)}
                className="w-full border border-stone-300 rounded p-2 text-sm"
                data-testid="sign-dokument-datum"
              />
              <p className="text-xs text-stone-500 mt-1">
                ℹ Lässt du dieses Feld leer, wird das heutige Datum eingedruckt. Setze ein Datum aus der Vergangenheit, wenn das Gespräch/Dokument tatsächlich an einem früheren Tag stattgefunden hat. Das Signatur-Audit-Datum (eIDAS) bleibt automatisch der heutige Tag der Unterschrift.
              </p>
            </div>

            {/1232|pflegehilfs|betreuungsassist/i.test([teilnehmer?.kurs, ...(teilnehmer?.kurse || []).map((k) => k.kurs_name)].join(" ")) && (
              <label className="flex items-start gap-2 text-sm text-stone-800" data-testid="sign-praktikum-paket-label">
                <input type="checkbox" className="mt-1" checked={signPraktikumPaket} onChange={(e) => setSignPraktikumPaket(e.target.checked)} data-testid="sign-praktikum-paket" />
                <span>Praktikumsunterlagen mitschicken (Praktikumsvertrag, Laufzettel, Praktikumsbewertung als PDF-Anhang – nur bei E-Mail)</span>
              </label>
            )}

            <div>
              <label className="text-sm font-medium text-stone-900 block mb-1">Persönliche Nachricht (optional)</label>
              <textarea
                value={signCustomMessage}
                onChange={e => setSignCustomMessage(e.target.value)}
                placeholder="z.B. „Bitte unterschreiben Sie bis Freitag damit wir Sie zum Kurs einbuchen können."
                className="w-full border border-stone-300 rounded p-2 text-sm"
                rows={3}
                data-testid="sign-custom-message"
              />
            </div>

            {signRequests.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2 text-stone-900">Bisherige Sign-Anfragen</h4>
                <div className="space-y-1 max-h-48 overflow-y-auto text-xs">
                  {signRequests.slice(0, 5).map((sr, i) => (
                    <div key={i} className="p-2 bg-stone-50 rounded space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span>{new Date(sr.created_at).toLocaleDateString('de-DE')} · {sr.dokumente?.length || 0} Dokument(e)</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          sr.status === 'signed' ? 'bg-emerald-100 text-emerald-800' :
                          sr.status === 'signed_with_errors' ? 'bg-red-100 text-red-800' :
                          sr.status === 'expired' ? 'bg-stone-200 text-stone-600' :
                          'bg-amber-100 text-amber-800'
                        }`}>
                          {sr.status === 'signed' ? '✓ Unterschrieben' :
                           sr.status === 'signed_with_errors' ? 'Mit Fehlern' :
                           sr.status === 'expired' ? 'Abgelaufen' :
                           sr.status === 'viewed' ? 'Geöffnet, wartet' : 'Gesendet, wartet'}
                        </span>
                      </div>
                      {(sr.save_errors && sr.save_errors.length > 0) && (
                        <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-1">
                          <div className="font-semibold mb-1">Fehler beim Speichern:</div>
                          <ul className="list-disc pl-4 space-y-0.5">
                            {sr.save_errors.map((e, j) => (
                              <li key={j}><b>{e.label}:</b> {e.error}</li>
                            ))}
                          </ul>
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2 h-7 text-xs border-red-400 text-red-700 hover:bg-red-100"
                            data-testid={`btn-regenerate-anhaenge-${sr.id}`}
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API}/sign-requests/${sr.id}/regenerate-anhaenge`);
                                if (res.data.errors && res.data.errors.length > 0) {
                                  toast.error(`Erneut Fehler: ${res.data.errors.map(e => e.label).join(', ')}`);
                                } else {
                                  toast.success(`✓ Erfolgreich nachgeneriert: ${res.data.regenerated.join(', ')}`);
                                }
                                const reqsRes = await axios.get(`${API}/sign-requests/teilnehmer/${teilnehmerId}`);
                                setSignRequests(reqsRes.data || []);
                                fetchAnhaenge();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Regeneration fehlgeschlagen');
                              }
                            }}
                          >
                            Anhänge neu generieren
                          </Button>
                        </div>
                      )}
                      {sr.status === 'signed' && !(sr.save_errors && sr.save_errors.length > 0) && (
                        <div className="mt-2 bg-amber-50 border-2 border-amber-400 rounded-lg p-3">
                          <div className="text-xs font-bold text-amber-900 mb-2">
                            Unterschrift an falscher Stelle? Layout-Fix vom 28.07.2026
                          </div>
                          <div className="text-[11px] text-stone-700 mb-2">
                            Klicke unten um ALLE Dokumente dieser Signatur-Anfrage (inkl. MV, Datenschutz etc.) mit KORREKTER Signatur-Position neu zu erzeugen. Die gespeicherte Referenz-Signatur wird wiederverwendet — der TN muss NICHT nochmal signieren.
                          </div>
                          <Button
                            size="sm"
                            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold border-2 border-red-800 shadow-md"
                            data-testid={`btn-rebuild-signed-anhaenge-${sr.id}`}
                            onClick={async () => {
                              if (!window.confirm('Alle unterschriebenen Dokumente aus dieser Anfrage mit KORREKTER Signatur-Position neu erzeugen?\n\n(Referenz-Signatur wird wiederverwendet, keine neue Zustimmung nötig)')) return;
                              const t = toast.loading('Regeneriere Dokumente mit korrigierter Signatur-Position...');
                              try {
                                const res = await axios.post(`${API}/sign-requests/${sr.id}/regenerate-anhaenge`);
                                if (res.data.errors && res.data.errors.length > 0) {
                                  toast.error(`Fehler bei: ${res.data.errors.map(e => e.label).join(', ')}`, { id: t });
                                } else {
                                  toast.success(`✓ ${(res.data.regenerated || []).length} Dokumente neu erzeugt: ${(res.data.regenerated || []).join(', ') || '—'}`, { id: t, duration: 8000 });
                                }
                                const reqsRes = await axios.get(`${API}/sign-requests/teilnehmer/${teilnehmerId}`);
                                setSignRequests(reqsRes.data || []);
                                fetchAnhaenge();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Regeneration fehlgeschlagen', { id: t });
                              }
                            }}
                          >
                            Alle Dokumente neu erzeugen (Signatur-Position fixen)
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowSignDialog(false)} data-testid="sign-cancel-btn">
              Abbrechen
            </Button>
            <Button
              onClick={async () => {
                if (selectedSignDocs.length === 0) {
                  toast.error("Bitte mindestens ein Dokument auswählen");
                  return;
                }
                setSignSending(true);
                try {
                  // sign_params: dokument_datum für ALLE ausgewählten Dokumente eintragen
                  const docSignParams = {};
                  if (signDokumentDatum) {
                    selectedSignDocs.forEach(k => {
                      docSignParams[k] = { dokument_datum: signDokumentDatum };
                    });
                  }
                  if (selectedSignDocs.includes("massnahmevertrag") && signGfDatum) {
                    docSignParams.massnahmevertrag = { ...(docSignParams.massnahmevertrag || {}), gf_datum: signGfDatum };
                  }
                  const res = await axios.post(`${API}/sign-requests`, {
                    teilnehmer_id: teilnehmerId,
                    dokumente: selectedSignDocs,
                    versand_methode: "whatsapp",
                    custom_message: signCustomMessage || null,
                    sign_params: Object.keys(docSignParams).length ? docSignParams : null,
                  });
                  if (res.data.whatsapp_url) {
                    window.open(res.data.whatsapp_url, '_blank', 'noopener,noreferrer');
                    toast.success(`WhatsApp geöffnet — bitte abschicken (Nummer: +${res.data.whatsapp_number})`);
                  }
                  setShowSignDialog(false);
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Fehler beim WhatsApp-Versand");
                } finally {
                  setSignSending(false);
                }
              }}
              disabled={signSending || selectedSignDocs.length === 0}
              className="bg-[#25D366] hover:bg-[#1ebe57] text-white"
              data-testid="sign-send-whatsapp-btn"
            >
              <MessageCircle size={16} className="mr-2" />
              Per WhatsApp
            </Button>
            <Button
              onClick={async () => {
                if (selectedSignDocs.length === 0) {
                  toast.error("Bitte mindestens ein Dokument auswählen");
                  return;
                }
                setSignSending(true);
                try {
                  const docSignParams = {};
                  if (signDokumentDatum) {
                    selectedSignDocs.forEach(k => {
                      docSignParams[k] = { dokument_datum: signDokumentDatum };
                    });
                  }
                  if (selectedSignDocs.includes("massnahmevertrag") && signGfDatum) {
                    docSignParams.massnahmevertrag = { ...(docSignParams.massnahmevertrag || {}), gf_datum: signGfDatum };
                  }
                  const res = await axios.post(`${API}/sign-requests`, {
                    teilnehmer_id: teilnehmerId,
                    dokumente: selectedSignDocs,
                    versand_methode: "email",
                    custom_message: signCustomMessage || null,
                    sign_params: (() => {
                      const p = { ...docSignParams };
                      if (signPraktikumPaket && /1232|pflegehilfs|betreuungsassist/i.test([teilnehmer?.kurs, ...(teilnehmer?.kurse || []).map((k) => k.kurs_name)].join(" "))) p.praktikum_paket = true;
                      return Object.keys(p).length ? p : null;
                    })(),
                  });
                  toast.success(`E-Mail an ${res.data.tn_email} gesendet (${res.data.dokumente_count} Dokumente)`);
                  setShowSignDialog(false);
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Fehler beim Senden");
                } finally {
                  setSignSending(false);
                }
              }}
              disabled={signSending || selectedSignDocs.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="sign-send-btn"
            >
              {signSending ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
              {signSending ? 'Sende...' : `Per E-Mail`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Nachdruck-Modal: Original-Zertifikat mit Originaldaten erneut drucken === */}
      <Dialog open={showNachdruckModal} onOpenChange={setShowNachdruckModal}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} className="text-blue-700" />
              Zertifikat-Nachdruck mit Originaldaten
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-stone-600">
              Druckt ein Zertifikat mit der <b>ursprünglichen Nummer</b> und dem <b>ursprünglichen Datum</b> —
              ohne „Vorschau"- oder „Zweitausfertigung"-Markierung. Sinnvoll wenn der TN das Original verloren hat
              oder ein altes Zertifikat (z.B. aus 2025) erneut benötigt wird.
            </p>
            {nachdruckKurs && (
              <div className="bg-stone-50 border border-stone-200 rounded p-2 text-xs text-stone-700">
                <div><b>Kurs:</b> {nachdruckKurs.kurs_name}</div>
                {nachdruckKurs.vorlage_name && <div><b>Vorlage:</b> {nachdruckKurs.vorlage_name}</div>}
              </div>
            )}
            <div>
              <Label htmlFor="nachdruck-nummer" className="text-sm font-medium">Original Zertifikats-Nr. *</Label>
              <Input
                id="nachdruck-nummer"
                value={nachdruckNummer}
                onChange={(e) => setNachdruckNummer(e.target.value)}
                placeholder="z.B. 2025/45"
                className="mt-1"
                data-testid="nachdruck-nummer-input"
              />
              <p className="text-xs text-stone-500 mt-1">Format: JJJJ/NN (z.B. 2025/45). Falls bekannt – sonst leer lassen für neue Nummer.</p>
            </div>
            <div>
              <Label htmlFor="nachdruck-datum" className="text-sm font-medium">Original Ausstellungsdatum *</Label>
              <Input
                id="nachdruck-datum"
                type="date"
                value={nachdruckDatum}
                onChange={(e) => setNachdruckDatum(e.target.value)}
                className="mt-1"
                data-testid="nachdruck-datum-input"
              />
              <p className="text-xs text-stone-500 mt-1">Wird unten rechts als „Erkrath am ..." angedruckt.</p>
            </div>
            <div>
              <Label htmlFor="nachdruck-rechtsgrundlage" className="text-sm font-medium">Rechtsgrundlage *</Label>
              <select
                id="nachdruck-rechtsgrundlage"
                data-testid="nachdruck-rechtsgrundlage-select"
                className={`mt-1 w-full h-9 rounded border px-2 py-1 text-sm bg-white ${nachdruckRechtsgrundlage === "hessen" ? "border-amber-500 text-amber-800 font-medium" : "border-stone-300 text-stone-700"}`}
                value={nachdruckRechtsgrundlage}
                onChange={(e) => setNachdruckRechtsgrundlage(e.target.value)}
              >
                <option value="nrw">NRW (Standard) — kein Zusatztext</option>
                <option value="hessen">Hessen — HLfGP-Leitlinie + § 5 HGBPAV</option>
              </select>
              <p className="text-xs text-stone-500 mt-1">Muss zum Original passen — bei Hessen wird der HLfGP-/§5-HGBPAV-Hinweis unten mit gedruckt.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNachdruckModal(false)} data-testid="nachdruck-cancel">
              Abbrechen
            </Button>
            <Button
              disabled={nachdruckLoading || !nachdruckDatum}
              data-testid="nachdruck-print-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={async () => {
                if (!nachdruckKurs) return;
                setNachdruckLoading(true);
                try {
                  const params = new URLSearchParams();
                  params.set("preview", "true"); // Counter NICHT hochzählen, keine Speicherung
                  params.set("rechtsgrundlage", nachdruckRechtsgrundlage || "nrw");
                  if (nachdruckKurs.kurs_id) params.set("kurs_id", nachdruckKurs.kurs_id);
                  if (nachdruckNummer.trim()) params.set("nummer", nachdruckNummer.trim());
                  if (nachdruckDatum) params.set("ausstellungs_datum", nachdruckDatum);
                  const res = await axios.get(
                    `${API}/teilnehmer/${teilnehmerId}/zertifikat-pdf?${params.toString()}`,
                    { responseType: "blob" }
                  );
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  const a = document.createElement("a"); a.href = url;
                  const nrTag = nachdruckNummer.trim() ? `_${nachdruckNummer.trim().replace("/", "-")}` : "";
                  a.download = `Zertifikat_NACHDRUCK_${teilnehmer.nachname || 'TN'}${nrTag}.pdf`;
                  document.body.appendChild(a); a.click(); a.remove();
                  toast.success("Nachdruck-PDF erstellt");
                  setShowNachdruckModal(false);
                } catch (err) {
                  let msg = "Fehler beim Nachdruck";
                  try { const txt = await err.response?.data?.text?.(); if (txt) { msg = JSON.parse(txt).detail || msg; } } catch (e) {}
                  toast.error(msg);
                } finally {
                  setNachdruckLoading(false);
                }
              }}
            >
              {nachdruckLoading ? "Erstelle..." : "PDF erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Anwesenheitsliste Online-Signatur Modal === */}
      {/* ===== Schnell-Anwesenheit Modal (Auto-fill von Kursstart bis heute) ===== */}
      <Dialog open={showQuickAnwesenheit} onOpenChange={setShowQuickAnwesenheit}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quick-anwesenheit-modal">
          <DialogHeader>
            <DialogTitle>Anwesenheitsliste Schnell-Versand</DialogTitle>
            <DialogDescription>
              Alle Werktage von Kursstart bis heute werden automatisch als „anwesend" markiert. Klick einzelne Tage durch, um Status zu ändern (anwesend → entschuldigt → fehlend → anwesend).
            </DialogDescription>
          </DialogHeader>

          {quickLoading && (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="animate-spin mr-2" size={20} /> Tage werden berechnet...
            </div>
          )}

          {!quickLoading && quickData && (
            <div className="space-y-3">
              {/* Header-Info */}
              <div className="bg-stone-50 border border-stone-200 rounded p-3 text-sm grid grid-cols-2 gap-2">
                <div><strong>TN:</strong> {quickData.teilnehmer_name}</div>
                <div><strong>Kurs:</strong> {quickData.kurs_name || quickData.kurs_id}</div>
                <div><strong>Zeitraum:</strong> {quickData.datum_von} → {quickData.datum_bis}</div>
                <div><strong>Tage:</strong> {quickData.anzahl_tage} <span className="text-stone-500">(Curriculum-Vorgabe; ändere „Tag bis" für Abbruch/Zwischen-AL)</span></div>
              </div>

              {/* Warnung bei existierenden Einträgen */}
              {quickData.has_existing_entries && (
                <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-900">
                  Es existieren bereits Klassenbuch-Einträge für diesen TN. Beim Senden werden sie <strong>überschrieben</strong>.
                </div>
              )}

              {/* Dozent + Datumsbereich anpassen */}
              <div className="flex flex-wrap items-end gap-3 bg-stone-50 border border-stone-200 rounded p-3">
                <div>
                  <label className="text-xs font-medium block mb-1">Dozent</label>
                  <select
                    className="border border-stone-300 rounded px-3 py-1.5 text-sm"
                    value={quickDozent}
                    onChange={(e) => setQuickDozent(e.target.value)}
                    data-testid="quick-dozent-select"
                  >
                    <option value="AL">AL — Annika Lorke</option>
                    <option value="JM">JM — Julia Müller</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">Tag von</label>
                  <input
                    type="date"
                    className="border border-stone-300 rounded px-2 py-1.5 text-sm"
                    value={quickData.datum_von || ""}
                    onChange={(e) => setQuickData({ ...quickData, datum_von: e.target.value })}
                    data-testid="quick-datum-von"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">Tag bis (für Abbruch/Zwischen-AL)</label>
                  <input
                    type="date"
                    className="border border-stone-300 rounded px-2 py-1.5 text-sm"
                    value={quickData.datum_bis || ""}
                    onChange={(e) => setQuickData({ ...quickData, datum_bis: e.target.value })}
                    data-testid="quick-datum-bis"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  data-testid="btn-quick-recalculate"
                  onClick={async () => {
                    setQuickLoading(true);
                    try {
                      const res = await axios.post(`${API}/teilnehmer/${teilnehmerId}/anwesenheit/auto-vorschau`, {
                        datum_von: quickData.datum_von,
                        datum_bis: quickData.datum_bis,
                      });
                      setQuickData(res.data);
                      toast.success(`Neu berechnet — ${res.data.anzahl_tage} Tage`);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Neuberechnung fehlgeschlagen");
                    } finally {
                      setQuickLoading(false);
                    }
                  }}
                  disabled={quickLoading}
                >
                  Neu berechnen
                </Button>
                <p className="text-[10px] text-stone-500 basis-full mt-1">
                  Lass die Felder leer für die <strong>volle Curriculum-Anwesenheit</strong> (32/46/66 Tage je nach Kurs). Setze ein „Tag bis"-Datum bei <strong>Abbruch</strong> oder wenn das Amt eine <strong>Zwischen-Anwesenheit</strong> verlangt.
                </p>
              </div>

              {/* Tage-Tabelle */}
              <div className="border border-stone-200 rounded max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-stone-100 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Tag</th>
                      <th className="text-left p-2 w-40">Datum (editierbar)</th>
                      <th className="text-left p-2">LG/M</th>
                      <th className="text-left p-2">Thema (editierbar)</th>
                      <th className="text-center p-2 w-32">Status (Klick)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quickData.tage.map((t, i) => {
                      const rowKey = t._orig_datum || t.datum;
                      return (
                      <tr key={rowKey} className={`border-t border-stone-100 hover:bg-stone-50 ${t._edited ? "bg-amber-50/30" : ""}`}>
                        <td className="p-2 text-stone-500">{i + 1}</td>
                        <td className="p-2">
                          <input
                            type="date"
                            value={t.datum || ""}
                            onChange={(e) => quickEditTag(rowKey, "datum", e.target.value)}
                            className="border border-stone-300 rounded px-1.5 py-1 text-xs w-full focus:border-emerald-500 focus:outline-none"
                            data-testid={`quick-edit-datum-${rowKey}`}
                          />
                          <div className="text-[10px] text-stone-500 mt-0.5">{t.wochentag} {t.datum_display}</div>
                        </td>
                        <td className="p-2 text-stone-500">
                          <div className="flex gap-1">
                            <input
                              type="text"
                              value={t.lg || ""}
                              onChange={(e) => quickEditTag(rowKey, "lg", e.target.value)}
                              placeholder="LG"
                              className="border border-stone-300 rounded px-1.5 py-1 text-xs w-16 focus:border-emerald-500 focus:outline-none"
                              data-testid={`quick-edit-lg-${rowKey}`}
                              title={'LG/Bereich (z.B. „LG 2", „Prüfung", „Wiederhol.")'}
                            />
                            <input
                              type="text"
                              value={t.modul_nr != null ? String(t.modul_nr) : ""}
                              onChange={(e) => quickEditTag(rowKey, "modul_nr", e.target.value)}
                              placeholder="M"
                              className="border border-stone-300 rounded px-1.5 py-1 text-xs w-12 focus:border-emerald-500 focus:outline-none"
                              data-testid={`quick-edit-modul-${rowKey}`}
                              title="Modul-Nr. (z.B. 18, 19 oder leer)"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              quickEditTag(rowKey, "lg", "Prüfung");
                              quickEditTag(rowKey, "modul_nr", "19");
                              quickEditTag(rowKey, "thema", "Prüfung");
                            }}
                            className="mt-1 text-[10px] text-emerald-700 hover:text-emerald-900 underline"
                            data-testid={`quick-mark-pruefung-${rowKey}`}
                            title="Diesen Tag als Prüfungstag markieren"
                          >
                            als Prüfung
                          </button>
                        </td>
                        <td className="p-2">
                          <input
                            type="text"
                            value={t.thema || ""}
                            onChange={(e) => quickEditTag(rowKey, "thema", e.target.value)}
                            placeholder="Thema des Tages"
                            className="border border-stone-300 rounded px-1.5 py-1 text-xs w-full focus:border-emerald-500 focus:outline-none"
                            data-testid={`quick-edit-thema-${rowKey}`}
                          />
                        </td>
                        <td className="p-2 text-center">
                          <button
                            type="button"
                            onClick={() => quickToggleStatus(rowKey)}
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              t.status === "anwesend" ? "bg-emerald-100 text-emerald-800" :
                              t.status === "entschuldigt" ? "bg-amber-100 text-amber-800" :
                              "bg-rose-100 text-rose-800"
                            }`}
                            data-testid={`quick-status-${rowKey}`}
                          >
                            {t.status === "anwesend" ? "✓ Anwesend" : t.status === "entschuldigt" ? "≈ Entsch." : "✗ Fehlend"}
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-stone-500 px-1">
                Tipp: Klicke auf Datum oder Thema, um Phantasie-Inhalte direkt zu korrigieren (z.B. doppelte „Abschlussprüfung"-Tage). Gelb hinterlegt = überschrieben. Änderungen werden beim Senden gespeichert.
              </p>

              {/* Zusatztag für Prüfung */}
              <div className="flex items-center gap-2 px-1 py-2 bg-amber-50 border border-amber-200 rounded">
                <span className="text-xs text-stone-700 flex-1">
                  <strong>Prüfungstag am {(quickData.tage.length ? "Tag " + (quickData.tage.length + 1) : "Ende")}</strong> nötig?
                  {quickExtraTage > 0 && <span className="ml-1 text-amber-800">({quickExtraTage} Zusatztag{quickExtraTage > 1 ? "e" : ""} angehängt)</span>}
                </span>
                {quickExtraTage > 0 && (
                  <button
                    type="button"
                    onClick={removeExtraTag}
                    disabled={quickLoading}
                    className="text-xs px-2 py-1 border border-stone-300 rounded hover:bg-stone-100 disabled:opacity-40"
                    data-testid="quick-remove-extra-tag"
                  >
                    − Zurücknehmen
                  </button>
                )}
                <button
                  type="button"
                  onClick={addExtraTag}
                  disabled={quickLoading || quickExtraTage >= 5}
                  className="text-xs px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-40"
                  data-testid="quick-add-extra-tag"
                >
                  + Prüfungstag anhängen
                </button>
              </div>

              {/* Statistik */}
              <div className="flex gap-4 text-sm text-stone-700 px-1">
                <span>✓ Anwesend: <strong>{quickData.tage.filter(t => t.status === "anwesend").length}</strong></span>
                <span>≈ Entschuldigt: <strong>{quickData.tage.filter(t => t.status === "entschuldigt").length}</strong></span>
                <span>✗ Fehlend: <strong>{quickData.tage.filter(t => t.status === "fehlend").length}</strong></span>
              </div>

              {/* Senden */}
              <div className="flex gap-2 pt-2 border-t border-stone-200">
                <Button
                  variant="outline"
                  onClick={() => setShowQuickAnwesenheit(false)}
                  disabled={quickSubmitting}
                >
                  Abbrechen
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                  onClick={() => submitQuickAnwesenheit("email")}
                  disabled={quickSubmitting || !teilnehmer?.email}
                  data-testid="quick-send-email"
                >
                  <Mail size={14} className="mr-2" />
                  {quickSubmitting ? "Sende..." : "Per E-Mail senden"}
                </Button>
                <Button
                  variant="outline"
                  className="border-green-600 text-green-700 hover:bg-green-50"
                  onClick={() => submitQuickAnwesenheit("whatsapp")}
                  disabled={quickSubmitting || !teilnehmer?.telefon}
                  data-testid="quick-send-whatsapp"
                >
                  <MessageCircle size={14} className="mr-2" />
                  Per WhatsApp
                </Button>
              </div>
              {(!teilnehmer?.email || !teilnehmer?.telefon) && (
                <p className="text-[10px] text-stone-500">
                  {!teilnehmer?.email && "Kein E-Mail beim TN hinterlegt. "}
                  {!teilnehmer?.telefon && "Keine Telefonnummer beim TN hinterlegt."}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>


      <Dialog open={showAnwesenheitSignModal} onOpenChange={setShowAnwesenheitSignModal}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pen size={20} className="text-emerald-600" />
              {alSignForm.use_stored_signature
                ? "Anwesenheitsliste mit gespeicherter Unterschrift füllen"
                : "Anwesenheitsliste online unterschreiben lassen"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Dozent-Auswahl */}
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1">Dozent/in (Unterschrift wird automatisch eingefügt)</label>
              <select
                className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white"
                value={alSignForm.dozent_kuerzel}
                onChange={e => { setAlSignForm({...alSignForm, dozent_kuerzel: e.target.value}); setAlPreviewUrl(null); }}
                data-testid="al-sign-dozent-select"
              >
                <option value="AL">Annika Lorke (AL)</option>
                <option value="JM">Julia Müller (JM)</option>
                <option value="MD">Markus Dickie (MD, bis 11.10.2026)</option>
              </select>
            </div>

            {/* Datum-Bereich */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1">Anwesenheits-Datum von <span className="text-stone-400">(optional)</span></label>
                <input
                  type="date"
                  className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white"
                  value={alSignForm.datum_von}
                  onChange={e => { setAlSignForm({...alSignForm, datum_von: e.target.value}); setAlPreviewUrl(null); }}
                  data-testid="al-sign-datum-von"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1">bis <span className="text-stone-400">(optional)</span></label>
                <input
                  type="date"
                  className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white"
                  value={alSignForm.datum_bis}
                  onChange={e => { setAlSignForm({...alSignForm, datum_bis: e.target.value}); setAlPreviewUrl(null); }}
                  data-testid="al-sign-datum-bis"
                />
              </div>
            </div>

            {/* Unterschrifts-Datum */}
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1">
                Datum der elektronischen Unterschrift <span className="text-stone-400">(optional — leer = letztes Anwesenheits-Datum)</span>
              </label>
              <input
                type="date"
                className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white"
                value={alSignForm.sign_date}
                onChange={e => { setAlSignForm({...alSignForm, sign_date: e.target.value}); setAlPreviewUrl(null); }}
                data-testid="al-sign-date"
              />
            </div>

            {/* Optional: persönliche Nachricht (nur bei E-Mail-Versand) */}
            {!alSignForm.use_stored_signature && (
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1">Persönliche Nachricht <span className="text-stone-400">(optional)</span></label>
                <textarea
                  rows={2}
                  className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white"
                  placeholder="z.B. „Bitte unterschreibe die Anwesenheitsliste für November..."
                  value={alSignForm.custom_message}
                  onChange={e => setAlSignForm({...alSignForm, custom_message: e.target.value})}
                  data-testid="al-sign-custom-msg"
                />
              </div>
            )}

            {/* Vorschau-Button */}
            <div className="border-t pt-3">
              <Button
                size="sm"
                variant="outline"
                className="w-full border-blue-300 text-blue-700 bg-blue-50/40"
                data-testid="al-sign-preview-btn"
                onClick={async () => {
                  try {
                    toast.info("Vorschau wird erstellt...");
                    const payload = {
                      dozent_kuerzel: alSignForm.dozent_kuerzel,
                      only_anwesend: alSignForm.only_anwesend,
                    };
                    if (alSignForm.sign_date) payload.sign_date = alSignForm.sign_date;
                    if (alSignForm.datum_von) payload.datum_von = alSignForm.datum_von;
                    if (alSignForm.datum_bis) payload.datum_bis = alSignForm.datum_bis;
                    const res = await axios.post(
                      `${API}/teilnehmer/${teilnehmerId}/anwesenheitsliste-preview`,
                      payload,
                      { responseType: 'blob' }
                    );
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    setAlPreviewUrl(url);
                  } catch (err) {
                    // err.response.data ist ein Blob (wegen responseType blob) — als Text lesen
                    let msg = "Fehler bei Vorschau";
                    try {
                      if (err.response?.data instanceof Blob) {
                        const txt = await err.response.data.text();
                        try { msg = JSON.parse(txt).detail || txt; } catch { msg = txt; }
                      } else if (err.response?.data?.detail) {
                        msg = err.response.data.detail;
                      }
                    } catch (_) {}
                    if (msg.includes("Keine Anwesenheits")) {
                      msg = "Dieser Teilnehmer hat noch keine Anwesenheitseinträge im Klassenbuch. Bitte zuerst Anwesenheitstage im Klassenbuch anlegen.";
                    }
                    toast.error(msg);
                  }
                }}
              >
                <FileDown size={14} className="mr-2" />
                {alPreviewUrl ? "Vorschau aktualisieren" : "Vorschau anzeigen"}
              </Button>

              {alPreviewUrl && (
                <div className="mt-3 border border-stone-200 rounded overflow-hidden">
                  <iframe
                    src={alPreviewUrl}
                    title="Vorschau"
                    className="w-full"
                    style={{ height: "500px" }}
                  />
                  <div className="bg-stone-50 px-3 py-1 text-xs text-stone-500 border-t">
                    Vorschau — die echte TN-Unterschrift wird beim {alSignForm.use_stored_signature ? "Generieren" : "Online-Signieren"} eingefügt
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowAnwesenheitSignModal(false); setAlPreviewUrl(null); }}
              disabled={alSubmitting}
            >
              Abbrechen
            </Button>
            {!alSignForm.use_stored_signature && (
              <Button
                className="bg-[#25D366] hover:bg-[#1ebe57] text-white"
                disabled={alSubmitting}
                data-testid="al-sign-whatsapp-btn"
                onClick={async () => {
                  setAlSubmitting(true);
                  try {
                    const params = {
                      dozent_kuerzel: alSignForm.dozent_kuerzel,
                      only_anwesend: alSignForm.only_anwesend,
                    };
                    if (alSignForm.sign_date) params.sign_date = alSignForm.sign_date;
                    if (alSignForm.datum_von) params.datum_von = alSignForm.datum_von;
                    if (alSignForm.datum_bis) params.datum_bis = alSignForm.datum_bis;
                    const res = await axios.post(`${API}/sign-requests`, {
                      teilnehmer_id: teilnehmerId,
                      dokumente: ["anwesenheitsliste"],
                      versand_methode: "whatsapp",
                      expires_in_days: 14,
                      custom_message: alSignForm.custom_message || null,
                      sign_params: { anwesenheitsliste: params },
                    });
                    if (res.data.whatsapp_url) {
                      window.open(res.data.whatsapp_url, '_blank', 'noopener,noreferrer');
                      toast.success(`WhatsApp geöffnet — bitte abschicken (+${res.data.whatsapp_number})`);
                    }
                    setShowAnwesenheitSignModal(false);
                    setAlPreviewUrl(null);
                  } catch (err) {
                    let msg = "Fehler beim WhatsApp-Versand";
                    try {
                      if (err.response?.data instanceof Blob) {
                        const txt = await err.response.data.text();
                        try { msg = JSON.parse(txt).detail || txt; } catch { msg = txt; }
                      } else if (err.response?.data?.detail) {
                        msg = err.response.data.detail;
                      }
                    } catch (_) {}
                    toast.error(msg);
                  } finally {
                    setAlSubmitting(false);
                  }
                }}
              >
                <MessageCircle size={16} className="mr-2" />
                Per WhatsApp
              </Button>
            )}
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={alSubmitting}
              data-testid="al-sign-submit-btn"
              onClick={async () => {
                setAlSubmitting(true);
                try {
                  const params = {
                    dozent_kuerzel: alSignForm.dozent_kuerzel,
                    only_anwesend: alSignForm.only_anwesend,
                  };
                  if (alSignForm.sign_date) params.sign_date = alSignForm.sign_date;
                  if (alSignForm.datum_von) params.datum_von = alSignForm.datum_von;
                  if (alSignForm.datum_bis) params.datum_bis = alSignForm.datum_bis;

                  if (alSignForm.use_stored_signature) {
                    // Direkt PDF mit gespeicherter Unterschrift erstellen
                    toast.info("Anwesenheitsliste wird generiert...");
                    const res = await axios.post(
                      `${API}/teilnehmer/${teilnehmerId}/anwesenheitsliste-signed`,
                      params,
                      { responseType: 'blob' }
                    );
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const a = document.createElement('a'); a.href = url;
                    a.download = `Anwesenheitsliste_signed_${teilnehmer.nachname || 'TN'}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    toast.success("Anwesenheitsliste erstellt und in Akte gespeichert");
                    try { await fetchTeilnehmer(); } catch (_) {}
                  } else {
                    // Sign-Link erstellen + per E-Mail an TN senden
                    toast.info("Sign-Link wird erstellt und per E-Mail gesendet...");
                    const res = await axios.post(`${API}/sign-requests`, {
                      teilnehmer_id: teilnehmerId,
                      dokumente: ["anwesenheitsliste"],
                      versand_methode: "email",
                      expires_in_days: 14,
                      custom_message: alSignForm.custom_message || null,
                      sign_params: { anwesenheitsliste: params },
                    });
                    if (res.data.status === "sent") {
                      toast.success(`Sign-Link an ${res.data.tn_email} gesendet`);
                    } else if (res.data.warning) {
                      toast.error(`Erstellt, aber E-Mail-Fehler: ${res.data.warning.substring(0, 100)}`);
                    }
                  }
                  setShowAnwesenheitSignModal(false);
                  setAlPreviewUrl(null);
                } catch (err) {
                  // Bei responseType blob ist err.response.data ein Blob
                  let msg = "Fehler beim Versenden";
                  try {
                    if (err.response?.data instanceof Blob) {
                      const txt = await err.response.data.text();
                      try { msg = JSON.parse(txt).detail || txt; } catch { msg = txt; }
                    } else if (err.response?.data?.detail) {
                      msg = err.response.data.detail;
                    }
                  } catch (_) {}
                  if (msg.includes("Keine Anwesenheits")) {
                    msg = "Dieser Teilnehmer hat noch keine Anwesenheitseinträge im Klassenbuch. Bitte zuerst Anwesenheitstage anlegen.";
                  }
                  toast.error(msg);
                } finally {
                  setAlSubmitting(false);
                }
              }}
            >
              {alSubmitting ? <Loader2 className="animate-spin mr-2" size={16} /> : (alSignForm.use_stored_signature ? <FileDown size={16} className="mr-2" /> : <Mail size={16} className="mr-2" />)}
              {alSubmitting ? 'Bitte warten...' : (alSignForm.use_stored_signature ? 'Anwesenheitsliste generieren' : 'Per E-Mail')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* BG-Ausfertigung Modal */}
      <BgAusfertigungModal
        open={bgModalOpen}
        onClose={() => setBgModalOpen(false)}
        teilnehmer={teilnehmer}
      />
    </Dialog>
  );
};

export default TeilnehmerDetail;
